/**
 * es-CR is the source-of-truth dictionary: every other locale's key set is
 * type-checked against this one (see locales/en-US.ts), which is what keeps
 * fallback behavior deterministic — a missing key is a compile-time error,
 * not a runtime guess.
 */
export const esCR = {
  'app.name': 'LuParX',
  'app.tagline': 'Estacionamiento inteligente',

  'common.loading': 'Cargando…',
  'common.error.generic': 'Ocurrió un error. Intenta de nuevo.',
  'common.error.NO_ACTIVE_MEMBERSHIP': 'Tu cuenta todavía no pertenece a ninguna municipalidad activa. Pedile acceso a un administrador.',
  'common.error.TENANT_CONTEXT_REQUIRED': 'Elegí primero una municipalidad para continuar.',
  'common.error.network': 'No se pudo conectar con el servidor.',
  'common.empty': 'No hay datos para mostrar.',
  'common.retry': 'Reintentar',
  'app.error.title': 'Algo se rompió en esta pantalla',
  'app.error.body':
    'No es culpa suya y no se perdió nada de lo que estaba guardado. Puede reintentar; si vuelve ' +
    'a pasar, pásele este mensaje a quien mantiene la plataforma.',
  // Support reference for a server-produced Problem Details (CONTRACT.md §4): the stable `code`
  // is what an engineer greps for and the `traceId` is what correlates the user's report with the
  // server's log line. Shown instead of a bare "something went wrong", which costs a whole
  // round-trip with the person reporting it.
  'common.error.reference': 'Código {{code}} · Traza {{traceId}}',
  'common.error.referenceNoTrace': 'Código {{code}}',
  'common.cancel': 'Cancelar',
  'common.save': 'Guardar',
  'common.confirm': 'Confirmar',
  'common.back': 'Atrás',
  'common.next': 'Siguiente',
  'common.search': 'Buscar',
  'common.filter': 'Filtrar',
  'common.actions': 'Acciones',
  'common.status': 'Estado',
  'common.yes': 'Sí',
  'common.no': 'No',
  'common.close': 'Cerrar',
  'common.understood': 'Entendido',
  'common.select.placeholder': 'Selecciona una opción',
  'common.optional': 'opcional',
  'common.languageSwitcher.label': 'Cambiar idioma',

  'auth.portal.citizen.title': 'Portal ciudadano',
  'auth.portal.admin.title': 'Administración municipal',
  'auth.portal.inspector.title': 'Fiscalización',
  'auth.portal.platform.title': 'Back-office de plataforma',
  // Copy tomado del sitio del cliente (luparx.com/app/login) para que el portal web y la marca hablen igual.
  'auth.hero.description':
    'Encuentra tu zona, administra tu tiempo y mantén tus pagos en un solo lugar.',

  'auth.login.title': 'Ingresá a tu cuenta',
  'auth.login.emailLabel': 'Correo electrónico',
  'auth.login.passwordLabel': 'Contraseña',
  'auth.login.passwordToggle.show': 'Mostrar contraseña',
  'auth.login.passwordToggle.hide': 'Ocultar contraseña',
  'auth.login.submit': 'Iniciar sesión',
  'auth.login.forgotPassword': '¿Olvidaste tu contraseña?',
  'auth.login.noAccount': '¿No tienes una cuenta?',
  'auth.login.registerLink': 'Crear cuenta',
  'auth.login.platformNoRegister': 'Las cuentas de este portal las crea un administrador de plataforma.',

  'auth.login.error.invalidCredentials': 'Correo electrónico o contraseña incorrectos.',
  'auth.login.error.network': 'No se pudo conectar con el servidor. Revisá tu conexión o que el servicio esté disponible.',
  'auth.login.error.tooManyAttempts': 'Demasiados intentos. Esperá unos minutos antes de volver a probar.',
  'auth.login.error.server': 'El servidor no pudo procesar el inicio de sesión.',

  'auth.forgotPassword.title': 'Recuperar contraseña',
  'auth.forgotPassword.description':
    'Ingresa tu correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.',
  'auth.forgotPassword.emailLabel': 'Correo electrónico',
  'auth.forgotPassword.submit': 'Enviar instrucciones',
  'auth.forgotPassword.success': 'Si el correo existe, recibirás instrucciones en breve.',
  'auth.forgotPassword.checkInbox': 'Revisá tu bandeja de entrada y la carpeta de spam. El enlace vence en 1 hora.',
  'auth.forgotPassword.backToLogin': 'Volver a iniciar sesión',

  'auth.resetPassword.title': 'Restablecer contraseña',
  'auth.resetPassword.newPasswordLabel': 'Nueva contraseña',
  'auth.resetPassword.confirmPasswordLabel': 'Confirmar contraseña',
  'auth.resetPassword.submit': 'Restablecer',
  'auth.resetPassword.success': 'Tu contraseña se actualizó correctamente.',
  'auth.resetPassword.redirecting': 'Te llevamos a iniciar sesión…',
  'auth.resetPassword.goToLogin': 'Iniciar sesión ahora',

  'auth.register.title': 'Crear cuenta',
  'auth.register.submit': 'Registrarme',
  'auth.register.success.pendingApproval':
    'Tu registro fue recibido. Un administrador debe aprobar tu acceso antes de que puedas iniciar sesión.',
  'auth.register.success.active': 'Tu cuenta fue creada. Ya puedes iniciar sesión.',
  'auth.register.loginLink': '¿Ya tienes cuenta? Inicia sesión',
  'auth.register.section.name': 'Nombre completo',
  'auth.register.section.identityDocument': 'Documento de identidad',
  'auth.register.section.address': 'Dirección',
  'auth.register.section.phone': 'Teléfono',
  'auth.register.section.nationality': 'Nacionalidad',
  'auth.register.section.email': 'Correo electrónico',
  'auth.register.section.birthDate': 'Fecha de nacimiento',
  'auth.register.section.account': 'Datos de la cuenta',

  'auth.logout.action': 'Cerrar sesión',

  'user.field.givenName': 'Nombre',
  'user.field.familyName': 'Primer apellido',
  'user.field.secondFamilyName': 'Segundo apellido',
  'user.field.identityDocument.countryCode': 'País emisor del documento',
  'user.field.identityDocument.type': 'Tipo de documento',
  'user.field.identityDocument.number': 'Número de documento',
  'user.field.address.country': 'País',
  'user.field.address.line1': 'Dirección (línea 1)',
  'user.field.address.line2': 'Dirección (línea 2)',
  'user.field.address.postalCode': 'Código postal',
  'user.field.address.loadError': 'No se pudo cargar esta lista.',
  'user.field.phone.countryCode': 'País',
  'user.field.phone.nationalNumber': 'Número de teléfono',
  'user.field.nationalityCode': 'Nacionalidad',
  'user.field.email': 'Correo electrónico',
  'user.field.birthDate': 'Fecha de nacimiento',
  'user.field.password': 'Contraseña',
  'user.field.confirmPassword': 'Confirmar contraseña',
  'user.field.tenant': 'Municipalidad',
  'user.field.tenant.none': 'Sin municipalidad por ahora',

  'document.type.NATIONAL_ID': 'Cédula de identidad',
  'document.type.FOREIGN_RESIDENT_ID': 'Cédula de residencia (DIMEX)',
  'document.type.PASSPORT': 'Pasaporte',
  'document.type.TAX_ID': 'Identificación tributaria',
  'document.type.OTHER': 'Otro documento',

  'address.level.province': 'Provincia',
  'address.level.canton': 'Cantón',
  'address.level.district': 'Distrito',
  'address.level.state': 'Estado/Provincia',
  'address.level.county': 'Condado',
  'address.level.city': 'Ciudad',

  'terms.accept': 'Acepto los términos y condiciones y la política de privacidad',
  'terms.viewLink': 'Ver términos',

  'validation.required': 'Este campo es obligatorio.',
  'validation.email.invalid': 'Ingresa un correo electrónico válido.',
  'validation.password.tooShort': 'La contraseña debe tener al menos {{min}} caracteres.',
  'validation.password.mismatch': 'Las contraseñas no coinciden.',
  'validation.phone.invalid': 'Ingresa un número de teléfono válido.',
  'validation.document.invalid': 'El número de documento no tiene un formato válido.',
  'validation.age.tooYoung': 'Debes tener al menos {{min}} años para registrarte.',
  'validation.date.invalid': 'Ingresa una fecha válida.',
  'validation.terms.required': 'Debes aceptar los términos y condiciones.',
  'validation.code.length': 'El código debe tener {{length}} dígitos.',
  'validation.generic': 'El valor ingresado no es válido.',
  'validation.form.incomplete.one': 'Falta un dato para continuar: «{{field}}». Te llevamos a ese campo.',
  'validation.form.incomplete.many':
    'Faltan {{count}} datos para continuar. El primero es «{{field}}»: te llevamos a ese campo.',

  'pagination.of': 'de',
  'pagination.itemsPerPage': 'Filas por página',
  'pagination.next': 'Página siguiente',
  'pagination.previous': 'Página anterior',
  'pagination.page': 'Página',
  'pagination.resultCount.zero': 'Sin resultados',
  'pagination.resultCount.one': '{{count}} resultado',
  'pagination.resultCount.other': '{{count}} resultados',

  // El título de la hoja es sólo "Municipalidad": nombra lo que se está eligiendo, y cabe en una
  // barra de 320 px sin cortarse. La frase completa vive en la descripción, debajo.
  'tenant.sheet.title': 'Municipalidad',
  'tenant.sheet.description': 'Las zonas, las tarifas y tu saldo dependen de la que elijás.',
  // La hoja del ciudadano ofrece todo el país, no sólo su historial: elegir una municipalidad
  // nueva lo inscribe en el acto (POST /citizen/session/tenant), con saldo en cero.
  'tenant.sheet.catalogDescription':
    'Elegí cualquier municipalidad del país. Si es nueva para vos, entrás con saldo en cero y con sus zonas y tarifas.',
  'tenant.sheet.searchLabel': 'Buscar municipalidad',
  'tenant.sheet.searchPlaceholder': 'Buscar municipalidad',
  'tenant.sheet.memberBadge': 'Ya la usás',
  'tenant.sheet.noMatches.title': 'Sin resultados',
  'tenant.sheet.noMatches.description': 'Ninguna municipalidad coincide con «{{query}}».',
  'tenant.selector.title': 'Elegí tu municipalidad',
  'tenant.selector.description':
    'Tu cuenta pertenece a varias municipalidades. Las zonas, las tarifas y tu saldo dependen de la que elijas.',
  'tenant.selector.continue': 'Continuar',
  'tenant.selector.noneAvailable': 'Aún no tienes acceso aprobado a ninguna municipalidad.',
  'tenant.selector.empty.title': 'Sin municipalidades por ahora',
  // El mismo caso que responde el servidor con NO_ACTIVE_MEMBERSHIP: una sola situación, una sola
  // redacción (la descripción reutiliza esa clave, no la repite con otras palabras).
  'tenant.selector.error.notAvailable':
    'Esa municipalidad ya no está disponible para tu cuenta. Elegí otra o pedile acceso a un administrador.',
  // TENANT_NOT_OPEN_TO_CITIZENS: la municipalidad decidió no admitir registros por cuenta propia.
  // No es un error del ciudadano y no hay nada que pueda corregir, así que se dice tal cual.
  'tenant.selector.error.notOpenToCitizens':
    'Esta municipalidad no admite registros por cuenta propia. Pedile acceso a su administración.',
  'tenant.selector.signOut': 'Cerrar sesión',
  'tenant.badge.changeAction': 'cambiar de municipalidad',
  'tenant.switch.action': 'Cambiar',
  'tenant.switch.description': 'Al cambiar de municipalidad se actualizan las zonas, las tarifas y tu saldo.',
  'tenant.switch.parkingHint': 'Las zonas, las tarifas y el formato de bahía son de esta municipalidad.',
  'tenant.membership.status.ACTIVE': 'Activo',
  'tenant.membership.status.PENDING_APPROVAL': 'Pendiente de aprobación',
  'tenant.membership.status.REJECTED': 'Rechazado',
  'tenant.membership.status.REVOKED': 'Revocado',

  'admin.search.placeholder': 'Buscar…',
  'admin.search.destinations': 'Ir a',
  'admin.search.inUsers': 'Buscar en Usuarios',
  'admin.search.empty': 'Nada coincide con eso.',
  'admin.header.account': 'Tu cuenta',
  'admin.users.title': 'Usuarios',
  'admin.users.subtitle': 'Personas registradas y sus accesos a LuParX.',
  'admin.users.column.accountStatus': 'Estado de cuenta',
  'admin.users.noRole': 'Sin rol asignado',

  'admin.zones.title': 'Zonas',
  'admin.zones.description':
    'Los sectores donde esta municipalidad cobra. Una zona no se borra nunca: se desactiva, y todo lo que se pagó en ella se sigue pudiendo leer.',
  'admin.zones.create': 'Crear zona',
  'admin.zones.edit': 'Editar zona',
  'admin.zones.empty': 'Esta municipalidad todavía no tiene zonas.',
  'admin.zones.created': 'Zona creada.',
  'admin.zones.updated': 'Zona actualizada.',
  'admin.zones.error.codeTaken': 'Ya hay otra zona con ese código en esta municipalidad.',
  'admin.zones.error.generic': 'No se pudo guardar el cambio.',
  'admin.zones.column.zone': 'Zona',
  'admin.zones.column.spaces': 'Espacios',
  'admin.zones.column.status': 'Estado',
  'admin.zones.spaceCount.one': '{{count}} espacio',
  'admin.zones.spaceCount.other': '{{count}} espacios',
  'admin.zones.status.active': 'Operando',
  'admin.zones.status.inactive': 'Desactivada',
  'admin.zones.action.spaces': 'Espacios',
  'admin.zones.action.activate': 'Activar',
  'admin.zones.deactivate.title': '¿Desactivar esta zona?',
  'admin.zones.deactivate.body':
    'Deja de aceptar estacionamientos nuevos de inmediato. Las estadías que ya están corriendo ' +
    'siguen su curso y las boletas y el historial se conservan. La zona no se borra: se puede ' +
    'volver a activar cuando quiera.',
  'admin.zones.activate.title': '¿Activar esta zona?',
  'admin.zones.activate.body': 'Vuelve a aceptar estacionamientos con las tarifas y el horario que tiene configurados.',
  'admin.zones.deactivate.confirm': 'Desactivar zona',
  'admin.zones.activate.confirm': 'Activar zona',
  'admin.zones.field.state': 'Estado',
  'admin.zones.state.active': 'Operando',
  'admin.zones.state.inactive': 'Inactiva',
  'admin.zones.action.deactivate': 'Desactivar',
  'admin.zones.action.rules': 'Reglas de la zona',
  'admin.zones.rules.title': 'Reglas de {{zone}}',
  'admin.zones.rules.notice':
    'Lo que deje en blanco lo hereda de la municipalidad, y sigue heredándolo si la municipalidad lo cambia. El texto gris de cada campo es lo que aplica hoy.',
  'admin.zones.rules.saved': 'Reglas de la zona guardadas.',
  'admin.zones.rules.inheritHint': 'Hoy aplica: {{value}}',
  'admin.zones.rules.incrementsLabel': 'Duraciones que se venden aquí (minutos, separadas por coma)',
  'admin.zones.rules.minLabel': 'Estadía mínima',
  'admin.zones.rules.maxLabel': 'Estadía máxima',
  'admin.zones.rules.maxHint': 'Es la palanca que maneja la rotación: dos horas en el centro, más en la periferia.',
  'admin.zones.rules.freeLabel': 'Minutos de cortesía',
  'admin.zones.rules.freeHint': 'Se conceden una vez por placa y día. Cero es no dar cortesía.',
  'admin.zones.rules.ownScheduleLabel': 'Esta zona tiene su propio horario de cobro',
  'admin.zones.rules.dayFree': 'No se cobra',
  'admin.zones.rules.freeZoneWarning':
    'Con horario propio y sin ninguna franja, esta zona no cobra nunca. Es válido —una zona gratuita— pero conviene que sea a propósito.',
  'admin.zones.rules.error.band': 'Revise las horas del {{day}}: la de cierre tiene que ser posterior a la de apertura.',
  'admin.zones.rules.error.invalid': 'Revise los números: la cortesía no puede pasar de la estadía máxima.',
  'admin.zones.field.code': 'Código',
  'admin.zones.field.codeHint': 'Es como se le llama a la zona en los reportes y en la radio. No se puede cambiar después.',
  'admin.zones.field.codeFixed': 'El código no se cambia: es por lo que se agrupan los reportes de todo lo cobrado en esta zona.',
  'admin.zones.field.name': 'Nombre',
  'admin.zones.field.description': 'Descripción',

  'admin.spaces.title': 'Espacios',
  'admin.spaces.description':
    'Las bahías de una zona. El código es el que está pintado en el suelo; si la municipalidad lo repinta, corríjalo aquí y la bahía sigue siendo la misma.',
  'admin.spaces.create': 'Agregar espacio',
  'admin.spaces.empty': 'Esta zona todavía no tiene espacios.',
  'admin.spaces.created': 'Espacio agregado.',
  'admin.spaces.updated': 'Espacio actualizado.',
  'admin.spaces.pickZone': 'Escoja una zona para ver sus espacios.',
  'admin.spaces.zoneLabel': 'Zona',
  'admin.spaces.zonePlaceholder': 'Seleccione una zona',
  'admin.spaces.search.label': 'Buscar por código',
  'admin.spaces.search.placeholder': 'Por ejemplo {{example}}',
  'admin.spaces.search.hint':
    'Busca en toda la municipalidad, aunque no haya una zona escogida. Desde dos caracteres.',
  'admin.spaces.search.empty': 'Ninguna bahía empieza por «{{query}}».',
  'admin.spaces.search.more': 'Hay más. Escriba algún carácter más para acotar.',
  'admin.spaces.search.showingOne': 'Mostrando solo la bahía {{code}}.',
  'admin.spaces.search.clear': 'Ver todas las bahías de la zona',
  'admin.spaces.column.code': 'Código',
  'admin.spaces.column.status': 'Estado',
  'admin.spaces.status.AVAILABLE': 'En servicio',
  'admin.spaces.status.OUT_OF_SERVICE': 'Fuera de servicio',
  'admin.spaces.action.outOfService': 'Sacar de servicio',
  'admin.spaces.action.backInService': 'Poner en servicio',
  'admin.spaces.action.editCode': 'Editar código',
  'admin.spaces.editCode.title': 'Editar el código de la bahía',
  'admin.spaces.editCode.current': 'Código actual: {{code}}',
  'admin.spaces.editCode.notice':
    'Es la misma bahía: conserva su historial, su zona y su estado. Las estadías ya pagadas y las boletas ya emitidas siguen mostrando el código que tenían en ese momento.',
  'admin.spaces.renamed': 'Código actualizado. Lo ya cobrado conserva el código anterior.',
  'admin.spaces.codeHint': 'Formato de esta municipalidad, por ejemplo {{example}}.',
  'admin.spaces.error.codeTaken': 'Ya hay un espacio con ese código en esta municipalidad.',
  'admin.spaces.error.codeInvalid': 'Ese código no tiene el formato de esta municipalidad ({{example}}).',

  'admin.tariffs.grid.title': 'Precio por duración',
  'admin.tariffs.grid.description':
    'Lo que se le cobra hoy a un ciudadano por cada duración en cada sector. Toque una celda para ponerle un precio propio a esa duración; sin precio propio la cobra la tarifa base de la zona.',
  'admin.tariffs.fromBase': 'de la base',
  'admin.tariffs.base.label': 'Tarifa base',
  'admin.tariffs.base.value': 'Base: {{amount}} por {{minutes}} min',
  'admin.tariffs.base.missing': 'Sin tarifa base — ponerla',
  'admin.tariffs.noDurations': 'Esta municipalidad todavía no vende ninguna duración. Se definen en Política de parqueo.',
  'admin.tariffs.column.what': 'Qué',
  'admin.tariffs.action.clear': 'Quitar precio propio',
  'admin.tariffs.set.baseTitle': 'Tarifa base de {{zone}}',
  'admin.tariffs.set.rungTitle': '{{duration}} en {{zone}}',
  'admin.tariffs.set.baseBody':
    'Es el cimiento de la zona: cobra por bloque empezado y le pone precio a toda duración que no tenga uno propio, incluidos los minutos guardados de un ciudadano.',
  'admin.tariffs.set.rungBody':
    'Este monto ES el precio de esa duración, sin multiplicar por nada. Reemplaza lo que la base cobraría.',
  'admin.tariffs.set.inheriting': 'Hoy esa duración la cobra la base: {{price}}.',
  'admin.tariffs.saved.base': 'Tarifa base actualizada en {{zone}}.',
  'admin.tariffs.saved.rung': '{{duration}} en {{zone}} ya tiene precio propio.',
  'admin.tariffs.cleared': '{{duration}} en {{zone}} vuelve a cobrarse con la base.',
  'admin.tariffs.action.other': 'Otra duración…',
  'admin.tariffs.field.duration': 'Duración (minutos)',
  'admin.tariffs.field.durationHint': 'Cualquier número de minutos.',
  'admin.tariffs.set.newTitle': 'Otra duración en {{zone}}',
  'admin.tariffs.set.newNotice':
    'Al guardar, esta duración también se pone a la venta en toda la municipalidad: un precio para algo que nadie puede comprar no se le cobra a nadie.',
  'admin.tariffs.error.generic': 'El servidor rechazó el cambio.',
  'admin.tariffs.error.network': 'No se pudo hablar con el servidor.',
  'admin.tariffs.error.unknownRoute':
    'El servidor no reconoce esta operación. Puede que esté corriendo una versión anterior a la que tiene esta pantalla.',
  'admin.tariffs.error.zoneGone': 'Esa zona ya no existe en esta municipalidad.',
  'admin.tariffs.error.noRung': 'Esa duración ya no tiene precio propio.',
  'admin.tariffs.error.invalid': 'El monto o la duración no son válidos.',
  'admin.tariffs.error.forbidden': 'Su cuenta no puede cambiar tarifas en esta municipalidad.',
  'admin.tariffs.title': 'Tarifas',
  'admin.tariffs.description':
    'Cuánto cuesta cada duración en cada zona. Cada una puede tener su propio precio; la que no tenga la cobra la tarifa base de la zona, por bloque empezado.',
  'admin.tariffs.unpriced':
    'Estas zonas no tienen tarifa vigente, así que nadie puede parquear en ellas: {{zones}}. Al ciudadano que lo intente se le rechaza la estadía.',
  'admin.tariffs.noZones': 'Esta municipalidad todavía no tiene zonas.',
  'admin.tariffs.set.submit': 'Poner tarifa',
  // --- Portada del portal de administración (especificación del 23-09-2026) ---------------------
  'admin.home.footer.product': 'LuParX · Plataforma municipal de parqueo',
  'admin.home.kpi.inPeriod': 'Hoy',
  'admin.home.subtitle': 'Resumen operativo · {{date}}',
  'admin.home.system.api': 'Sistema',
  'admin.home.system.database': 'Base de datos',
  'admin.home.system.enforcement': 'Fiscalización',
  'admin.home.system.gateway': 'Pasarela de pagos',
  'admin.home.system.alerts': 'Alertas',
  'admin.home.system.up': 'Operativo',
  'admin.home.system.down': 'Sin respuesta',
  'admin.home.system.unknown': 'No se pudo comprobar',
  'admin.home.system.enforcementActive': '{{count}} en operación hoy',
  'admin.home.system.enforcementIdle': 'Sin actividad hoy',
  // Dicho así y no con un punto verde: esta plataforma todavía no tiene de dónde preguntarlo, y un
  // «Operativo» sin haber preguntado hace que alguien confíe.
  'admin.home.system.noSource': 'Sin verificación disponible',
  'admin.home.system.noAlerts': 'Sin alertas',
  'admin.home.system.failedPayments': '{{count}} pagos rechazados hoy',
  'admin.home.kpi.revenue': 'Ingresos del día',
  'admin.home.kpi.vsYesterday': '{{delta}} contra ayer',
  'admin.home.kpi.noComparison': 'Sin comparación disponible',
  /* --- Referencia visual exacta del Inicio (08-10-2026) ------------------------------------ */
  'admin.home.greeting': 'Hola, {{name}}',
  'admin.home.greeting.plain': 'Hola',
  'admin.home.where': 'Administración municipal · {{tenant}}',
  'admin.home.kpi.registeredUsers': 'Usuarios registrados',
  'admin.home.kpi.registered': 'Cuentas en esta municipalidad',
  'admin.home.occupancy.total': 'Ocupación total',
  'admin.home.occupancy.detail': 'Ver detalle',
  'admin.home.occupancy.noBase': 'Sin bahías con las que medirla',
  'admin.home.rank.title': 'Zonas más utilizadas',
  'admin.home.rank.detail': 'Ver detalle',
  'admin.home.rank.empty': 'Todavía no hay zonas con bahías numeradas.',
  /* --- Inicio municipal, referencia cerrada (09-10-2026) ------------------------------------ */
  'admin.home.occupancy.spaces': '{{activas}} / {{bahias}} espacios',
  'admin.home.revenue.weekTotal': 'Total de la semana',
  'admin.home.attention.title': 'Atención requerida',
  'admin.home.attention.review': 'Revisar',
  'admin.home.attention.payments.one': '1 pago rechazado hoy',
  'admin.home.attention.payments.other': '{{count}} pagos rechazados hoy',
  'admin.home.attention.noZones': 'Ninguna zona tiene bahías numeradas',
  'admin.home.system.title': 'Estado de los servicios',
  // --- Roles y permisos (§3 de la guía funcional, 24-09-2026) -----------------------------------
  'nav.roles': 'Roles y permisos',
  'admin.roles.title': 'Roles y permisos',
  'admin.roles.subtitle': 'Qué puede hacer cada rol en esta municipalidad.',
  'admin.roles.readOnly':
    'Esta tabla es la que el servidor aplica en cada operación. Se consulta acá y se cambia en el código, donde queda versionada y revisable.',
  'admin.roles.matrix.title': 'Permisos por rol',
  'admin.roles.matrix.description':
    'Tocá un rol para ver qué concede y qué no, agrupado por área de trabajo.',
  'admin.roles.summary': '{{granted}} de {{total}} permisos',
  'admin.roles.expand': 'Ver los permisos de {{role}}',
  'admin.roles.collapse': 'Ocultar los permisos de {{role}}',
  'admin.roles.group.people': 'Personas y accesos',
  'admin.roles.group.staff': 'Funcionarios y roles',
  'admin.roles.group.tenant': 'Municipalidad y parqueo',
  'admin.roles.group.citations': 'Boletas y fiscalización',
  'admin.roles.group.finance': 'Finanzas y conciliación',
  'admin.roles.group.security': 'Seguridad y auditoría',
  'admin.roles.group.other': 'Otros',
  'admin.roles.empty': 'No hay roles para este portal.',
  'admin.roles.column.role': 'Rol',
  'admin.roles.column.portal': 'Portal',
  'admin.roles.granted': 'Concedido',
  'admin.roles.denied': 'No concedido',
  'admin.roles.permission.USER_READ': 'Ver personas',
  'admin.roles.permission.USER_WRITE': 'Editar personas',
  'admin.roles.permission.USER_BLOCK': 'Bloquear personas',
  'admin.roles.permission.MEMBERSHIP_APPROVE': 'Aprobar membresías',
  'admin.roles.permission.ROLE_ASSIGN': 'Asignar roles',
  'admin.roles.permission.ZONE_ASSIGN': 'Asignar zonas',
  'admin.roles.permission.AUDIT_READ': 'Ver auditoría',
  'admin.roles.permission.EXPORT_RUN': 'Exportar',
  'admin.roles.permission.TENANT_MANAGE': 'Administrar la municipalidad',
  'admin.roles.permission.PLATFORM_MANAGE': 'Administrar la plataforma',
  'admin.roles.permission.CITATION_ISSUE': 'Emitir boletas',
  'admin.roles.permission.CITATION_READ': 'Ver boletas',
  'admin.roles.permission.CITATION_VOID': 'Anular boletas',
  'admin.roles.permission.ENFORCEMENT_MANAGE': 'Administrar fiscalización',
  'admin.roles.permission.CITATION_INGEST': 'Cargar boletas externas',
  'admin.roles.permission.WALLET_TOPUP': 'Recargar y conciliar',
  // --- Historial por registro (§4) --------------------------------------------------------------
  'admin.history.empty': 'Todavía no hay cambios registrados para este registro.',
  'admin.history.column.when': 'Cuándo',
  'admin.history.column.who': 'Quién',
  'admin.history.column.what': 'Acción',
  'admin.history.column.changes': 'Cambio',
  'admin.history.column.reason': 'Motivo',
  'admin.history.system': 'Sistema',
  'admin.history.inactive': 'Inactivo',
  'admin.history.masked': 'valor protegido',
  'admin.audit.filter.module': 'Módulo',
  'admin.audit.filter.module.all': 'Todos los módulos',
  'admin.audit.module.parking-rate': 'Tarifas',
  'admin.audit.module.parking-zone': 'Zonas',
  'admin.audit.module.parking-policy': 'Política de parqueo',
  'admin.audit.module.parking-space': 'Espacios',
  'admin.audit.module.parking-space-format': 'Formato de espacio',
  'admin.audit.module.citation': 'Boletas',
  'admin.audit.module.exemption': 'Exoneraciones',
  'admin.audit.module.user': 'Personas',
  'admin.audit.module.membership': 'Membresías',
  'admin.audit.module.settlement': 'Conciliación',
  'admin.tariffs.who.title': 'Quién cambió esta tarifa',
  'admin.tariffs.who.description': 'El cambio, quién lo hizo y cuándo. Distinto del historial de versiones de abajo.',
  'admin.tariffs.who.zone': 'Zona',
  'admin.tariffs.who.pickZone': 'Elegí una zona',
  'admin.tariffs.who.empty': 'Esta tarifa no se ha modificado desde que se creó.',

  'admin.home.kpi.activeSessions': 'Estacionamientos activos',
  'admin.home.kpi.rightNow': 'En este momento',
  'admin.home.kpi.citations': 'Multas emitidas',
  'admin.home.kpi.occupancy': 'Ocupación actual',
  'admin.home.kpi.open': 'Abrir {{label}}',
  'admin.home.kpi.noBays': 'Sin dato',
  'admin.home.kpi.noBaysHint': 'Ninguna zona tiene bahías numeradas, así que no hay contra qué medirla.',
  'admin.home.revenue.range': 'Últimos {{days}} días',
  'admin.home.revenue.title': 'Ingresos esta semana',
  'admin.home.revenue.description': 'Lo capturado cada día, en el calendario de la municipalidad.',
  'admin.home.revenue.empty': 'Sin recaudación registrada en este período.',
  'admin.home.revenue.columnDay': 'Día',
  'admin.home.revenue.columnAmount': 'Recaudado',
  'admin.home.zones.title': 'Ocupación en tiempo real',
  'admin.home.zones.description': 'Estadías corriendo sobre bahías en servicio.',
  'admin.home.zones.empty': 'Esta municipalidad todavía no tiene zonas.',
  'admin.home.zones.noBays': 'Sin bahías',
  'admin.home.activity.title': 'Actividad reciente',
  'admin.home.activity.description': 'Lo último que pasó en la municipalidad, del registro de auditoría.',
  'admin.home.activity.emptyTitle': 'Todavía no hay movimiento',
  'admin.home.activity.emptyBody':
    'Apenas se emita una boleta, se registre una recarga o alguien cambie una tarifa, va a aparecer acá.',
  'admin.home.activity.system': 'Sistema',
  'admin.home.activity.seeAll': 'Ver todas',
  /* --- Actos que faltaban en la portada (08-10-2026) ----------------------------------------
     Los cuatro primeros son los que la referencia visual aprobada dibuja en «Actividad reciente»
     y que la lista blanca no aceptaba. Ver la nota en HomePage. */
  'admin.home.activity.PARKING_SESSION_STARTED': 'Nuevo estacionamiento iniciado',
  'admin.home.activity.PARKING_SESSION_FINISHED': 'Estacionamiento finalizado',
  'admin.home.activity.USER_REGISTERED': 'Usuario registrado',
  'admin.home.activity.USER_PASSWORD_RESET_REQUESTED': 'Restablecer acceso',
  'admin.home.activity.PARKING_ZONE_CREATED': 'Zona creada',
  'admin.home.activity.WALLET_TOPUP_RECORDED': 'Recarga registrada',
  'admin.home.activity.CITATION_ISSUED': 'Boleta emitida',
  'admin.home.activity.CITATION_PAID': 'Boleta pagada',
  'admin.home.activity.CITATION_CANCELLED': 'Boleta anulada',
  'admin.home.activity.CITATION_APPEAL_FILED': 'Reclamo recibido',
  'admin.home.activity.CITATION_APPEAL_RESOLVED': 'Reclamo resuelto',
  'admin.home.activity.PLATE_EXEMPTION_GRANTED': 'Exoneración otorgada',
  'admin.home.activity.PARKING_SPACE_CREATED': 'Espacio registrado',
  'admin.home.activity.PARKING_ZONE_UPDATED': 'Zona modificada',
  'admin.home.activity.PARKING_RATE_UPDATED': 'Tarifa modificada',
  'admin.home.activity.PARKING_POLICY_UPDATED': 'Política de parqueo modificada',
  'admin.home.activity.PARKING_SCHEDULE_UPDATED': 'Horario de cobro modificado',
  'admin.home.quick.title': 'Accesos rápidos',
  'admin.home.quick.none': 'Esta cuenta no tiene ninguna de estas pantallas habilitada.',
  'admin.home.quick.zone': 'Crear zona',
  'admin.home.quick.zoneHint': 'Definir una nueva área de cobro',
  'admin.home.quick.space': 'Registrar espacio',
  'admin.home.quick.spaceHint': 'Agregar bahías a una zona',
  'admin.home.quick.plate': 'Buscar placa',
  'admin.home.quick.plateHint': 'Consultar boletas y estado',
  'admin.home.quick.report': 'Generar reporte',
  'admin.home.quick.reportHint': 'Exportar lo recaudado y lo fiscalizado',
  'admin.home.noFigures.title': 'Sin acceso a las cifras',
  'admin.home.noFigures.body':
    'Su cuenta no tiene permiso para ver la recaudación ni el registro de auditoría de esta municipalidad. ' +
    'Abajo están las pantallas que sí puede abrir.',
  'admin.tariffs.confirm.title': '¿Poner esta tarifa?',
  'admin.tariffs.confirm.body':
    'Es lo que se le va a cobrar a un ciudadano en la calle desde que usted confirme. Las estadías ' +
    'que ya están corriendo mantienen el precio con el que se pagaron; el precio anterior pasa a ' +
    '«Precios anteriores» y no se borra.',
  'admin.tariffs.confirm.effectiveLabel': 'Rige',
  'admin.tariffs.confirm.effectiveNow': 'Desde que confirme, hasta el próximo cambio',
  'admin.tariffs.confirm.fromBase': '{{price}} (de la base)',
  'admin.tariffs.confirm.none': 'Sin precio',
  'admin.tariffs.set.blockExample':
    'Cobra por bloque empezado: una estadía de {{minutes}} min paga {{blocks}} bloques, {{amount}}.',
  'admin.tariffs.history.title': 'Precios anteriores',
  'admin.tariffs.history.description':
    'Las ventanas ya cerradas. No se editan: son las que le pusieron precio a las estadías que se pagaron mientras regían.',
  'admin.tariffs.history.empty': 'Todavía no se ha cambiado ninguna tarifa.',
  'admin.tariffs.field.amount': 'Monto ({{currency}})',
  'admin.tariffs.field.amountNoCurrency': 'Monto',
  'admin.tariffs.field.minutes': 'Minutos',
  'admin.tariffs.field.minutesHint': 'El bloque que cubre ese monto.',
  'admin.tariffs.column.zone': 'Zona',
  'admin.tariffs.column.price': 'Precio',
  'admin.tariffs.column.from': 'Desde',
  'admin.tariffs.column.to': 'Hasta',
  'admin.tariffs.price': '{{amount}} por {{minutes}} min',
  'admin.tariffs.openWindow': 'Sin cierre',

  'admin.staff.title': 'Funcionarios',
  'admin.staff.description':
    'El personal de esta municipalidad. Desactivar a alguien le quita el acceso aquí y no le toca la cuenta ni el historial de lo que hizo.',
  'admin.staff.empty': 'Esta municipalidad todavía no tiene funcionarios.',
  'admin.staff.error': 'No se pudo completar la acción.',
  'admin.staff.suspended': 'Acceso suspendido.',
  'admin.staff.reactivated': 'Acceso restablecido. El puesto vuelve a estar activo.',
  'admin.staff.revoked': 'Acceso revocado.',
  'admin.staff.resetSent': 'Se le envió el enlace para poner una contraseña nueva.',
  'admin.staff.zonesAssigned': 'Sectores actualizados.',
  'admin.staff.filter.status': 'Estado',
  'admin.staff.filter.all': 'Todos los estados',
  'admin.staff.column.person': 'Persona',
  'admin.staff.column.role': 'Rol',
  'admin.staff.column.status': 'Estado',
  'admin.staff.column.zones': 'Sectores',
  'admin.staff.column.lastLogin': 'Último acceso',
  'admin.staff.column.actions': 'Acciones',
  'admin.staff.lastLogin.never': 'Nunca ingresó',
  'admin.staff.zones.all': 'Todos',
  'admin.staff.zones.more': '+{{count}}',
  'admin.staff.zones.allTitle': 'Sin restricción de sector: puede trabajar en todos.',
  'admin.staff.zones.title': 'Sectores asignados',
  'admin.staff.zones.help':
    'Un fiscalizador sólo puede consultar placas y levantar boletas en los sectores que tenga asignados.',
  'admin.staff.zones.noneMeansAll': 'Sin sectores marcados puede actuar en toda la municipalidad.',
  'admin.staff.add.title': 'Agregar funcionario',
  'admin.staff.add.help':
    'Primero busque a la persona: mucha gente ya tiene cuenta porque se registró como ciudadana. Si ya existe, se le da el puesto sin volver a pedirle sus datos.',
  'admin.staff.add.searchBy': 'Buscar por',
  'admin.staff.add.by.document': 'Por identificación',
  'admin.staff.add.by.email': 'Por correo electrónico',
  'admin.staff.add.exactHint': 'Tiene que ser el dato completo y exacto; no es un buscador por partes.',
  'admin.staff.add.search': 'Buscar',
  'admin.staff.add.searchAgain': 'Buscar a otra persona',
  'admin.staff.add.notFound': 'Nadie en la plataforma tiene ese dato. Si es su primera vez, ábrale el expediente.',
  'admin.staff.add.createNew': 'Crear expediente nuevo',
  'admin.staff.add.noAccessHere': 'Todavía no tiene ningún acceso en esta municipalidad.',
  'admin.staff.add.blocked': 'Esta cuenta está bloqueada en la plataforma. Desbloquéela antes de darle un puesto.',
  'admin.staff.add.rolePerApp': 'Un puesto por aplicación: puede ser fiscalizador en la app de calle y llevar finanzas en este portal, con la misma cuenta.',
  'admin.staff.add.appTaken': 'Ya tiene un puesto en esa aplicación',
  'admin.staff.add.grant': 'Dar acceso',
  'admin.staff.add.granted': 'Listo: {{name}} ahora es {{role}} en esta municipalidad. Se le avisó por correo.',
  'admin.staff.add.error.rateLimited': 'Demasiadas búsquedas seguidas. Espere unos minutos.',
  'admin.staff.add.error.documentInvalid': 'Ese número no tiene el formato del documento seleccionado.',
  'admin.staff.add.error.alreadyHasAccess': 'Esa persona ya tiene un puesto en esa aplicación en esta municipalidad.',
  'invitation.title': 'Le ofrecieron un puesto en {{tenant}}',
  'invitation.description': 'Puesto: {{role}}. La invitación se envió a {{email}}.',
  'invitation.expires': 'La invitación vence el {{date}}.',
  'invitation.passwordIsYours':
    'La contraseña la elige usted. La municipalidad no la conoce ni puede verla: eso es lo que hace que lo que usted haga en el sistema quede a su nombre y no al de otra persona.',
  'invitation.submit': 'Aceptar y crear mi cuenta',
  'invitation.done.title': 'Listo, su cuenta está creada',
  'invitation.done.body': 'Ya puede ingresar a {{tenant}} como {{role}} con el correo de la invitación y la contraseña que acaba de elegir.',
  'invitation.done.signIn': 'Ir a ingresar',
  'invitation.error.notFound': 'Esta invitación ya no está disponible. Pídale a la municipalidad que se la envíe de nuevo.',
  'invitation.error.expired': 'Esta invitación venció. Pídale a la municipalidad que se la envíe de nuevo.',
  'invitation.error.alreadyAccepted': 'Esta invitación ya se usó. Ingrese con su cuenta.',
  'invitation.error.documentTaken':
    'Ese documento de identidad ya está registrado en la plataforma. Avísele a la municipalidad: no hace falta invitarlo, pueden darle el puesto a la cuenta que ya tiene.',
  'admin.staff.add.inviteHelp':
    'Invítela: la municipalidad pone el correo y el puesto, y ella completa sus propios datos desde un enlace. Así la cédula la escribe quien la tiene, que es lo único que evita una identidad equivocada.',
  'admin.staff.add.inviteEmailHint': 'Ahí le llega el enlace para aceptar. Vence en 14 días.',
  'admin.staff.add.invite': 'Invitar por correo',
  'admin.staff.add.invited': 'Invitación enviada a {{email}} como {{role}}.',
  'admin.staff.add.error.emailRegistered':
    'Ese correo ya tiene cuenta. Búsquelo aquí mismo por correo y déle el puesto sin volver a pedirle sus datos.',
  'admin.staff.invitations.title': 'Invitaciones',
  'admin.staff.invitations.description':
    'Puestos ofrecidos a gente que todavía no tiene cuenta. La invitación no da acceso: el acceso nace cuando la persona la acepta.',
  'admin.staff.invitations.empty': 'No hay invitaciones pendientes.',
  'admin.staff.invitations.column.email': 'Correo',
  'admin.staff.invitations.column.role': 'Puesto ofrecido',
  'admin.staff.invitations.column.sent': 'Enviada',
  'admin.staff.invitations.column.expires': 'Vence',
  'admin.staff.invitations.expired': 'Vencida',
  'admin.staff.invitations.action.resend': 'Enviar de nuevo',
  'admin.staff.invitations.action.revoke': 'Retirar',
  'admin.staff.invitations.resent': 'Se envió de nuevo. El enlace anterior dejó de servir.',
  'admin.staff.invitations.revoked': 'Invitación retirada.',
  'admin.staff.column.lastUsed': 'Último uso del puesto',
  'admin.staff.lastUsed.none': 'Sin uso registrado',
  'admin.staff.lastUsed.personHint': 'La persona ingresó el {{date}}, por otra aplicación.',
  'admin.staff.action.changeRole': 'Cambiar rol',
  'admin.staff.changeRole.title': 'Cambiar el rol del puesto',
  'admin.staff.changeRole.body': 'Puesto de {{name}} en {{app}}.',
  'admin.staff.changeRole.sameApp':
    'Sólo se puede cambiar dentro de la misma aplicación. Para darle un puesto en otra app, agréguelo como puesto nuevo: son dos puestos, no un cambio.',
  'admin.staff.changeRole.done': 'Rol actualizado.',
  'portal.citizen': 'App ciudadana',
  'portal.platform': 'Plataforma',
  'portal.admin': 'Portal municipal',
  'portal.inspector': 'App de fiscalización',
  'admin.staff.action.suspend': 'Desactivar',
  'admin.staff.action.reactivate': 'Restablecer acceso',
  'admin.staff.action.revoke': 'Revocar',
  'admin.staff.action.zones': 'Sectores',
  'admin.staff.action.resetAccess': 'Forzar cambio de contraseña',
  'admin.staff.action.open': 'Ver ficha',
  'admin.staff.actions.menu': 'Acciones de este puesto',
  'admin.staff.actions.title': 'Acciones',
  'admin.staff.actions.subject': '{{name}} · {{role}} · {{status}}',
  'admin.staff.actions.none': 'Este puesto está revocado: no queda ninguna acción que aplicarle.',
  'admin.staff.action.resetAccess.self':
    'No se puede forzar el cambio de contraseña sobre la propia cuenta: cerraría tu sesión.',
  'admin.staff.suspend.title': '¿Desactivar a esta persona?',
  'admin.staff.suspend.body':
    '{{name}} deja de tener acceso al portal de esta municipalidad. Puede reactivarlo cuando quiera.',
  'admin.staff.suspend.keepsHistory':
    'La cuenta no se toca: la persona sigue usando la aplicación como ciudadana. Sus boletas y todo lo que hizo se conservan igual.',
  'admin.staff.suspend.reasonLabel': 'Motivo',
  'admin.staff.suspend.reasonPlaceholder': 'Motivo (opcional)',
  'tenant.membership.status.SUSPENDED': 'Desactivado',
  'admin.users.searchPlaceholder': 'Buscar por nombre, correo o documento',
  'admin.users.column.name': 'Nombre',
  'admin.users.column.email': 'Correo electrónico',
  'admin.users.column.status': 'Estado',
  'admin.users.column.role': 'Rol',
  'admin.users.column.tenant': 'Municipalidad',
  'admin.users.column.createdAt': 'Registrado',
  'admin.users.filter.role': 'Rol',
  'admin.users.filter.status': 'Estado',
  'admin.users.filter.portal': 'Portal',
  'admin.users.filter.allRoles': 'Todos los roles',
  'admin.users.filter.allStatuses': 'Todos los estados',
  'admin.users.filter.allPortals': 'Todos los portales',
  'admin.users.empty': 'No se encontraron usuarios con esos criterios.',
  'admin.users.detail.title': 'Detalle de usuario',

  // Roles, as a person reads them. The enum name is what the API speaks; this is what a screen shows.
  'role.PLATFORM_ADMIN': 'Administrador de plataforma',
  'role.PLATFORM_SUPPORT': 'Soporte de plataforma',
  'role.TENANT_ADMIN': 'Administrador municipal',
  'role.TENANT_FINANCE': 'Finanzas',
  'role.TENANT_FINANCE.detail': 'Recargas y reportes financieros de la municipalidad',
  'role.TENANT_SUPPORT': 'Soporte municipal',
  'role.TENANT_SUPPORT.detail': 'Consulta de usuarios, multas y bitácora, sin poder modificar',
  'role.TENANT_INTEGRATION': 'Integración',
  'role.TENANT_INTEGRATION.detail': 'Credenciales de sistemas que se conectan con la municipalidad',
  'role.INSPECTOR': 'Fiscalizador',
  'role.INSPECTOR.detail': 'Verifica placas y levanta boletas en campo',
  'role.INSPECTOR_LEAD': 'Jefe de fiscalización',
  'role.INSPECTOR_LEAD.detail': 'Lo mismo que un fiscalizador, y además puede anular boletas',
  'role.CITIZEN': 'Ciudadano',

  'admin.users.create.cta': 'Crear usuario',
  'admin.users.create.title': 'Crear un usuario de la municipalidad',
  'admin.users.create.description':
    'La persona recibe un correo para elegir su propia contraseña. Hasta que lo haga no puede ingresar, y nadie más puede hacerlo por ella.',
  'admin.users.create.access.label': 'Acceso',
  'admin.users.create.access.description': 'Qué va a poder hacer esta persona y a qué correo se le avisa.',
  'admin.users.create.roleLabel': 'Rol',
  'admin.users.create.emailHint': 'A esta dirección llega el enlace para elegir la contraseña.',
  'admin.users.create.personalData.label': 'Datos de la persona',
  'admin.users.create.personalData.description':
    'Los mismos datos que se le piden a cualquiera al registrarse. Escríbalos como aparecen en el expediente.',
  'admin.users.create.submit': 'Crear y enviar el correo',
  'admin.users.create.error.EMAIL_ALREADY_REGISTERED':
    'Ya existe una cuenta con ese correo. Use «Agregar funcionario» en Funcionarios: ahí se busca a la persona y se le da el puesto sin volver a crearla.',
  'admin.users.create.error.DOCUMENT_ALREADY_REGISTERED':
    'Ya existe una cuenta con esa identificación. Use «Agregar funcionario» en Funcionarios: ahí se busca a la persona y se le da el puesto sin volver a crearla.',
  'admin.users.create.error.ROLE_NOT_ALLOWED_FOR_PORTAL':
    'Un administrador municipal no puede otorgar ese rol. Solicítelo a la plataforma.',

  'admin.users.detail.grant.title': 'Otorgar acceso',
  'admin.users.detail.grant.description':
    'Para alguien que ya tiene cuenta: no se crea nada, sólo se le da acceso a esta municipalidad.',
  'admin.users.detail.grant.roleLabel': 'Rol',
  'admin.users.detail.grant.submit': 'Otorgar',
  'admin.users.detail.grant.error': 'No se pudo otorgar el acceso. Puede que la persona ya lo tenga.',
  'admin.users.detail.actions.block': 'Bloquear',
  'admin.users.detail.actions.unblock': 'Desbloquear',
  'admin.users.detail.actions.forcePasswordReset': 'Forzar reseteo de contraseña',
  'admin.users.detail.actions.assignRole': 'Asignar rol',
  'admin.users.detail.actions.approveMembership': 'Aprobar membresía',
  'admin.users.detail.actions.rejectMembership': 'Rechazar membresía',
  'admin.users.detail.blockReasonLabel': 'Motivo del bloqueo',
  'admin.users.detail.rejectReasonLabel': 'Motivo del rechazo',
  'admin.users.detail.actionSuccess': 'La acción se aplicó correctamente.',
  'admin.users.detail.account.title': 'Estado de la cuenta',
  'admin.users.detail.account.description':
    'La cuenta de LuParX de esta persona. Es una sola, y no depende de ninguna municipalidad.',
  'admin.users.detail.access.title': 'Acceso a la municipalidad',
  'admin.users.detail.access.description':
    'Cada municipalidad donde esta persona tiene un puesto, con su rol y su estado. Son estados DISTINTOS del de la cuenta: desactivar un puesto no bloquea la cuenta, y bloquear la cuenta no revoca ningún puesto.',
  'admin.users.detail.access.none': 'Esta persona no tiene ningún acceso municipal.',
  'admin.users.detail.access.role': 'Rol',
  'admin.users.detail.access.status': 'Estado del acceso',
  'admin.users.detail.actions.forcePasswordReset.self':
    'No se puede forzar el cambio de contraseña sobre la propia cuenta: cerraría tu sesión.',
  'admin.users.detail.actions.failed': 'No se pudo aplicar la acción.',
  'admin.users.status.ACTIVE': 'Activo',
  'admin.users.status.BLOCKED': 'Bloqueado',
  'admin.users.status.PENDING_VERIFICATION': 'Pendiente de verificación',

  'admin.audit.title': 'Auditoría',
  'admin.audit.column.actor': 'Actor',
  'admin.audit.column.action': 'Acción',
  'admin.audit.column.resource': 'Recurso',
  'admin.audit.column.occurredAt': 'Fecha',
  'admin.audit.column.origin': 'Origen',
  'admin.audit.actor.system': 'Sistema',
  'admin.audit.actor.inactive': 'inactivo',
  'admin.audit.origin.system': 'Proceso del sistema',
  'admin.audit.origin.fingerprint': 'Huella de la conexión. No es la dirección: la plataforma no la guarda.',
  'admin.audit.origin.probe.title': 'Cotejar una dirección IP',
  'admin.audit.origin.probe.description':
    'La plataforma no guarda direcciones, sólo una huella. Si usted ya tiene una dirección bajo sospecha, escríbala aquí y le decimos si aparece en esta bitácora. La dirección no se guarda ni viaja en la dirección web, y el cotejo queda registrado.',
  'admin.audit.origin.probe.placeholder': 'Dirección IP',
  'admin.audit.origin.probe.submit': 'Cotejar',
  'admin.audit.origin.probe.found': '{{count}} entrada(s) desde esa dirección. Huella {{fingerprint}}.',
  'admin.audit.origin.probe.none': 'Ninguna entrada de esta municipalidad viene de esa dirección en el período consultado.',
  'admin.audit.origin.probe.filter': 'Ver sólo esas entradas',
  'admin.audit.origin.probe.failed': 'No se pudo cotejar la dirección.',
  'admin.audit.group.people': 'Personas y accesos',
  'admin.audit.group.staff': 'Funcionarios y roles',
  'admin.audit.group.tenant': 'Municipalidad',
  'admin.audit.group.parking': 'Parqueo y espacios',
  'admin.audit.group.exemptions': 'Exoneraciones',
  'admin.audit.group.citations': 'Boletas',
  'admin.audit.group.finance': 'Finanzas y conciliación',
  'admin.audit.group.security': 'Seguridad y auditoría',
  'admin.audit.origin.probe.invalid':
    'Eso no es una dirección IP. Escribí una IPv4 como 190.10.1.25 o una IPv6 como 2001:db8::1.',
  'admin.audit.filter.only': 'Ver sólo lo de esta persona',
  'admin.audit.filter.onlyPerson': 'Mostrando sólo lo realizado por {{name}}.',
  'admin.audit.filter.onlyOrigin': 'Mostrando sólo lo que vino de la conexión {{fingerprint}}.',
  'admin.audit.filter.clear': 'Quitar el filtro',
  'admin.audit.column.changes': 'Qué cambió',
  'admin.audit.masked': 'enmascarado',
  'admin.audit.chain.title': 'Integridad de la bitácora',
  'admin.audit.chain.description':
    'Las entradas no se pueden modificar ni borrar: la base de datos lo rechaza. Además se sellan en cadena, así que si alguien lo lograra, se puede demostrar.',
  'admin.audit.chain.intact': 'Cadena íntegra: {{entries}} entrada(s) cubierta(s) por {{seals}} sello(s).',
  'admin.audit.chain.broken': 'La cadena no cuadra: {{problems}} hallazgo(s). Esto amerita revisión de una persona.',
  'admin.audit.chain.sealedThrough': 'Sellada hasta el {{date}}. Lo posterior está protegido, pero todavía no sellado.',
  'admin.audit.chain.notSealedYet': 'Todavía no hay sellos: se emiten cada diez minutos.',
  'admin.audit.chain.lastSeal': 'Último sello #{{seq}} · {{digest}}…',
  'admin.audit.chain.problem.DIGEST_MISMATCH': 'Filas cambiadas o desaparecidas dentro de un rango ya sellado',
  'admin.audit.chain.problem.BROKEN_LINK': 'Un sello no sigue al anterior',
  'admin.audit.chain.problem.MISSING_SEAL': 'Falta un sello de la serie',
  'audit.action.USER_REGISTERED': 'Persona registrada',
  'audit.action.USER_CREATED': 'Cuenta creada por un operador',
  'audit.action.USER_UPDATED': 'Datos de la persona actualizados',
  'audit.action.USER_BLOCKED': 'Cuenta bloqueada',
  'audit.action.USER_UNBLOCKED': 'Cuenta desbloqueada',
  'audit.action.USER_PASSWORD_RESET_REQUESTED': 'Restablecimiento de contraseña solicitado',
  'audit.action.USER_PASSWORD_CHANGED': 'Contraseña cambiada',
  'audit.action.USER_EMAIL_VERIFIED': 'Correo verificado',
  'audit.action.USER_EMAIL_CHANGE_REQUESTED': 'Cambio de correo solicitado',
  'audit.action.USER_EMAIL_CHANGED': 'Correo cambiado',
  'audit.action.USER_DIRECTORY_LOOKUP': 'Búsqueda en el directorio de personas',
  'audit.action.LOGIN_SUCCEEDED': 'Ingreso exitoso',
  'audit.action.LOGIN_FAILED': 'Intento de ingreso fallido',
  'audit.action.LOGOUT': 'Cierre de sesión',
  'audit.action.REFRESH_TOKEN_REUSE_DETECTED': 'Reutilización de token detectada',
  'audit.action.SESSION_TENANT_SWITCHED': 'Cambio de municipalidad activa',
  'audit.action.FEDERATED_IDENTITY_LINKED': 'Identidad federada vinculada',
  'audit.action.MEMBERSHIP_REQUESTED': 'Acceso solicitado',
  'audit.action.MEMBERSHIP_CREATED': 'Puesto otorgado',
  'audit.action.MEMBERSHIP_APPROVED': 'Acceso aprobado',
  'audit.action.MEMBERSHIP_REJECTED': 'Acceso rechazado',
  'audit.action.MEMBERSHIP_REVOKED': 'Funcionario revocado',
  'audit.action.MEMBERSHIP_SUSPENDED': 'Funcionario desactivado',
  'audit.action.MEMBERSHIP_REACTIVATED': 'Funcionario reactivado',
  'audit.action.MEMBERSHIP_ZONES_ASSIGNED': 'Sectores asignados',
  'audit.action.MEMBERSHIP_ROLE_CHANGED': 'Rol cambiado',
  'audit.action.STAFF_INVITATION_SENT': 'Invitación enviada',
  'audit.action.STAFF_INVITATION_REVOKED': 'Invitación revocada',
  'audit.action.STAFF_INVITATION_ACCEPTED': 'Invitación aceptada',
  'audit.action.TENANT_CREATED': 'Municipalidad creada',
  'audit.action.TENANT_UPDATED': 'Municipalidad actualizada',
  'audit.action.TENANT_STATUS_CHANGED': 'Estado de la municipalidad cambiado',
  'audit.action.TENANT_SETTING_CHANGED': 'Configuración de la municipalidad cambiada',
  'audit.action.TENANT_LOCALES_UPDATED': 'Idiomas de la municipalidad actualizados',
  'audit.action.TENANT_BRANDING_UPDATED': 'Identidad visual actualizada',
  'audit.action.EXPORT_REQUESTED': 'Exportación solicitada',
  'audit.action.CATALOG_UPDATED': 'Catálogo actualizado',
  'audit.action.PLATFORM_SCOPE_ACCESS': 'Acceso con alcance de plataforma',
  'audit.action.RETENTION_PURGE_RAN': 'Purga por retención ejecutada',
  'audit.action.AUDIT_ORIGIN_PROBED': 'Dirección IP cotejada',
  'audit.action.VEHICLE_REGISTERED': 'Vehículo registrado',
  'audit.action.VEHICLE_UPDATED': 'Vehículo actualizado',
  'audit.action.VEHICLE_DELETED': 'Vehículo eliminado',
  'audit.action.VEHICLE_PRIMARY_CHANGED': 'Vehículo principal cambiado',
  'audit.action.PARKING_SESSION_STARTED': 'Estadía iniciada',
  'audit.action.PARKING_SESSION_EXTENDED': 'Estadía extendida',
  'audit.action.PARKING_SESSION_FINISHED': 'Estadía terminada',
  'audit.action.PARKING_POLICY_UPDATED': 'Política de parqueo actualizada',
  'audit.action.PARKING_SCHEDULE_UPDATED': 'Horario de cobro actualizado',
  'audit.action.PARKING_ZONE_CREATED': 'Zona creada',
  'audit.action.PARKING_ZONE_UPDATED': 'Zona actualizada',
  'audit.action.PARKING_ZONE_RULES_UPDATED': 'Reglas de zona actualizadas',
  'audit.action.PARKING_ZONE_GEOMETRY_UPDATED': 'Perímetro de zona modificado',
  'audit.action.PARKING_ZONE_GEOMETRY_CLEARED': 'Perímetro de zona borrado',
  'audit.action.PARKING_ZONE_GEOMETRY_VERIFIED': 'Perímetro de zona verificado',
  'audit.action.PARKING_ZONE_GEOMETRY_REJECTED': 'Perímetro de zona rechazado',
  'audit.action.PARKING_RATE_UPDATED': 'Tarifa actualizada',
  'audit.action.PARKING_SPACE_CREATED': 'Espacio creado',
  'audit.action.PARKING_SPACE_UPDATED': 'Espacio actualizado',
  'audit.action.PARKING_SPACE_RENAMED': 'Espacio renombrado',
  'audit.action.PARKING_SPACE_FORMAT_UPDATED': 'Formato de espacios actualizado',
  'audit.action.PLATE_EXEMPTION_REQUESTED': 'Exoneración solicitada',
  'audit.action.PLATE_EXEMPTION_GRANTED': 'Exoneración otorgada',
  'audit.action.PLATE_EXEMPTION_APPROVED': 'Exoneración aprobada',
  'audit.action.PLATE_EXEMPTION_REJECTED': 'Exoneración rechazada',
  'audit.action.PLATE_EXEMPTION_AMENDED': 'Exoneración modificada',
  'audit.action.PLATE_EXEMPTION_REVOKED': 'Exoneración revocada',
  'audit.action.PLATE_EXEMPTION_PLATE_ADDED': 'Placa agregada a una exoneración',
  'audit.action.PLATE_EXEMPTION_PLATE_REMOVED': 'Placa quitada de una exoneración',
  'audit.action.PLATE_EXEMPTION_DOCUMENT_ATTACHED': 'Documento adjuntado a una exoneración',
  'audit.action.EXEMPTION_TYPE_CREATED': 'Tipo de exoneración creado',
  'audit.action.EXEMPTION_TYPE_UPDATED': 'Tipo de exoneración actualizado',
  'audit.action.CITATION_DRAFTED': 'Boleta en borrador',
  'audit.action.CITATION_ISSUED': 'Boleta levantada',
  'audit.action.CITATION_EVIDENCE_ATTACHED': 'Evidencia adjuntada a una boleta',
  'audit.action.CITATION_STATUS_CHANGED': 'Estado de boleta cambiado',
  'audit.action.CITATION_CANCELLED': 'Boleta anulada',
  'audit.action.CITATION_PAID': 'Boleta pagada',
  'audit.action.CITATION_INGESTED': 'Boleta importada',
  'audit.action.INFRACTION_TYPES_UPDATED': 'Tipos de infracción actualizados',
  'audit.action.EXTERNAL_CAUSAL_MAPPED': 'Causal externa mapeada',
  'audit.action.ENFORCEMENT_SETTINGS_UPDATED': 'Ajustes de fiscalización actualizados',
  'audit.action.CITATION_APPEAL_FILED': 'Reclamo presentado',
  'audit.action.CITATION_APPEAL_RESOLVED': 'Reclamo resuelto',
  'audit.action.APPEAL_NOTICE_PUBLISHED': 'Notificación de reclamo publicada',
  'audit.action.SETTLEMENT_IMPORTED': 'Conciliación importada',
  'audit.action.SETTLEMENT_DISPUTED': 'Conciliación objetada',
  'audit.action.WALLET_TOPUP_RECORDED': 'Recarga de billetera registrada',
  'audit.action.WALLET_TOPUP_CODE_ROTATED': 'Código de recarga rotado',
  'audit.action.WALLET_TOPUP_CODE_RESOLVED': 'Código de recarga canjeado',
  'admin.audit.filter.action.all': 'Todas las acciones',
  'admin.audit.search.label': 'Buscar en la bitácora',
  'admin.audit.search.placeholder': 'Buscar por identificador de recurso o código de evento',
  'admin.audit.pageSize.label': 'Filas por página',
  'admin.audit.pageSize.option': '{{count}} por página',
  'admin.audit.detail.open': 'Ver detalle',
  'admin.audit.detail.title': 'Detalle de auditoría',
  'admin.audit.detail.actor': 'Actor',
  'admin.audit.detail.action': 'Acción',
  'admin.audit.detail.module': 'Módulo / recurso',
  'admin.audit.detail.changes': 'Qué cambió',
  'admin.audit.detail.occurredAt': 'Fecha y hora',
  'admin.audit.detail.origin': 'Origen',
  'admin.audit.detail.resourceId': 'ID del recurso',
  'admin.audit.detail.technicalEvent': 'Evento técnico',
  'admin.audit.detail.fingerprint': 'Huella / identificador de origen',
  'admin.audit.field.name': 'Nombre',
  'admin.audit.field.description': 'Descripción',
  'admin.audit.field.active': 'Estado',
  'admin.audit.field.code': 'Código',
  'admin.audit.field.divisionId': 'Distrito',
  'admin.audit.value.active.true': 'Activa',
  'admin.audit.value.active.false': 'Inactiva',
  'admin.audit.changes.none': 'Sin cambios en los datos',
  'admin.audit.changes.full': 'Ver el valor completo',
  'admin.audit.field.role': 'Rol',
  'admin.audit.field.status': 'Estado',
  'admin.audit.field.zones': 'Sectores',
  /* El formato del código de espacio, que ahora deja su antes/después (06-10-2026). */
  'admin.audit.field.prefix': 'Prefijo',
  'admin.audit.field.digits': 'Cantidad de caracteres',
  'admin.audit.field.allowLetters': 'Permitir letras',
  'admin.audit.field.pattern': 'Patrón que se valida',
  'admin.audit.value.allowLetters.true': 'Sí',
  'admin.audit.value.allowLetters.false': 'No',
  'admin.audit.value.empty': '(vacío)',
  'admin.audit.module.staff-invitation': 'Invitaciones',
  'admin.audit.module.wallet': 'Billeteras',
  'admin.audit.module.parking-session': 'Estadías',
  'admin.audit.module.vehicle': 'Vehículos',
  'admin.audit.module.tenant': 'Municipalidad',
  'admin.audit.module.audit': 'Bitácora',
  'admin.staff.revoke.title': '¿Revocar el puesto de esta persona?',
  'admin.staff.revoke.body':
    '{{name}} deja de tener este puesto en la municipalidad. A diferencia de desactivar, revocar no se levanta: para devolverle el acceso hay que otorgarle el puesto de nuevo.',
  'admin.staff.revoke.keepsHistory':
    'La cuenta no se toca y todo lo que hizo se conserva igual. Las boletas que levantó siguen siendo suyas.',
  'admin.staff.revoke.confirm': 'Revocar',
  'admin.staff.error.stale':
    'Esta lista no se pudo actualizar, así que puede estar mostrando datos viejos. Recargue la pantalla.',
  'admin.staff.reset.title': '¿Forzar un cambio de contraseña?',
  'admin.staff.reset.body':
    'A {{name}} se le envía un enlace para poner una contraseña nueva. No cambia su puesto, su rol ni sus sectores: para devolverle un acceso quitado, use «Restablecer acceso».',
  'admin.staff.reset.closesSessions':
    'Se le cierran todas las sesiones abiertas, en todos sus dispositivos. Si está trabajando, va a tener que volver a entrar.',
  'admin.staff.reset.confirm': 'Enviar el enlace',
  'admin.staff.error.selfAccount':
    'No puede ejecutar esto sobre su propia cuenta: le cerraría la sesión en el acto. Pídaselo a otra persona administradora.',
  'admin.staff.error.self':
    'No puede terminar ni pausar su propio puesto. Pídaselo a otra persona administradora de esta municipalidad.',
  'admin.staff.changeRole.noAlternatives':
    'No hay otro rol disponible para este puesto en esta aplicación. Para darle un rol de otra app, agréguele un puesto nuevo.',
  'admin.reports.registeredUsers.groupByLabel': 'Agrupar por',
  'admin.reports.registeredUsers.reportLabel': 'Reporte',
  'admin.audit.filter.action': 'Acción',
  'admin.audit.filter.from': 'Desde',
  'admin.audit.filter.to': 'Hasta',
  'admin.audit.empty': 'No hay eventos de auditoría con esos criterios.',

  'admin.reports.registeredUsers.title': 'Usuarios registrados',
  /* --- El módulo de reportes, preparado para crecer (06-10-2026) ------------------------------
     Un selector de tipo con un solo tipo parece una promesa incumplida, y con tipos que el
     servidor no implementa es peor: una opción que no responde. Se listan los que existen. */
  'admin.reports.title': 'Reportes',
  'admin.reports.subtitle': 'Los reportes de esta municipalidad, por período.',
  'admin.reports.typeLabel': 'Tipo de reporte',
  'admin.reports.type.registered-users': 'Usuarios registrados',
  'admin.reports.fromLabel': 'Desde',
  'admin.reports.toLabel': 'Hasta',
  'admin.reports.rangeInvalid': 'La fecha de inicio tiene que ser anterior a la de fin.',
  'admin.reports.comingSoon':
    'Los demás reportes municipales —recaudación, ocupación por zona, boletas, exoneraciones y conciliación— se agregarán acá cuando el servidor los exponga. Esta pantalla ya está lista para recibirlos.',
  'admin.reports.registeredUsers.groupBy': 'Agrupar por',
  'admin.reports.registeredUsers.groupBy.tenant': 'Municipalidad',
  'admin.reports.registeredUsers.groupBy.country': 'País',
  'admin.reports.registeredUsers.groupBy.portal': 'Portal',
  'admin.reports.registeredUsers.groupBy.month': 'Mes',
  'admin.reports.registeredUsers.export': 'Exportar CSV',
  'admin.reports.registeredUsers.column.group': 'Grupo',
  'admin.reports.registeredUsers.column.count': 'Cantidad',
  'admin.reports.registeredUsers.empty': 'No hay datos con estos filtros.',
  'admin.reports.registeredUsers.period': 'Personas registradas entre el {{from}} y el {{to}}.',

  'home.citizen.title': 'Inicio',
  'home.citizen.parkingStub.title': 'Parquímetros',
  'home.citizen.parkingStub.description':
    'Próximamente podrás iniciar y pagar sesiones de estacionamiento desde aquí.',

  'nav.home': 'Inicio',
  'nav.portal.admin': 'Municipalidad',
  'nav.group.operation': 'Operación',
  'nav.group.administration': 'Administración',
  'nav.profile': 'Perfil',
  'nav.users': 'Usuarios',
  'nav.staff': 'Funcionarios',
  'nav.zones': 'Zonas',
  'nav.zonesGeometry': 'Perímetros',
  'admin.zoneGeometry.title': 'Perímetros de zona',
  'admin.zoneGeometry.description': 'Cargá el polígono oficial (GeoJSON WGS84) de cada zona y verificalo para que el ciudadano lo vea en el mapa.',
  'admin.zoneGeometry.selectZone': 'Zona',
  'admin.zoneGeometry.selectZonePlaceholder': 'Elegí una zona…',
  'admin.zoneGeometry.current': 'Esta zona ya tiene un perímetro cargado.',
  'admin.zoneGeometry.currentNone': 'Esta zona todavía no tiene perímetro.',
  'admin.zoneGeometry.paste': 'Pegá el GeoJSON del perímetro (Polygon o MultiPolygon, orden lon/lat)',
  'admin.zoneGeometry.validate': 'Validar',
  'admin.zoneGeometry.valid': 'GeoJSON válido',
  'admin.zoneGeometry.rings': '{{count}} anillo(s)',
  'admin.zoneGeometry.vertices': '{{count}} vértices',
  'admin.zoneGeometry.outsideCR': 'Aviso: la caja cae fuera de Costa Rica. Revisá que el orden sea [lon, lat] y no [lat, lon].',
  'admin.zoneGeometry.save': 'Guardar perímetro',
  'admin.zoneGeometry.saved': 'Perímetro guardado. Queda sin verificar hasta que lo apruebes.',
  'admin.zoneGeometry.verify': 'Verificar como oficial',
  'admin.zoneGeometry.verified': 'Perímetro verificado: ya es oficial y el ciudadano lo verá en el mapa.',
  'admin.zoneGeometry.verifyHint': 'Verificá solo un perímetro oficial. Una geometría DEMO no se puede aprobar.',
  'admin.zoneGeometry.error.generic': 'No se pudo guardar el perímetro.',
  'admin.zoneGeometry.error.verify': 'No se pudo verificar el perímetro.',
  'nav.spaces': 'Espacios',
  'nav.tariffs': 'Tarifas',
  'nav.parkingPolicy': 'Política de parqueo',

  // ---- Municipalidad: política de parqueo ------------------------------------------------------

  'admin.policy.title': 'Política de parqueo',
  'admin.policy.description':
    'Qué puede hacer un ciudadano con una estadía en esta municipalidad: cuánto tiempo puede comprar, si puede extender, si le devuelven los minutos que no usó y cuánto se le tolera después de que se le vence.',
  'admin.policy.preview.title': 'Lo que verá el ciudadano',
  'admin.policy.preview.description': 'Estas son las duraciones que le van a aparecer al escoger cuánto tiempo parquear.',
  'admin.policy.preview.none': 'Con estos números el ciudadano no puede escoger ninguna duración, así que no podría parquear.',
  'admin.policy.session.title': 'Duraciones que se venden',
  'admin.policy.session.description':
    'Las opciones del selector. De esta lista salen también el piso y el techo de una estadía, así que no hay forma de ofrecer algo que después se rechace.',
  'admin.policy.session.empty': 'Todavía no hay ninguna duración.',
  'admin.policy.session.range': 'Con esta lista se vende desde {{min}} hasta {{max}} de una sola vez.',
  'admin.policy.error.emptyList': 'Sin ninguna duración el ciudadano no podría parquear. Agregue al menos una.',
  'admin.policy.minuteField': 'Minutos',
  'admin.policy.addOption': 'Agregar',
  'admin.policy.removeOption': 'Quitar {{option}}',
  'admin.policy.removeConfirm.title': '¿Quitar {{option}}?',
  'admin.policy.removeConfirm.body':
    'Deja de ofrecérsele al ciudadano. El cambio se aplica cuando guarde esta pantalla.',
  'admin.policy.removeConfirm.confirm': 'Quitar',
  'admin.policy.extension.title': 'Extensiones',
  'admin.policy.extension.description': 'Si el ciudadano puede agregarle tiempo a una estadía que ya está corriendo, y cuánto.',
  'admin.policy.extension.enabled': 'Permitir extender una estadía',
  'admin.policy.extension.enabledHint': 'Con esto apagado, al ciudadano se le vence y tiene que iniciar una nueva.',
  'admin.policy.extension.empty': 'Todavía no hay ninguna duración de extensión.',
  'common.duration.hoursWithMinutes': '{{hours}} ({{minutes}} min)',
  'common.unit.minutes': 'minutos',
  'common.unit.days': 'días',
  'admin.policy.field.extensionMaxTotal': 'Techo total con extensiones',
  'admin.policy.field.extensionMaxTotalHint': 'Sumando la estadía y todo lo que se le agregue. No puede ser menor que la duración más larga que vende.',
  'admin.policy.credit.title': 'Terminar antes y minutos guardados',
  'admin.policy.credit.description':
    'Si el ciudadano puede cerrar la estadía antes de tiempo, y si lo que no usó se le guarda para la próxima vez.',
  'admin.policy.credit.earlyFinish': 'Permitir terminar antes',
  'admin.policy.credit.earlyFinishHint': 'Cerrar la estadía al irse, sin esperar a que se venza.',
  'admin.policy.credit.enabled': 'Guardarle los minutos que no usó',
  'admin.policy.credit.enabledHint': 'Sin terminar antes no hay minutos que guardar, así que esto queda apagado con aquello.',
  'admin.policy.field.creditMin': 'Mínimo para que se le guarden',
  'admin.policy.field.creditMinHint': 'Si le sobra menos que esto, no se le guarda nada.',
  'admin.policy.field.creditExpiry': 'Vencen a los (días)',
  'admin.policy.field.creditExpiryHint': 'Cuánto le duran los minutos guardados. 0 es que no vencen.',
  'admin.policy.grace.title': 'Tolerancia',
  'admin.policy.grace.description': 'Cuánto tiempo después de que se vence la estadía todavía no se puede levantar una boleta.',
  'admin.policy.field.grace': 'Tolerancia',
  'admin.policy.field.graceHint': 'En minutos. 0 es sin tolerancia.',
  'admin.policy.grace.notice':
    'Un fiscalizador que consulte una placa dentro de esos {{minutes}} después del vencimiento la va a ver como cubierta.',
  'admin.policy.summary.title': 'Cómo queda',
  'admin.policy.confirm.title': '¿Guardar la política de parqueo?',
  'admin.policy.confirm.body':
    'Cambia desde este momento qué se le puede vender a un ciudadano en toda la municipalidad. ' +
    'Las estadías que ya están corriendo siguen con las reglas que tenían.',
  'admin.policy.confirm.noChanges':
    'No hay nada distinto de lo que ya está guardado. Puede guardar igual, pero no va a cambiar nada.',
  'admin.policy.confirm.extension': 'Se puede extender',
  'admin.policy.confirm.extensionList': 'Duraciones de extensión',
  'admin.policy.confirm.extensionMax': 'Tope con extensiones',
  'admin.policy.confirm.earlyFinish': 'Se puede terminar antes',
  'admin.policy.confirm.credit': 'Devuelve crédito al terminar antes',
  'admin.policy.confirm.creditMin': 'Mínimo que queda para devolver',
  'admin.policy.confirm.creditExpiry': 'El crédito vence en',
  'admin.policy.confirm.days.one': '{{count}} día',
  'admin.policy.confirm.days.other': '{{count}} días',
  'admin.policy.summary.session': 'Duraciones',
  'admin.policy.summary.extension': 'Extensiones',
  'admin.policy.summary.credit': 'Minutos guardados',
  'admin.policy.summary.creditOn': 'Desde {{min}}, vencen a los {{days}} días',
  'admin.policy.summary.grace': 'Tolerancia',
  'admin.policy.summary.overlap': 'Espacio compartido',
  'admin.policy.summary.overlapOn': 'Permitido',
  'admin.policy.summary.off': 'Apagado',
  // v0.37 — el espacio que nadie liberó. Los dos avisos hablan de fiscalización porque es el único
  // lugar donde la decisión se siente: apagar esto no es neutral, es decidir que el segundo
  // ciudadano se estaciona sin cobertura.
  'admin.policy.overlap.title': 'Espacios con más de una estadía',
  'admin.policy.overlap.description': 'Qué pasa cuando alguien llega a un espacio que otra estadía todavía no liberó.',
  'admin.policy.overlap.enabled': 'Permitir pagar un espacio que figura ocupado',
  'admin.policy.overlap.enabledHint':
    'Quien paga dos horas y se va a los quince minutos sin finalizar deja el espacio libre en la calle y ocupado en el sistema.',
  'admin.policy.overlap.notice':
    'Cada estadía cubre su propia placa en ese espacio. El fiscalizador consulta placa y bahía, así que ninguno de los dos vehículos queda expuesto a una boleta por no pagar.',
  'admin.policy.overlap.offNotice':
    'Con esto apagado, el segundo ciudadano no puede pagar el espacio: se estaciona igual y queda sin nada que mostrarle al fiscalizador.',
  'admin.policy.error.generic': 'No se pudo guardar la política.',
  'admin.policy.error.extensionCeiling': 'El techo con extensiones no puede ser menor que {{max}}, que es la estadía más larga que vende.',
  'nav.dashboard': 'Panel',
  // --- Panel: filtros de período, KPIs y densidad (correcciones UX/UI v2, 23-09-2026) ----------
  'admin.dashboard.filters.apply': 'Aplicar',
  'admin.dashboard.filters.presets': 'Atajos de período',
  'admin.dashboard.filters.preset.hoy': 'Hoy',
  'admin.dashboard.filters.preset.ayer': 'Ayer',
  'admin.dashboard.filters.preset.ultimos7': 'Últimos 7 días',
  'admin.dashboard.filters.preset.ultimos30': 'Últimos 30 días',
  'admin.dashboard.filters.preset.esteMes': 'Este mes',
  'admin.dashboard.filters.preset.mesAnterior': 'Mes anterior',
  'admin.dashboard.filters.preset.personalizado': 'Personalizado',
  'admin.dashboard.kpi.netRevenueInPeriod': 'Recaudación del período',
  'admin.dashboard.kpi.paidStaysInPeriod': 'Estacionamientos del período',
  'admin.dashboard.kpi.citationsInPeriod': 'Boletas del período',
  'admin.dashboard.kpi.netRevenue': 'Recaudación neta',
  'admin.dashboard.kpi.paidStays': 'Estacionamientos pagados',
  'admin.dashboard.kpi.occupancy': 'Ocupación actual',
  'admin.dashboard.kpi.citations': 'Boletas emitidas',
  'admin.dashboard.kpi.inPeriod': 'En el período',
  'admin.dashboard.kpi.rightNow': 'En este momento',
  'admin.dashboard.occupancy.overBays': '{{active}} de {{bays}} bahías',
  // «Sin datos» y no «0»: un cero es un hecho medido, la ausencia de fuente es otra cosa (§14).
  'admin.dashboard.noData': 'Sin datos',
  'admin.dashboard.parking.expand': 'Ver todos los estados',
  'admin.dashboard.parking.collapse': 'Ver menos',
  'admin.dashboard.group.finance': 'Finanzas',
  'admin.dashboard.group.operation': 'Operación',
  'admin.dashboard.title': 'Panel de la municipalidad',
  'admin.dashboard.window': 'Del {{from}} al {{to}}.',
  'admin.dashboard.empty': 'Sin movimientos en el período.',
  'admin.dashboard.revenue.title': 'Recaudación',
  'admin.dashboard.revenue.description': 'Lo cobrado en el período y lo que ningún proveedor ha confirmado.',
  'admin.dashboard.revenue.captured': 'Cobrado',
  'admin.dashboard.revenue.capturedCount': '{{count}} pago(s)',
  'admin.dashboard.revenue.net': 'Neto',
  'admin.dashboard.revenue.unsettled': 'Sin confirmar',
  'admin.dashboard.transactions.title': 'Movimientos de billetera',
  'admin.dashboard.transactions.description': 'Los cargos se muestran con signo negativo y las recargas con signo positivo.',
  'admin.dashboard.parking.title': 'Estacionamientos del período',
  'admin.dashboard.parking.description': 'Estadías del período, por cómo se pagaron.',
  'admin.dashboard.occupancy.title': 'Ocupación por zona',
  'admin.dashboard.occupancy.description': 'Ocupación actual sobre bahías en servicio.',
  'admin.dashboard.occupancy.asOf': 'Al {{time}} · estado actual, no del período.',
  'admin.dashboard.occupancy.active': 'estadías vigentes',
  'admin.dashboard.occupancy.unzoned': '{{count}} de ellas fuera de toda zona.',
  'admin.dashboard.occupancy.noZones': 'Esta municipalidad no tiene zonas configuradas.',
  'admin.dashboard.occupancy.noBays': 'Esta zona no tiene bahías numeradas: no tiene porcentaje. No es 0%.',
  'admin.dashboard.checks.title': 'Fiscalizaciones',
  'admin.dashboard.checks.description': 'Consultas de placa del período, por lo que concluyeron.',
  'admin.dashboard.citations.title': 'Infracciones',
  'admin.dashboard.citations.description': 'Por la fecha en que ocurrieron, no en que se emitieron.',
  'admin.dashboard.exemptions.title': 'Exoneraciones',
  'admin.dashboard.exemptions.description': 'Como están hoy, no del período.',
  'admin.dashboard.inspectors.title': 'Actividad por inspector',
  'admin.dashboard.inspectors.description': 'Consultas y boletas lado a lado, del período.',
  'admin.dashboard.inspectors.none': 'Ningún inspector registró actividad en el período.',
  'admin.dashboard.inspectors.notice': 'Compare consultas y boletas para contextualizar la actividad de fiscalización por inspector.',
  'admin.dashboard.failures.title': 'Fallos de pago',
  'admin.dashboard.failures.description': 'Intentos de pago rechazados durante el período, agrupados por código del proveedor.',
  'admin.dashboard.failures.none': 'No se registraron fallos de pago en el período.',
  'admin.dashboard.failures.count': 'intento(s) fallido(s), por {{amount}}',
  'admin.dashboard.column.zone': 'Zona',
  'admin.dashboard.column.active': 'Vigentes',
  'admin.dashboard.column.bays': 'Bahías en servicio',
  'admin.dashboard.column.occupancy': 'Ocupación',
  'admin.dashboard.column.checks': 'Consultas',
  'admin.dashboard.column.citations': 'Boletas',
  'admin.dashboard.column.lastCheck': 'Última consulta',
  'admin.dashboard.column.code': 'Código',
  'admin.dashboard.column.reason': 'Motivo',
  'admin.dashboard.column.attempts': 'Intentos',
  'wallet.transaction.top_up': 'Recargas',
  'wallet.transaction.session_charge': 'Cobros de estadía',
  'wallet.transaction.extension_charge': 'Cobros de prórroga',
  'wallet.transaction.adjustment': 'Ajustes',
  'parking.payment.paid': 'Pagadas',
  'parking.payment.no_charge': 'Sin costo',
  'parking.payment.pending': 'En curso',
  'parking.payment.failed': 'Fallidas',
  'parking.payment.unknown': 'Sin registrar',
  'nav.billing': 'Conciliación',
  'admin.billing.title': 'Conciliación de pagos',
  'admin.billing.totals.title': 'El período',
  'admin.billing.totals.description':
    'Qué se cobró, qué confirmó el proveedor y qué falta por confirmar. Los tres salen de los mismos datos, así que no pueden contradecirse entre pantallas.',
  'admin.billing.totals.captured': 'Cobrado',
  'admin.billing.totals.capturedCount': '{{count}} pago(s)',
  'admin.billing.totals.settled': 'Confirmado por el proveedor',
  'admin.billing.totals.owed': 'Sin confirmar',
  'admin.billing.totals.net': 'Neto a la municipalidad',
  'admin.billing.totals.netHint': 'Después de comisiones',
  'admin.billing.totals.window': 'Del {{from}} al {{to}}.',
  'admin.billing.totals.failed': '{{count}} intento(s) fallido(s) en el período. Quedan registrados: son la respuesta a «yo pagué y no me subió el saldo».',
  'admin.billing.unsettled.title': 'Cobrado y sin confirmar',
  'admin.billing.unsettled.description':
    'Plata que la municipalidad cobró y que ningún corte de proveedor ha confirmado todavía. El efectivo de caja y los ajustes no aparecen aquí: nadie los va a reportar en un corte, y tenerlos en esta lista sería una alarma permanente por algo que no es un problema.',
  'admin.billing.unsettled.none': 'Todo lo cobrado está confirmado por su proveedor.',
  /* --- Los tres estados de la conciliación (06-10-2026) ---------------------------------------
     El verde de arriba decía «todo confirmado» cuando no había NADA que confirmar, porque su
     única señal era que la lista de pagos sin confirmar estuviera vacía — y lo está en los dos
     casos. Ahora cada estado tiene su frase y ninguna afirma más de lo que los datos dicen. */
  'admin.billing.state.nothingToReconcile':
    'No hay nada que conciliar en este período: lo cobrado no pasa por ningún corte del proveedor.',
  'admin.billing.state.noSettlements':
    'Aún no hay cortes del proveedor para conciliar este período. Hay {{amount}} cobrados esperando el primero.',
  'admin.billing.state.pending':
    '{{amount}} cobrados que ningún corte confirma todavía.',
  'admin.billing.state.reconciled':
    'Todo lo que puede conciliarse en este período está confirmado: {{amount}}.',
  'admin.billing.totals.notApplicable': 'Fuera de corte',
  'admin.billing.totals.notApplicableHint': 'Efectivo de caja y ajustes: ningún proveedor los reporta',
  'admin.billing.totals.settlementsInPeriod': '{{count}} corte(s) del proveedor cubren este período.',
  'admin.billing.settlements.title': 'Cortes recibidos',
  'admin.billing.settlements.description':
    'Lo que cada proveedor dice que liquidó. Se guardan como llegaron: si los totales del encabezado no cuadran con sus propias líneas, eso es un hallazgo y no algo que se corrija aquí.',
  'admin.billing.settlements.none': 'Todavía no se ha importado ningún corte.',
  'admin.billing.findings.title': 'Lo que no cuadró',
  'admin.billing.findings.description':
    'Sólo las líneas que necesitan que alguien las vea. Las que cuadraron no se listan: un corte son miles de líneas de las que cuatro están mal, y mostrarlas todas es esconder esas cuatro.',
  'admin.billing.findings.none': 'Todas las líneas de este corte cuadran.',
  'admin.billing.column.confirmedAt': 'Confirmado',
  'admin.billing.column.method': 'Medio',
  'admin.billing.column.reference': 'Referencia',
  'admin.billing.column.gross': 'Monto',
  'admin.billing.column.reconciliation': 'Conciliación',
  'admin.billing.column.provider': 'Proveedor',
  'admin.billing.column.period': 'Período',
  'admin.billing.column.declaredNet': 'Neto declarado',
  'admin.billing.column.deposit': 'Depósito',
  'admin.billing.column.status': 'Estado',
  'admin.billing.column.finding': 'Hallazgo',
  'admin.billing.deposit.pending': 'Pendiente',
  'wallet.topup.source.citizen': 'Recarga del ciudadano',
  'wallet.topup.source.municipal_counter': 'Caja municipal',
  'wallet.topup.source.partner': 'Comercio afiliado',
  'wallet.topup.source.adjustment': 'Ajuste',
  'wallet.topup.source.dev': 'MODO PRUEBA · saldo simulado',
  'payment.method.card': 'Tarjeta',
  'payment.method.sinpe': 'SINPE',
  'payment.method.bank_transfer': 'Transferencia',
  'payment.method.counter_cash': 'Caja municipal',
  'payment.method.partner': 'Comercio afiliado',
  'payment.method.adjustment': 'Ajuste',
  'payment.state.pending': 'En curso',
  'payment.state.authorized': 'Autorizado',
  'payment.state.captured': 'Cobrado',
  'payment.state.failed': 'Fallido',
  'payment.state.cancelled': 'Cancelado',
  'payment.state.refunded': 'Devuelto',
  'payment.state.charged_back': 'Contracargo',
  'payment.checkout.state.open': 'En curso',
  'payment.checkout.state.completed': 'Terminado',
  'payment.checkout.state.expired': 'Vencido',
  'payment.purpose.wallet_topup': 'Recarga de saldo',
  'payment.purpose.fine': 'Multa',
  'payment.purpose.permit': 'Permiso',
  'payment.purpose.other': 'Otro',
  'payment.network.visa': 'Visa',
  'payment.network.mastercard': 'Mastercard',
  'payment.network.american_express': 'American Express',
  'payment.network.discover': 'Discover',
  'payment.network.other': 'Otra red',
  'payment.network.unknown': 'Red no indicada',
  'payment.reconciliation.pending': 'Sin confirmar',
  'payment.reconciliation.matched': 'Confirmado',
  'payment.reconciliation.missing_in_settlement': 'No apareció en el corte',
  'payment.reconciliation.amount_mismatch': 'Monto distinto',
  'payment.reconciliation.not_applicable': 'No aplica',
  'settlement.status.imported': 'Importado',
  'settlement.status.reconciled': 'Conciliado',
  'settlement.status.disputed': 'En reclamo',
  'settlement.line.matched': 'Cuadra',
  'settlement.line.unknown_payment': 'Pago desconocido aquí',
  'settlement.line.amount_mismatch': 'Monto distinto',
  'settlement.line.duplicate': 'Duplicado en el corte',
  'nav.audit': 'Auditoría',
  'nav.reports': 'Reportes',
  'nav.tenants': 'Municipalidades',
  'nav.park': 'Estacionar',
  'nav.vehicles': 'Vehículos',
  'nav.wallet': 'Billetera',
  'nav.more': 'Más',
  // --- Menú «Más» de Fiscalización (24-09-2026) --------------------------------------------------
  'inspector.more.profile': 'Mi perfil',
  'inspector.more.language': 'Idioma',
  'inspector.more.connection': 'Estado de conexión',
  'inspector.more.connection.online': 'Conectado. Las boletas se envían al momento.',
  'inspector.more.connection.offline': 'Sin conexión. Lo que levantés se guarda y se envía solo.',
  'inspector.more.connection.synced': 'Al día',
  'inspector.more.connection.waiting': 'En espera',
  'inspector.more.connection.pending.one': '{{count}} pendiente',
  'inspector.more.connection.pending.other': '{{count}} pendientes',
  'inspector.more.help': 'Ayuda',
  'inspector.more.help.meta': 'Cómo trabajar un turno.',
  'inspector.more.about': 'Acerca de LuParX',
  'inspector.more.about.meta': 'Abre el sitio oficial.',
  // --- Ayuda: lo que de verdad pasa en un turno --------------------------------------------------
  'inspector.help.offline.action': 'Ver conexión',
  /* --- Ayuda del fiscalizador, reestructurada (08-10-2026) ---------------------------------- */
  'inspector.help.lead': '¿En qué podemos ayudarte?',
  'inspector.help.entry.diag': 'Diagnóstico',
  'inspector.help.entry.permissions': 'Permisos del dispositivo',
  'inspector.help.entry.sync': 'Sincronización',
  'inspector.help.entry.report': 'Reportar un problema',
  'inspector.help.permissions.title': 'Permisos del dispositivo',
  'inspector.help.permissions.lead':
    'La cámara es la que más importa: sin ella no se puede adjuntar evidencia a una boleta.',
  'inspector.help.permissions.none': 'Esta aplicación no pide ningún otro permiso del aparato.',
  'inspector.help.sync.title': 'Sincronización',
  'inspector.help.sync.state': 'Estado',
  'inspector.help.sync.pendingLabel': 'Operaciones esperando',
  'inspector.help.sync.lastLabel': 'Última sincronización',
  'inspector.help.sync.never': 'Este aparato no ha enviado nada todavía.',
  'inspector.help.sync.errorsLabel': 'Errores',
  'inspector.help.sync.noErrors': 'Ninguno.',
  'inspector.help.report.title': 'Reportar un problema',
  'inspector.help.report.noBackend':
    'Todavía no se puede enviar un reporte desde la aplicación: no existe ese canal. Lo que sí podés hacer es copiar el estado de la aplicación y pasárselo a quien te da soporte.',
  'inspector.help.report.copy': 'Copiar el estado',
  'inspector.help.report.copied': 'Copiado. Pegalo en el mensaje a soporte.',
  'inspector.help.report.failed': 'No se pudo copiar. El estado está acá abajo para leerlo.',

  /* --- Centro de soporte, ampliado el 12-10-2026 (encargo «Centro de soporte») ------------------
     Las seis filas del §7, la conectividad efectiva del §2, las guías del §6 y el «Acerca de».
     Lo que NO hay acá es un formulario de incidencias: el §5 pide no construirlo mientras no exista
     un destino autorizado, y no existe. */
  'inspector.help.entry.diag.meta': 'Cámara, ubicación, conexión y almacenamiento',
  'inspector.help.entry.permissions.meta': 'Qué le concediste a la aplicación en este aparato',
  'inspector.help.entry.sync.meta': 'Qué está esperando y qué ya se envió',
  'inspector.help.entry.report.meta': 'Copiar el estado para pasárselo a soporte',
  'inspector.help.entry.guides': 'Guías de uso',
  'inspector.help.entry.guides.meta': 'Cómo se hace cada tarea del turno',
  'inspector.help.entry.about': 'Acerca de LuParX',
  'inspector.help.entry.about.meta': 'Versión, entorno y compilación',
  'inspector.help.diag.tone.unavailable': 'no disponible',

  'inspector.help.diag.connection.checking': 'Comprobando…',
  'inspector.help.diag.connection.ok': 'Hay comunicación con el servidor ({{ms}} ms)',
  'inspector.help.diag.connection.slow': 'El servidor contesta lento ({{ms}} ms)',
  'inspector.help.diag.connection.slowNote':
    'Las boletas salen igual, pero el envío puede parecer colgado. Si podés, buscá mejor señal.',
  'inspector.help.diag.connection.unreachable': 'Hay red, pero no se llega al servidor',
  'inspector.help.diag.connection.unreachableNote':
    'Suele ser un wifi que pide aceptar condiciones, o una caída del servicio. Seguí trabajando: la boleta se guarda acá y sale sola.',
  'inspector.help.diag.connection.timeout': 'El servidor no contestó a tiempo',
  'inspector.help.diag.connection.timeoutNote':
    'Puede ser señal muy débil. Seguí trabajando: la boleta se guarda acá y sale sola cuando mejore.',
  'inspector.help.diag.connection.unauthorized': 'Se llega al servidor, pero la sesión no sirve',
  'inspector.help.diag.connection.unauthorizedNote': 'La red está bien. Volvé a entrar con tu usuario.',
  'inspector.help.diag.connection.retest': 'Comprobar de nuevo',

  'inspector.help.diag.camera.testCancelled': 'Cancelaste la prueba. No se guardó ninguna foto.',
  /* Lo que esta pantalla NO puede afirmar con el permiso bloqueado: que no se puedan tomar fotos
     de una boleta. En navegador la evidencia entra por el selector del sistema, que no usa el
     permiso de la página. Decir «la cámara no funciona» sería un parte falso al revés. */
  'inspector.help.diag.camera.deniedEvidence':
    'Esto bloquea la prueba de acá; la foto de una boleta puede seguir funcionando, porque la toma el selector del teléfono.',

  'inspector.help.diag.location.test': 'Probar ubicación',

  /* --- Fase 1 del encargo de las dos fases (13-10-2026) -----------------------------------------
     La comprobación de ubicación que sí comprueba, su fecha y su resultado, y las instrucciones
     del aparato que corresponde en vez de las de los tres. */
  'inspector.help.diag.location.check': 'Comprobar ubicación',
  'inspector.help.diag.location.checking': 'Comprobando…',
  'inspector.help.diag.location.lastCheck': 'Última comprobación: {{cuando}} · {{resultado}}',
  'inspector.help.diag.location.neverChecked': 'Última comprobación: aún no realizada.',
  'inspector.help.diag.location.result.ok': 'se obtuvo la posición',
  'inspector.help.diag.location.result.denied': 'permiso denegado',
  'inspector.help.diag.location.result.timeout': 'no respondió a tiempo',
  'inspector.help.diag.location.result.unavailable': 'no disponible en este aparato',
  'inspector.help.diag.location.denied': 'Permiso de ubicación denegado',
  'inspector.help.diag.location.deniedWhy':
    'Comprobar de nuevo no puede concederlo: el navegador no vuelve a preguntar una vez denegado. Se habilita en los ajustes del aparato y después esta comprobación lo detecta.',
  'inspector.help.permissions.howTo': 'Cómo habilitar los permisos en este aparato',
  'inspector.help.permissions.recheckNote':
    'Relee el permiso de la cámara, intenta una lectura real de la ubicación, vacía la cola y le pregunta al servidor por la sesión. La cámara se prueba con su propio botón.',
  'inspector.help.diag.location.fix': 'Última lectura: {{lat}}, {{lon}}',
  /* Los dos mapas, que sólo existen con una posición real delante. «Abrir en» y no «Navegar a»:
     Google Maps recibe una chincheta, no una ruta, y el inspector decide qué hace con ella. */
  'inspector.help.diag.location.openMaps': 'Abrir en Google Maps',
  'inspector.help.diag.location.openWaze': 'Abrir en Waze',
  'inspector.help.diag.location.accuracy': 'Precisión aproximada: {{m}} m',
  'inspector.help.diag.location.accuracyUnknown': 'El aparato no informó la precisión.',
  'inspector.help.diag.location.timeout': 'No se obtuvo la posición a tiempo',
  'inspector.help.diag.location.timeoutNote':
    'Pasa bajo techo y entre edificios altos. Probá al aire libre; la boleta se emite igual sin coordenadas.',
  'inspector.help.diag.location.unavailable': 'Este aparato no ofrece ubicación',
  'inspector.help.diag.location.unavailableNote':
    'No hay nada que arreglar en los ajustes. La boleta se emite igual y queda constancia de que no hubo coordenadas.',

  'inspector.help.permissions.settings.title': 'Si está denegado, se cambia en los ajustes del aparato',
  'inspector.help.permissions.settings.ios':
    'iPhone o iPad, aplicación instalada: Ajustes → buscá LuParX en la lista → Cámara y Ubicación.',
  'inspector.help.permissions.settings.safari':
    'iPad o iPhone en Safari: tocá «aA» en la barra de direcciones → Ajustes del sitio web → Cámara y Ubicación. También en Ajustes → Safari → Cámara y Ubicación.',
  'inspector.help.permissions.settings.android':
    'Android: Ajustes → Aplicaciones → el navegador o LuParX → Permisos.',
  /* Escritorio. Hasta el 13-10 por la tarde no había ninguno, y el desplegable se abría vacío
     justo en el entorno desde el que se prueba. En escritorio el permiso NO está en los ajustes
     del sistema: está en el candado de la barra de direcciones, que es otro sitio y otra frase. */
  'inspector.help.permissions.settings.chrome':
    'Chrome: tocá el candado (o el icono de ajustes) a la izquierda de la dirección → Ubicación → Permitir. Después volvé acá y tocá «Comprobar ubicación».',
  'inspector.help.permissions.settings.edge':
    'Edge: tocá el candado a la izquierda de la dirección → Permisos para este sitio → Ubicación → Permitir. Después volvé acá y tocá «Comprobar ubicación».',
  'inspector.help.permissions.settings.firefox':
    'Firefox: tocá el candado a la izquierda de la dirección → quitá el bloqueo de «Acceder a su ubicación». Después volvé acá y tocá «Comprobar ubicación».',
  'inspector.help.permissions.settings.safariMac':
    'Safari en Mac: menú Safari → Ajustes → Sitios web → Ubicación → poné «Permitir» para staging.luparx.com. Si el Mac tiene la ubicación apagada, además: Ajustes del Sistema → Privacidad y seguridad → Localización.',
  'inspector.help.permissions.settings.otro':
    'En este navegador el permiso se cambia desde el icono que hay a la izquierda de la dirección, en los permisos del sitio. Después volvé acá y tocá «Comprobar ubicación».',
  /* Y los tres estados que antes se decían todos igual, «no disponible». */
  'inspector.help.diag.location.insecure': 'No disponible sin conexión segura',
  'inspector.help.diag.location.insecureNote':
    'El navegador sólo entrega la ubicación en páginas seguras (https). Entrá por la dirección con https y volvé a comprobar. No es un problema de permisos ni del aparato.',
  'inspector.help.diag.location.unsupported': 'Este navegador no ofrece ubicación',
  'inspector.help.diag.location.unsupportedNote':
    'Este navegador no permite leer la ubicación desde una página. La boleta se emite igual, sin coordenadas.',
  'inspector.help.permissions.recheck': 'Volver a comprobar',
  'inspector.help.permissions.apiNote':
    'El navegador no siempre deja leer el estado exacto de un permiso ni volver a preguntar desde la página: por eso el cambio se hace en los ajustes y acá sólo se vuelve a leer.',

  'inspector.help.diag.sync.nothingYet': 'Sin pendientes · este aparato todavía no ha enviado nada',
  'inspector.help.sync.sentLabel': 'Enviadas y confirmadas',
  'inspector.help.sync.sent.one': '{{count}} boleta confirmada por el servidor',
  'inspector.help.sync.sent.other': '{{count}} boletas confirmadas por el servidor',
  'inspector.help.sync.noneSent': 'Ninguna todavía.',
  'inspector.help.sync.nothingYetNote':
    'No es lo mismo que «todo sincronizado»: este aparato no ha enviado ninguna boleta.',

  'inspector.help.guides.title': 'Guías de uso',
  'inspector.help.guides.lead':
    'Qué hace la aplicación en cada tarea. El procedimiento de tu municipalidad —cuándo corresponde multar y qué plazos corren— no está acá.',
  'inspector.help.guide.lookup.title': 'Consultar un vehículo',
  'inspector.help.guide.lookup.s1': 'Entrá la placa en la pantalla de inicio y tocá «Buscar».',
  'inspector.help.guide.lookup.s2':
    'Elegí la zona y escribí la bahía. Las dos son opcionales, pero sin la bahía la cobertura no se puede confirmar.',
  'inspector.help.guide.lookup.s3':
    'El resultado dice una de seis cosas: pago vigente, pago vencido, pagó por otra bahía, sin pago vigente, exonerado, o falta la bahía para responder.',
  'inspector.help.guide.lookup.s4':
    '«Falta la bahía» no es un error de la aplicación: con varias estadías de la misma placa, la plataforma se niega a adivinar cuál es el carro que tenés delante.',
  'inspector.help.guide.lookup.s5': 'La última consulta queda a la vista debajo, por si tenés que volver a leerla.',
  'inspector.help.guide.cite.title': 'Emitir una boleta',
  'inspector.help.guide.cite.s1': 'La boleta tiene cuatro pasos: vehículo, infracción, evidencia y revisar.',
  'inspector.help.guide.cite.s2':
    'En «infracción» se elige el tipo. Algunos tipos exigen fotografía y no se pueden emitir sin al menos una; la pantalla lo avisa.',
  'inspector.help.guide.cite.s3':
    'Si hay ubicación, se adjunta sola. Si no la hay, la boleta se emite igual y queda constancia de que no hubo coordenadas.',
  'inspector.help.guide.cite.s4': 'El último paso muestra todo junto antes de emitir. Nada se envía hasta que confirmás ahí.',
  'inspector.help.guide.cite.s5': 'Una vez emitida, el número de boleta lo asigna el servidor, no el teléfono.',
  'inspector.help.guide.photos.title': 'Fotografías y evidencia',
  'inspector.help.guide.photos.s1': 'Las fotos se agregan DENTRO de la boleta, en el paso de evidencia. No hay una galería aparte.',
  'inspector.help.guide.photos.s2': 'Hasta seis fotografías por boleta.',
  'inspector.help.guide.photos.s3': 'Cada una, hasta 10 MB. Si una foto pesa más, el teléfono la rechaza antes de guardarla.',
  'inspector.help.guide.photos.s4':
    'Sin señal las fotos se guardan en el teléfono junto con la boleta y viajan con ella cuando se envía.',
  'inspector.help.guide.offline.title': 'Trabajar sin señal',
  'inspector.help.guide.offline.s1':
    'Podés consultar, levantar boletas y sacar fotos sin conexión. Lo único que necesita señal es la consulta de una placa contra el servidor.',
  'inspector.help.guide.offline.s2': 'Cada boleta levantada sin señal se guarda en este teléfono y aparece en «Pendientes».',
  'inspector.help.guide.offline.s3': 'Cuando vuelve la señal se envían solas. No hace falta que hagas nada.',
  'inspector.help.guide.offline.s4':
    'Cerrar la aplicación no borra la cola. Lo que sí la pone en riesgo es el modo privado del navegador o quedarse sin espacio: el diagnóstico lo comprueba.',
  'inspector.help.guide.sync.title': 'Cómo se envían las boletas',
  'inspector.help.guide.sync.s1': 'Una boleta pasa por cuatro estados: esperando, enviando, enviada o con error.',
  'inspector.help.guide.sync.s2': '«Sincronizar ahora» sólo adelanta el envío de lo que ya iba a salir solo.',
  'inspector.help.guide.sync.s3': '«Reintentar» toca únicamente las que fallaron. Las que ya se enviaron no se vuelven a enviar.',
  'inspector.help.guide.sync.s4':
    'Cada boleta viaja con una clave propia, así que un reintento nunca crea una boleta duplicada en el servidor.',
  'inspector.help.guide.trouble.title': 'Si la cámara o el GPS no responden',
  'inspector.help.guide.trouble.s1':
    'Abrí «Diagnóstico»: dice el estado real de cada permiso y, cuando se puede hacer algo, ofrece el botón.',
  'inspector.help.guide.trouble.s2':
    'Un permiso denegado no se arregla desde la aplicación. Se cambia en los ajustes del aparato; «Permisos del dispositivo» explica dónde.',
  'inspector.help.guide.trouble.s3': 'Después de cambiarlo en los ajustes, volvé y tocá «Volver a comprobar».',
  'inspector.help.guide.trouble.s4':
    'Sin GPS se puede seguir trabajando: la boleta se emite sin coordenadas y queda constancia. Nunca se inventa una ubicación.',

  'inspector.help.about.title': 'Acerca de LuParX',
  'inspector.help.about.version': 'Versión',
  'inspector.help.about.build': 'Compilación',
  'inspector.help.about.environment': 'Entorno',
  'inspector.help.about.builtAt': 'Compilada el',
  'inspector.help.about.unknown': 'No disponible en esta compilación',
  'inspector.help.about.note':
    'Estos datos los escribe el proceso de compilación. Cuando alguno dice «no disponible», es que esta compilación no lo recibió — no se sustituye por un valor inventado.',
  'inspector.help.title': 'Ayuda',
  /* --- Sexta opción: el diagnóstico (06-10-2026) ----------------------------------------------
     Antes era «Resolver un problema» y abría una lista de siete filas que repetía, casi exacta, las
     cinco tarjetas de arriba. Ahora mide: contesta «¿mi aplicación está funcionando?» y sólo ofrece
     un botón donde hay algo que arreglar. Las claves del triaje —`triage.*`, `error.*`, `back.*`—
     se borraron con él: una cadena que ya nadie muestra es una cadena que alguien traduce de más. */
  'inspector.help.diag.title': 'Diagnóstico de la aplicación',
  'inspector.help.diag.checking': 'Revisando el estado…',
  'inspector.help.diag.allGood': 'Todo está funcionando correctamente.',
  'inspector.help.diag.someWarning': 'Funciona, pero hay algo que conviene que sepás.',
  'inspector.help.diag.someIssue': 'Encontramos algo que hay que resolver.',
  'inspector.help.diag.tone.ok': 'correcto',
  'inspector.help.diag.tone.warning': 'atención',
  'inspector.help.diag.tone.problem': 'problema',
  'inspector.help.diag.connection': 'Conexión',
  'inspector.help.diag.connection.note':
    'Seguí trabajando igual: la boleta se guarda en el teléfono y se envía sola cuando vuelva la señal.',
  'inspector.help.diag.sync': 'Sincronización',
  'inspector.help.diag.sync.ok': 'Todo sincronizado',
  'inspector.help.diag.sync.pending.one': '{{count}} operación esperando',
  'inspector.help.diag.sync.pending.other': '{{count}} operaciones esperando',
  'inspector.help.diag.sync.failed.one': '{{count}} operación no se pudo enviar',
  'inspector.help.diag.sync.failed.other': '{{count}} operaciones no se pudieron enviar',
  'inspector.help.diag.sync.note': 'Se envían solas; esto sólo adelanta el envío.',
  'inspector.help.diag.sync.offlineNote': 'Sin señal no se pueden enviar. Salen solas cuando vuelva.',
  'inspector.help.diag.sync.failedNote':
    'En Pendientes podés abrir cada una y ver por qué falló. Reintentar no duplica nada: se reusa la misma clave de envío.',
  'inspector.help.diag.sync.action': 'Sincronizar ahora',
  'inspector.help.diag.sync.retry': 'Reintentar ahora',
  'inspector.help.diag.sync.working': 'Enviando…',
  'inspector.help.diag.session': 'Sesión',
  'inspector.help.diag.session.ok': 'Sesión activa',
  'inspector.help.diag.session.problem': 'Sesión no disponible',
  'inspector.help.diag.session.note': 'Volvé a entrar con tu usuario para seguir trabajando.',
  'inspector.help.diag.session.action': 'Iniciar sesión',
  'inspector.help.diag.session.noTenant': 'Sin municipalidad activa',
  'inspector.help.diag.session.noTenantNote':
    'Tu sesión está abierta pero no hay una municipalidad elegida, así que no se puede consultar ni emitir.',
  'inspector.help.diag.session.chooseTenant': 'Elegir municipalidad',
  'inspector.help.diag.location': 'Ubicación',
  'inspector.help.diag.location.deniedNote':
    'La boleta se emite igual, sin coordenadas. Se activa en los ajustes del teléfono, en los permisos de LuParX.',
  'inspector.help.diag.permission.granted': 'Permiso concedido',
  'inspector.help.diag.permission.asks': 'Se pide al usarla',
  'inspector.help.diag.permission.denied': 'Permiso denegado',
  'inspector.help.diag.permission.working': 'Pidiendo…',
  'inspector.help.diag.location.allow': 'Permitir la ubicación',
  'inspector.help.diag.location.allowNote':
    'Con el permiso dado, la consulta de placa guarda dónde se hizo. Sin él la boleta se emite igual, sin coordenadas.',
  'inspector.help.diag.app': 'Aplicación',
  'inspector.help.diag.app.ok': 'Funcionando correctamente',
  'inspector.help.diag.app.problem': 'Este teléfono no está guardando datos',
  'inspector.help.diag.app.note':
    'Una boleta levantada sin señal podría perderse al cerrar la aplicación. Suele ser el modo privado del navegador o el almacenamiento lleno.',
  'inspector.help.diag.app.reload': 'Recargar la aplicación',
  'citizen.more.profile': 'Mi perfil',
  'citizen.more.fines': 'Multas',
  'citizen.more.fines.meta': 'Consultá tus multas y presentá un reclamo.',
  'citizen.more.language': 'Seleccionar idioma',
  'citizen.more.notifications': 'Notificaciones',
  'citizen.more.notifications.meta': 'Lo que pasó, y qué de eso te llega al correo.',
  // --- Menú «Más» del ciudadano: lo que agregó la especificación del 24-09-2026 -------------------
  'citizen.more.connection': 'Estado de conexión',
  'citizen.more.connection.online': 'Con conexión. Todo se guarda al momento.',
  'citizen.more.connection.offline': 'Sin conexión. Volvé a intentar cuando tengas señal.',
  'citizen.more.connection.ok': 'En línea',
  'citizen.more.connection.none': 'Sin conexión',
  'citizen.more.help': 'Ayuda',
  'citizen.more.help.meta': 'Cómo funciona el estacionamiento y las boletas.',
  'citizen.more.about': 'Acerca de LuParX',
  'citizen.more.about.meta': 'Abre el sitio oficial.',
  // --- Ayuda del ciudadano -------------------------------------------------------------------------
  'citizen.help.title': 'Ayuda',
  // --- Centro de Ayuda del ciudadano (07-10-2026) ------------------------------------------------
  /* Dejó de ser un segundo menú de módulos: ahora pregunta por el PROBLEMA y el módulo aparece al
     final, cuando ya se eligió qué resolver. Las claves viejas `citizen.help.<tema>.*` se borraron
     con la estructura que las usaba. */
  'citizen.help.ask': '¿En qué podemos ayudarte?',
  'citizen.help.search.label': 'Buscar ayuda',
  'citizen.help.search.placeholder': '¿Qué estás buscando?',
  'citizen.help.search.empty': 'No encontramos nada para «{{query}}». Probá con otras palabras o elegí una categoría.',
  'citizen.help.search.go': 'Ver cómo resolverlo',
  'citizen.help.whatDoYouNeed': '¿Qué necesitás?',
  'citizen.help.whatToSolve': '¿Qué necesitás resolver?',
  'citizen.help.whatIsHappening': '¿Qué está pasando?',
  'citizen.help.cat.parking.title': 'Mi estacionamiento',
  'citizen.help.cat.payments.title': 'Pagos y billetera',
  'citizen.help.cat.fines.title': 'Multas y boletas',
  'citizen.help.cat.vehicles.title': 'Vehículos',
  'citizen.help.cat.app.title': 'Problemas con la aplicación',
  'citizen.help.status.title': 'Estado de la aplicación',
  'citizen.help.status.body': 'Revisá la conexión, tu sesión y si este dispositivo está guardando datos.',
  'citizen.help.status.action': 'Ver diagnóstico',
  'citizen.help.opt.parking.moreTime.title': 'Necesito más tiempo',
  'citizen.help.opt.parking.moreTime.body':
    'Podés ampliar desde el inicio mientras la estadía siga activa. Si ya terminó, no se extiende: hay que iniciar una nueva, y entre una y otra el carro está sin cubrir.',
  'citizen.help.opt.parking.moreTime.action': 'Ver estacionamiento activo',
  'citizen.help.opt.parking.cantStart.title': 'No puedo iniciar mi estacionamiento',
  'citizen.help.opt.parking.cantStart.body':
    'Revisá que el vehículo esté registrado, que haya saldo y que la bahía exista en la zona. La pantalla dice cuál de los cinco pasos falta.',
  'citizen.help.opt.parking.cantStart.action': 'Estacionar',
  'citizen.help.opt.parking.ended.title': 'Mi estacionamiento terminó',
  'citizen.help.opt.parking.ended.body':
    'Una estadía vencida no se reabre. Si el carro sigue ahí, iniciá una nueva: mientras no haya una activa, la bahía figura sin pagar.',
  'citizen.help.opt.parking.ended.action': 'Estacionar de nuevo',
  'citizen.help.opt.parking.cantPay.title': 'No puedo pagar',
  'citizen.help.opt.parking.cantPay.body':
    'El estacionamiento se cobra de la billetera. Si no alcanza, recargá primero y volvé a intentarlo.',
  'citizen.help.opt.parking.cantPay.action': 'Ver billetera',
  'citizen.help.opt.parking.location.title': 'Tengo un problema con mi ubicación',
  'citizen.help.opt.parking.location.body':
    'No hace falta el GPS: la zona y la bahía se eligen de una lista, y es el número de la bahía lo que identifica dónde está el carro.',
  'citizen.help.opt.parking.location.action': 'Estacionar',
  'citizen.help.opt.payments.cantPay.title': 'No puedo realizar un pago',
  'citizen.help.opt.payments.cantPay.body':
    'Revisá el saldo de la billetera. Si el cobro fue rechazado, el movimiento queda registrado con el motivo.',
  'citizen.help.opt.payments.cantPay.action': 'Ver billetera',
  'citizen.help.opt.payments.missing.title': 'Mi pago no aparece',
  'citizen.help.opt.payments.missing.body':
    'Todo cobro y toda recarga dejan un movimiento con su fecha y su referencia. Si no está ahí, no se registró.',
  'citizen.help.opt.payments.missing.action': 'Ver movimientos',
  'citizen.help.opt.payments.movements.title': 'Quiero revisar mis movimientos',
  'citizen.help.opt.payments.movements.body':
    'El detalle de cada entrada y cada salida de la billetera, de la más reciente a la más vieja.',
  'citizen.help.opt.payments.movements.action': 'Ver movimientos',
  'citizen.help.opt.payments.unknownCharge.title': 'No reconozco un cobro',
  'citizen.help.opt.payments.unknownCharge.body':
    'Buscá el movimiento por su fecha: dice qué lo originó. Si la placa está compartida, puede haberla usado otra persona.',
  'citizen.help.opt.payments.unknownCharge.action': 'Ver movimientos',
  'citizen.help.opt.payments.walletHelp.title': 'Necesito ayuda con mi billetera',
  'citizen.help.opt.payments.walletHelp.body':
    'Se recarga en los puntos habilitados o con tarjeta. El saldo es el que paga las estadías.',
  'citizen.help.opt.payments.walletHelp.action': 'Ver billetera',
  'citizen.help.opt.fines.got.title': 'Me llegó una boleta',
  'citizen.help.opt.fines.got.body':
    'Aparece con el motivo, la zona y la hora. Es un acto de la municipalidad: no desaparece sola, pero se puede apelar.',
  'citizen.help.opt.fines.got.action': 'Ver multas',
  'citizen.help.opt.fines.appeal.title': 'Quiero apelar una boleta',
  'citizen.help.opt.fines.appeal.body':
    'Se apela desde el detalle de la boleta, contando qué pasó. Mientras tanto la boleta sigue viva, así que ojo con dejar pasar el plazo.',
  'citizen.help.opt.fines.appeal.action': 'Elegir la boleta',
  'citizen.help.opt.fines.dontUnderstand.title': 'No entiendo una boleta',
  'citizen.help.opt.fines.dontUnderstand.body':
    'El detalle trae la infracción, dónde y cuándo fue, y la evidencia que se adjuntó al emitirla.',
  'citizen.help.opt.fines.dontUnderstand.action': 'Ver el detalle',
  'citizen.help.opt.fines.mine.title': 'Consultar mis boletas',
  'citizen.help.opt.fines.mine.body': 'Todas las boletas de tus placas, pagadas y pendientes.',
  'citizen.help.opt.fines.mine.action': 'Ver multas',
  'citizen.help.opt.vehicles.add.title': 'Agregar un vehículo',
  'citizen.help.opt.vehicles.add.body':
    'Sólo se puede estacionar con una placa registrada. Se agrega una vez y queda disponible.',
  'citizen.help.opt.vehicles.add.action': 'Ver vehículos',
  'citizen.help.opt.vehicles.edit.title': 'Modificar un vehículo',
  'citizen.help.opt.vehicles.edit.body':
    'Se puede corregir el nombre con que lo reconocés. La placa no se edita: se agrega la correcta y se quita la otra.',
  'citizen.help.opt.vehicles.edit.action': 'Ver vehículos',
  'citizen.help.opt.vehicles.list.title': 'Consultar mis vehículos',
  'citizen.help.opt.vehicles.list.body': 'Las placas registradas a tu nombre en esta municipalidad.',
  'citizen.help.opt.vehicles.list.action': 'Ver vehículos',
  'citizen.help.opt.vehicles.shared.title': 'Placas compartidas',
  'citizen.help.opt.vehicles.shared.body':
    'Una misma placa puede estar registrada por más de una persona. Si eso pasa, la boleta se ve, se paga y se apela desde las dos cuentas, y quien la pague primero la paga para todos.',
  'citizen.help.opt.vehicles.shared.action': 'Ver vehículos',
  'citizen.help.opt.app.offline.title': 'No tengo conexión',
  'citizen.help.opt.app.offline.body':
    'Sin conexión no se puede iniciar ni pagar una estadía: todo se confirma contra el servidor en el momento.',
  'citizen.help.opt.app.offline.action': 'Ver diagnóstico',
  'citizen.help.opt.app.location.title': 'Tengo un problema con mi ubicación',
  'citizen.help.opt.app.location.body':
    'La aplicación no necesita tu ubicación para nada: la zona y la bahía se eligen a mano.',
  'citizen.help.opt.app.location.action': 'Ver diagnóstico',
  'citizen.help.opt.app.notifications.title': 'No recibo notificaciones',
  'citizen.help.opt.app.notifications.body':
    'Los avisos llegan a esa pantalla siempre, y al correo según lo que tengás elegido en sus preferencias.',
  'citizen.help.opt.app.notifications.action': 'Ver avisos',
  'citizen.help.opt.app.error.title': 'La aplicación presenta un error',
  'citizen.help.opt.app.error.body':
    'Recargar la vuelve a montar desde cero y resuelve casi todos los errores de pantalla. No borra nada tuyo.',
  'citizen.help.opt.app.error.action': 'Recargar',
  'citizen.help.opt.app.diagnostic.title': 'Diagnóstico de la aplicación',
  'citizen.help.opt.app.diagnostic.body':
    'Revisa de una vez la conexión, tu sesión y si este dispositivo está guardando datos.',
  'citizen.help.opt.app.diagnostic.action': 'Ver diagnóstico',
  'citizen.help.diag.title': 'Diagnóstico de la aplicación',
  'citizen.help.diag.allGood': 'Todo está funcionando correctamente.',
  'citizen.help.diag.someWarning': 'Funciona, pero hay algo que conviene que sepás.',
  'citizen.help.diag.someIssue': 'Encontramos algo que hay que resolver.',
  'citizen.help.permissions.title': 'Permisos del dispositivo',
  'citizen.help.permissions.body': 'Revisá si la cámara está habilitada para tomar fotografías de evidencia.',
  'citizen.help.permissions.action': 'Abrir permisos',
  'citizen.help.permissions.lead': 'La cámara se usa para adjuntar fotografías cuando impugnás una multa. El navegador la pide en el momento de usarla.',
  'citizen.help.permissions.ready': 'La cámara respondió: está lista para usarse.',
  'citizen.help.permissions.pending': 'La cámara todavía no se comprobó en este aparato.',
  'citizen.help.permissions.none': 'Esta aplicación no pide ningún otro permiso del aparato. La ubicación no se usa: la zona se elige de una lista.',

  'citizen.help.diag.tone.ok': 'correcto',
  'citizen.help.diag.tone.warning': 'atención',
  'citizen.help.diag.tone.unavailable': 'no disponible',
  'citizen.help.diag.tone.problem': 'problema',
  'citizen.help.diag.connection': 'Conexión',
  'citizen.help.diag.connection.ok': 'En línea',
  'citizen.help.diag.connection.off': 'Sin conexión',
  'citizen.help.diag.connection.note':
    'Sin conexión no se puede iniciar ni pagar una estadía. Revisá los datos o el wifi y volvé a intentarlo.',
  'citizen.help.diag.session': 'Sesión',
  'citizen.help.diag.session.ok': 'Sesión activa',
  'citizen.help.diag.session.problem': 'Sesión no disponible',
  'citizen.help.diag.session.note': 'Volvé a entrar con tu cuenta para seguir usando la aplicación.',
  'citizen.help.diag.app': 'Aplicación',
  'citizen.help.diag.app.ok': 'Funcionando correctamente',
  'citizen.help.diag.app.problem': 'Este dispositivo no está guardando datos',
  'citizen.help.diag.app.note':
    'La aplicación va a pedirte que entres cada vez. Suele ser el modo privado del navegador o el almacenamiento lleno.',
  'citizen.help.diag.app.reload': 'Recargar la aplicación',
  /* Cada tema lleva a la pantalla que de verdad lo resuelve (07-10-2026). Ninguna ruta es nueva:
     son las cinco pestañas y Multas, que ya existían. Lo que se agregó es el enlace. */

  // ---- v0.38 — la campana ---------------------------------------------------------------------
  // El servidor manda un tipo estable y parámetros crudos; la oración se arma acá. Una frase ya
  // escrita en el servidor quedaría congelada en el idioma en que se escribió, y quien cambie la app
  // a inglés leería su historial en español para siempre.
  'citizen.reminder.expiring.title': 'Le quedan {{minutes}} min de parqueo',
  'citizen.reminder.expiring.body': '{{plate}} en el espacio {{space}}. Extienda desde la app si lo necesita.',
  'citizen.reminder.expired.title': 'Se venció su parqueo',
  'citizen.reminder.expired.body': '{{plate}} en el espacio {{space}}.',
  'citizen.reminder.permission.title': '¿Le avisamos antes de que se venza?',
  'citizen.reminder.permission.body': 'El teléfono le avisa aunque se quede sin señal. Puede desactivarlo cuando quiera.',
  'citizen.reminder.permission.allow': 'Avisarme',
  'citizen.reminder.permission.dismiss': 'Ahora no',
  'citizen.notifications.title': 'Notificaciones',
  'citizen.notifications.subtitle': 'Lo que pasó en esta municipalidad.',
  'citizen.notifications.markAllRead': 'Marcar todas como leídas',
  'citizen.notifications.unread': 'Sin leer',
  'citizen.notifications.empty.title': '¡Al día!',
  'citizen.notifications.empty.description': 'No hay notificaciones en esta municipalidad.',
  'citizen.notifications.preferences.open': 'Preferencias de aviso',
  'citizen.notifications.preferences.title': 'Avisos por correo',
  'citizen.notifications.preferences.description': 'La campana muestra todo. Elegí qué además te llega al correo.',
  'citizen.notifications.preferences.emailEnabled': 'Avisarme también por correo',
  'citizen.notifications.preferences.emailEnabledHint': 'Se envía a la dirección con la que ingresás. Podés apagarlo cuando querás.',
  'citizen.notifications.preferences.categories': '¿De qué querés que te avisemos?',
  'citizen.notifications.preferences.error': 'No se pudo guardar la preferencia.',

  'notification.category.PARKING': 'Estacionamiento',
  'notification.category.FINES': 'Multas',
  'notification.category.WALLET': 'Billetera y minutos',

  'notification.type.PARKING_SESSION_EXPIRING': 'Tu estacionamiento de la placa {{plate}} vence a las {{when}}.',
  'notification.type.PARKING_SESSION_EXPIRED': 'El estacionamiento de la placa {{plate}} venció a las {{when}}.',
  'notification.type.CITATION_ISSUED': 'Te levantaron la boleta {{citationNumber}} por {{amount}}.',
  'notification.type.APPEAL_RESOLVED': 'La municipalidad resolvió tu reclamo de la boleta {{citationNumber}}.',
  'notification.type.WALLET_TOPUP_CREDITED': 'Se acreditaron {{amount}} a tu billetera.',
  'notification.type.TIME_CREDITS_EXPIRING': 'Tus {{minutes}} minutos a favor vencen el {{when}}.',
  // Los avisos de quien trabaja para la municipalidad (06-10-2026). Segunda persona, igual que los
  // del ciudadano: quien los lee es a quien le pasó.
  'notification.type.CITATION_VOIDED': 'Anularon la boleta {{citationNumber}} de la placa {{plate}}.',
  'notification.type.CITATION_VOIDED.reason': 'Motivo: {{reason}}',
  'notification.type.POST_SUSPENDED': 'Tu acceso a esta municipalidad quedó desactivado.',
  'notification.type.POST_SUSPENDED.reason': 'Motivo: {{reason}}',
  'notification.type.POST_REACTIVATED': 'Te restablecieron el acceso a esta municipalidad.',
  'notification.type.POST_ROLE_CHANGED': 'Tu rol cambió de {{previousRole}} a {{role}}.',
  'notification.type.POST_ZONES_CHANGED.one': 'Te reasignaron los sectores: ahora cubrís 1.',
  'notification.type.POST_ZONES_CHANGED.other': 'Te reasignaron los sectores: ahora cubrís {{count}}.',
  'notification.type.POST_ZONES_CHANGED.all': 'Te reasignaron los sectores: ahora podés trabajar en todos.',
  'inspector.notifications.title': 'Avisos',
  'inspector.notifications.subtitle': 'Lo que pasó con tu trabajo en esta municipalidad.',
  'inspector.notifications.open': 'Avisos',
  'inspector.notifications.markAllRead': 'Marcar todos como leídos',
  'inspector.notifications.unread': 'Sin leer',
  'inspector.notifications.empty.title': 'Nada nuevo',
  'inspector.notifications.empty.description': 'Acá vas a ver si te anulan una boleta o si cambia tu puesto.',
  'inspector.notifications.queue.title': 'Boletas esperando subir',
  'inspector.notifications.queue.one': '1 boleta todavía no salió del teléfono.',
  'inspector.notifications.queue.other': '{{count}} boletas todavía no salieron del teléfono.',
  'inspector.notifications.queue.action': 'Ver pendientes',

  'nav.fines': 'Multas',
  'nav.catalogs': 'Catálogos',
  'nav.system': 'Sistema',

  'profile.title': 'Mi perfil',
  'profile.changeTenant': 'Cambiar municipalidad activa',
  'profile.verifiedBadge': 'Verificado',
  'profile.activeTenant': 'Municipalidad activa',

  'common.notifications': 'Notificaciones',
  'common.viewAll': 'Ver todo',
  'common.today': 'Hoy',

  // ---- Citizen app screens (mocked domain — module-parking not shipped yet) --------------------

  'locale.name.es-CR': 'Español',
  'locale.name.en-US': 'Inglés',

  'citizen.home.greeting': 'Hola, {{name}} 👋',
  'citizen.home.subtitle.noSession': 'Estaciona fácil y rápido.',
  'citizen.home.subtitle.activeSession': 'Tu estacionamiento está activo.',
  'citizen.home.subtitle.expiredSession': 'Tu estacionamiento venció.',
  'citizen.home.cta.title': 'Estacionar ahora',
  'citizen.home.cta.subtitle': 'Selecciona zona, vehículo y tiempo',
  'citizen.home.balanceCard.action': 'Recargar',
  'citizen.home.vehicleCard.label': 'Vehículo principal',
  'citizen.home.vehicleCard.changeCta': 'Cambiar',
  'citizen.home.vehicleCard.empty': 'Sin vehículos',
  'citizen.home.vehicleCard.addCta': 'Agregar',
  'citizen.home.balanceCard.empty': 'Sin saldo',
  'citizen.home.finesCard.label': 'Multas pendientes',
  'citizen.home.finesCard.none': 'Ninguna',
  'citizen.home.finesCard.one': '1 multa por pagar',
  'citizen.home.finesCard.many': '{{count}} multas por pagar',
  'citizen.home.finesCard.amount': 'Total: {{amount}}',
  'citizen.home.finesCard.loading': 'Consultando…',
  'citizen.home.activity.title': 'Actividad reciente',
  'citizen.home.activity.viewAllCta': 'Ver todas',
  'citizen.home.activeSession.title': 'Estacionamiento activo',
  'citizen.home.activeSession.titleExpired': 'Estacionamiento vencido',
  // La tolerancia se dice con la misma regla que el portal administrativo le explica a quien la
  // configura: «todavía no se puede levantar una boleta». Los minutos los manda el servidor.
  'citizen.home.activeSession.expired.withinGrace':
    'Venció. Esta municipalidad da {{minutes}} de tolerancia; después se puede levantar una boleta.',
  'citizen.home.activeSession.expired.graceOver': 'Venció y se puede levantar una boleta.',
  'citizen.home.activeSession.statusActive': 'Activo',
  'citizen.home.activeSession.statusExpired': 'Expirado',
  'citizen.home.activeSession.urgency.warning': 'Quedan menos de 10 minutos.',
  'citizen.home.activeSession.urgency.critical': 'Quedan menos de 5 minutos: extendé o volvé al carro.',
  'citizen.timer.expired': 'Expirado',
  'citizen.home.activeSession.zoneAndSpace': 'Zona: {{zone}} · Espacio {{space}}',
  'citizen.home.activeSession.remainingLabel': 'tiempo restante',
  'citizen.home.activeSession.expiresAt': 'Vence hoy a las {{time}}',
  'citizen.home.activeSession.expiredAt': 'Venció hoy a las {{time}}',
  'citizen.home.activeSession.extendCta': 'Extender tiempo',
  'citizen.home.activeSession.finishCta': 'Finalizar ahora',

  'citizen.timerBar.moreCount': '+{{count}}',
  'citizen.timerBar.moreLabel': 'Ver todas las sesiones activas',
  'citizen.timerBar.listTitle': 'Sesiones activas',
  'citizen.timerBar.announce.warning': '{{plate}}: quedan menos de 10 minutos.',
  'citizen.timerBar.announce.expired': '{{plate}}: el estacionamiento venció.',

  'citizen.parking.title': 'Estacionar',
  'citizen.parking.subtitle': 'Sigue estos pasos para iniciar tu sesión.',
  'citizen.parking.step0.title': 'Municipalidad',
  'citizen.parking.step1.title': 'Zona y espacio',
  'citizen.parking.step1.description': 'Selecciona la zona donde estás estacionado.',
  'citizen.parking.step1.zoneLabel': 'Zona',
  'citizen.parking.step1.viewMapCta': 'Ver mapa',
  'citizen.parking.step2.title': 'Vehículo',
  'citizen.parking.step2.selectLabel': 'Selecciona un vehículo',
  'citizen.parking.step2.empty': 'Agrega un vehículo antes de estacionar.',
  'citizen.parking.step2.guestOption': 'Otro vehículo',
  'citizen.parking.step2.guestOptionDetail': 'No es tuyo · la placa no se guarda',
  'citizen.parking.step2.guestPlateLabel': 'Placa del vehículo',
  'citizen.parking.step2.guestPlateHint':
    'Se usa solo para este estacionamiento y queda en el comprobante. No se agrega a tus vehículos.',
  'citizen.parking.step2.guestPlatePlaceholder': 'BHL019',
  'citizen.parking.step2.guestTypeLabel': 'Tipo de vehículo',
  'citizen.parking.step2.addVehicleCta': 'Agregar vehículo',
  'citizen.parking.step3.title': 'Tiempo',
  'citizen.parking.step3.creditApplied': 'incluye {{minutes}} a favor',
  'citizen.parking.step3.benefitHeading': 'Beneficio incluido',
  'citizen.parking.step3.freeBadge': 'GRATIS',
  'citizen.parking.step3.noChargeBadge': 'Sin cobro',
  'citizen.parking.step3.freeMinutesBenefit': '{{minutes}} gratis',
  'citizen.parking.step3.savedMinutes': 'Tus minutos guardados',
  'citizen.parking.step3.courtesy': 'Cortesía, una vez al día',
  'citizen.parking.step4.title': 'Resumen y pago',
  'citizen.parking.step4.zoneLabel': 'Zona',
  'citizen.parking.step4.vehicleLabel': 'Vehículo',
  'citizen.parking.step4.durationLabel': 'Duración',
  'citizen.parking.step4.amountLabel': 'Monto',
  'citizen.parking.step4.creditAppliedLabel': 'Minutos a favor aplicados',
  'citizen.parking.step4.balanceLabel': 'Saldo disponible',
  'citizen.parking.step4.balanceAfterLabel': 'Saldo después de pagar',
  'citizen.parking.falta.zona': 'Elegí una zona para continuar.',
  'citizen.parking.falta.espacio': 'Escribí el código del espacio.',
  'citizen.parking.falta.vehiculo': 'Elegí un vehículo.',
  'citizen.parking.falta.duracion': 'Elegí cuánto tiempo vas a estacionar.',
  'citizen.parking.step4.shortBy': 'Te faltan {{amount}} para esta estadía.',
  'citizen.parking.step4.topUpCta': 'Recargar y volver acá',
  'citizen.parking.step4.payableLabel': 'A pagar de tu billetera',
  'citizen.parking.submit': 'Iniciar estacionamiento',
  'citizen.parking.wizard.step1': 'Zona',
  'citizen.parking.wizard.step2': 'Vehículo',
  'citizen.parking.wizard.step3': 'Confirmar',
  'citizen.parking.wizard.step4': 'Listo',
  'citizen.parking.wizard.progress': 'Paso {{current}} de {{total}}',
  'citizen.parking.wizard.continue': 'Continuar',
  'citizen.parking.step4.confirmAndPay': 'Confirmar y pagar {{amount}}',
  'citizen.parking.result.title': '¡Listo!',
  'citizen.parking.result.subtitle': 'Tu estacionamiento está activo.',
  'citizen.parking.result.validUntil': 'Válido hasta',
  'citizen.parking.result.seeMine': 'Ver mi estacionamiento',
  'citizen.parking.result.extendCta': 'Ampliar tiempo',
  'citizen.parking.result.seeOnMap': 'Ver en el mapa',
  'citizen.parking.result.hideMap': 'Ocultar el mapa',
  'citizen.parking.durationMinutes.one': '{{count}} minuto',
  'citizen.parking.durationMinutes.other': '{{count}} minutos',
  'citizen.parking.durationHours.one': '{{count}} hora',
  'citizen.parking.durationHours.other': '{{count}} horas',

  'citizen.parking.extend.title': 'Ampliar estacionamiento',
  'citizen.parking.extend.currentlyEndsAt': 'Ahora termina a las {{time}}.',
  'citizen.parking.extend.chooseLabel': 'Tiempo adicional',
  'citizen.parking.extend.chargeableMinutes': 'Se cobran {{minutes}}: el resto cae fuera del horario de cobro.',
  'citizen.parking.extend.creditApplied': 'Incluye {{minutes}} a favor.',
  'citizen.parking.extend.costLabel': 'Precio de la extensión',
  'citizen.parking.extend.newExpiryLabel': 'Nueva hora de vencimiento',
  'citizen.parking.extend.walletNote': 'El monto adicional se cobrará de tu billetera según la tarifa de esta municipalidad.',
  'citizen.parking.extend.noOptions': 'Esta municipalidad no ofrece ampliaciones para este estacionamiento.',
  'citizen.parking.extend.submit': 'Ampliar',

  'citizen.parking.finish.confirmTitle': '¿Terminar el estacionamiento?',
  'citizen.parking.finish.immediate': 'La sesión terminará de inmediato y el espacio {{space}} quedará libre.',
  'citizen.parking.finish.confirmDescription.credited':
    'Te quedan {{minutes}}. Se guardarán como minutos a favor en esta municipalidad y vencen el {{date}}. Se consumen primero en tu próxima sesión aquí; no son dinero y no se pueden usar en otra municipalidad.',
  'citizen.parking.finish.confirmDescription.lost':
    'Te quedan {{minutes}}. Esta municipalidad no acredita el tiempo restante: esos minutos se pierden y no se devuelven a tu billetera.',
  'citizen.parking.finish.confirmSubmit': 'Sí, finalizar',

  // Título del diálogo que interrumpe cuando falla el envío (ErrorDialog): nombra la acción que
  // no se pudo completar, para que el motivo de abajo se lea como respuesta al botón que se apretó.
  'citizen.parking.error.title': 'No se pudo iniciar el estacionamiento',
  'citizen.parking.error.SESSION_ALREADY_ACTIVE_FOR_VEHICLE': 'Este vehículo ya tiene una sesión de estacionamiento activa.',
  'citizen.parking.error.SESSION_ALREADY_ACTIVE_FOR_PLATE':
    'Esta placa ya tiene un estacionamiento activo en esta municipalidad.',
  'citizen.parking.error.SPACE_OCCUPIED': 'Este espacio ya está ocupado.',
  'citizen.parking.error.INVALID_INCREMENT': 'La duración seleccionada no es válida.',
  'citizen.parking.error.INSUFFICIENT_BALANCE': 'Saldo insuficiente. Recarga tu billetera para continuar.',
  'citizen.parking.error.EXTENSION_DISABLED': 'Esta municipalidad no permite extender el tiempo.',
  'citizen.parking.error.EXTENSION_EXCEEDS_MAX': 'La extensión supera el máximo permitido para esta sesión.',
  'citizen.parking.error.EARLY_FINISH_DISABLED': 'Esta municipalidad no permite finalizar antes de tiempo.',
  'citizen.parking.error.VEHICLE_NOT_FOUND': 'No se encontró el vehículo seleccionado.',
  'citizen.parking.error.SESSION_NOT_FOUND': 'No se encontró la sesión de estacionamiento.',
  'citizen.parking.error.PARKING_SESSION_NOT_ACTIVE': 'Esta sesión ya no está activa.',
  'citizen.parking.error.PARKING_SPACE_NOT_FOUND': 'No existe un espacio con ese código en la zona elegida.',
  'citizen.parking.error.PARKING_SPACE_OUT_OF_SERVICE': 'Ese espacio está fuera de servicio.',
  'citizen.parking.error.PARKING_SPACE_CODE_INVALID': 'Ese código no tiene el formato que usa esta municipalidad.',
  'citizen.parking.error.OUTSIDE_CHARGING_HOURS': 'A esta hora no se cobra parqueo en esta municipalidad.',

  'citizen.vehicles.title': 'Mis vehículos',
  'citizen.vehicles.addCta': 'Agregar',
  'citizen.vehicles.primaryBadge': 'Principal',
  'citizen.vehicles.editCta': 'Editar',
  'citizen.vehicles.menuCta': 'Más opciones',
  'citizen.vehicles.menuForPlate': 'Opciones de {{plate}}',
  'citizen.vehicles.imageIsGeneric':
    'La imagen y la categoría se estiman por marca y modelo; podés corregirlas al editar el vehículo.',
  'citizen.vehicles.form.typeAutoChosen': 'Automóvil (la elige la aplicación)',
  'citizen.vehicles.imageAlt': 'Ilustración genérica de un vehículo',
  'citizen.vehicles.empty.title': '¡Todavía no tienes vehículos!',
  'citizen.vehicles.empty.description': 'Agrega un vehículo para poder estacionar en esta municipalidad.',
  'citizen.vehicles.activeSessionBadge': 'En uso',
  'citizen.vehicles.actions.setPrimary': 'Marcar como principal',
  'citizen.vehicles.actions.delete': 'Eliminar',

  'citizen.vehicles.form.title.add': 'Agregar vehículo',
  'citizen.vehicles.form.title.edit': 'Editar vehículo',
  'citizen.vehicles.form.plateLabel': 'Placa',
  'citizen.vehicles.form.plateHint': 'Se guarda en mayúsculas, sin espacios ni guiones.',
  'citizen.vehicles.form.nameLabel': 'Nombre del vehículo',
  'citizen.vehicles.form.typeLabel': 'Tipo de vehículo',
  /*
    La pista dice lo que la carrocería hace y lo que NO hace. Lo segundo importa más: lo que se
    cobra y lo que fiscaliza un inspector es «automóvil» o «motocicleta», y elegir pick-up no
    cambia ninguna de las dos cosas. Sin esta línea, alguien podría creer que registrar su carro
    como pick-up le cambia la tarifa.
  */
  'citizen.vehicles.form.typeHint': 'La carrocería solo cambia la imagen que ves en esta aplicación; la tarifa se cobra por automóvil o motocicleta.',
  'citizen.vehicles.body.sedan': 'Sedán',
  'citizen.vehicles.body.hatchback': 'Hatchback',
  'citizen.vehicles.body.suv': 'SUV',
  'citizen.vehicles.body.pickup': 'Pick-up',
  'citizen.vehicles.body.minivan': 'Miniván',
  'citizen.vehicles.body.van': 'Furgoneta',
  'citizen.vehicles.form.colorLabel': 'Color',
  'citizen.vehicles.form.colorUnset': 'Sin especificar',
  'citizen.vehicles.form.brandLabel': 'Marca',
  'citizen.vehicles.form.modelLabel': 'Modelo',
  'citizen.vehicles.form.yearLabel': 'Año',
  'citizen.vehicles.form.isOwnerLabel': 'Soy el propietario',
  'citizen.vehicles.form.submit.add': 'Agregar vehículo',
  'citizen.vehicles.form.submit.edit': 'Guardar cambios',
  'citizen.vehicles.form.error.PLATE_ALREADY_REGISTERED': 'Ya tienes un vehículo registrado con esta placa.',

  'citizen.vehicles.delete.confirmTitle': 'Eliminar vehículo',
  'citizen.vehicles.delete.confirmDescription': '¿Seguro que quieres eliminar el vehículo {{plate}}? Esta acción no se puede deshacer.',
  'citizen.vehicles.delete.confirmSubmit': 'Sí, eliminar',
  'citizen.vehicles.delete.error.VEHICLE_HAS_ACTIVE_SESSION':
    'No puedes eliminar este vehículo: tiene una sesión de estacionamiento activa. Finalízala primero.',

  'citizen.fines.title': 'Multas',
  'citizen.fines.tab.pending': 'Pendientes',
  'citizen.fines.pendingWarning.one': 'Tenés {{count}} multa pendiente. Pagala para evitar recargos.',
  'citizen.fines.pendingWarning.other': 'Tenés {{count}} multas pendientes. Pagalas para evitar recargos.',
  'citizen.fines.help.title': '¿Tenés una duda?',
  'citizen.fines.help.meta': 'Revisá cómo funcionan las multas',
  'citizen.fines.tab.history': 'Historial',
  'citizen.fines.empty.title': '¡Todo en orden!',
  'citizen.fines.empty.description': 'No tienes multas pendientes en esta municipalidad.',
  'citizen.fines.dueLabel': 'Vence el',

  'citizen.wallet.title': 'Billetera',
  'citizen.wallet.balanceLabel': 'Saldo disponible',
  'citizen.wallet.topUpCta': 'Recargar saldo',
  'citizen.wallet.paymentMethods.title': 'Métodos de pago',
  'citizen.wallet.paymentMethods.viewAllCta': 'Ver todos',
  'citizen.wallet.paymentMethods.addCta': 'Agregar tarjeta',
  'citizen.wallet.paymentMethods.expiresLabel': 'Vence {{date}}',
  'citizen.wallet.paymentMethods.primaryBadge': 'Principal',
  'citizen.wallet.movements.viewAllCta': 'Ver todos',
  'citizen.wallet.timeCredits.title': 'Minutos a favor',
  'citizen.wallet.timeCredits.value.one': '{{count}} minuto',
  'citizen.wallet.timeCredits.value.other': '{{count}} minutos',
  'citizen.wallet.timeCredits.expiresLabel': 'Vencen el {{date}}',
  'citizen.wallet.timeCredits.noExpiry': 'No vencen',
  'citizen.wallet.timeCredits.empty': 'No tienes minutos a favor en esta municipalidad.',

  'citizen.movements.balanceAfter': 'Saldo posterior: {{amount}}',
  'citizen.movements.testMoney': 'MODO PRUEBA · saldo simulado',
  'citizen.wallet.testMoney.badge': 'MODO PRUEBA',
  'citizen.wallet.testMoney.notice': 'Parte de este saldo lo creó el simulador de pagos: no lo pagó nadie y sólo sirve en desarrollo.',
  'citizen.topup.title': 'Recargar saldo',
  'citizen.topup.amountLegend': '¿Cuánto querés recargar?',
  'citizen.topup.continue': 'Continuar al pago',
  'citizen.topup.cancel': 'Cancelar',
  'citizen.topup.close': 'Cerrar',
  'citizen.topup.simulatedNotice': 'Pago simulado: no se cobra nada y no se piden datos de tarjeta.',
  'citizen.topup.unavailable': 'Esta municipalidad todavía no acepta pagos con tarjeta.',
  'citizen.topup.starting': 'Abriendo el pago…',
  'citizen.topup.error': 'No se pudo abrir el pago. Intentá de nuevo.',
  'citizen.topup.range': 'Entre {{min}} y {{max}}.',
  'citizen.topup.result.credited': 'Saldo acreditado',
  'citizen.topup.result.creditedDetail': 'Se acreditaron {{amount}} a tu saldo.',
  'citizen.topup.result.pending': 'El pago todavía está en curso',
  'citizen.topup.result.pendingDetail': 'Todavía no se acreditó nada. Revisá más tarde.',
  'citizen.topup.result.failed': 'El pago no se completó',
  'citizen.topup.result.cancelled': 'Cancelaste el pago',
  'citizen.topup.result.noCharge': 'No se cobró nada y tu saldo no cambió.',
  'citizen.topup.result.card': 'Tarjeta terminada en {{last4}}.',
  'citizen.simulatedPay.title': 'Página de pago simulada',
  'citizen.simulatedPay.notice': 'Esto NO es un proveedor de pagos. Es una pantalla de prueba: elegí qué resultado querés que devuelva. No se piden ni se guardan datos de tarjeta.',
  'citizen.simulatedPay.amount': 'Monto: {{amount}}',
  'citizen.simulatedPay.scenarioLegend': '¿Qué resultado querés probar?',
  'citizen.simulatedPay.pay': 'Pagar',
  'citizen.simulatedPay.cancel': 'Cancelar y volver',
  'citizen.simulatedPay.challengeTitle': 'Autenticación del banco emisor',
  'citizen.simulatedPay.challengeDetail': 'El banco pide una confirmación adicional antes de aprobar.',
  'citizen.simulatedPay.challengeApprove': 'Completar la autenticación',
  'citizen.simulatedPay.challengeReject': 'No completarla',
  'citizen.simulatedPay.scenario.VISA_APPROVED': 'Visa · aprobada',
  'citizen.simulatedPay.scenario.MASTERCARD_APPROVED': 'Mastercard · aprobada',
  'citizen.simulatedPay.scenario.DECLINED': 'Rechazada por el emisor',
  'citizen.simulatedPay.scenario.INSUFFICIENT_FUNDS': 'Fondos insuficientes',
  'citizen.simulatedPay.scenario.THREE_DS_CHALLENGE': 'Pide autenticación del banco',
  'citizen.simulatedPay.scenario.EXPIRED_CARD': 'Tarjeta vencida',
  'payment.failure.DECLINED': 'El emisor rechazó la transacción.',
  'payment.failure.INSUFFICIENT_FUNDS': 'Fondos insuficientes en la tarjeta.',
  'payment.failure.EXPIRED_CARD': 'La tarjeta está vencida.',
  'payment.failure.AUTHENTICATION_FAILED': 'No se completó la autenticación del banco emisor.',
  'citizen.movements.title': 'Movimientos',
  'citizen.movements.filter.all': 'Todos',
  'citizen.movements.filter.parking': 'Parqueo',
  'citizen.movements.filter.topups': 'Recargas',
  'citizen.movements.filter.fines': 'Multas',
  'citizen.movements.empty.title': 'Sin movimientos',
  'citizen.movements.empty.description': 'Aún no hay movimientos con este filtro en esta municipalidad.',
  'citizen.movements.item.parking': 'Pago de estacionamiento',
  'citizen.movements.item.parkingExtension': 'Extensión de estacionamiento',
  'citizen.movements.item.topup': 'Recarga de saldo',
  'citizen.movements.item.fine': 'Multa',
  'citizen.movements.item.adjustment': 'Ajuste',

  'citizen.profile.title': 'Mi cuenta',
  'citizen.profile.personalData.label': 'Datos personales',
  'citizen.profile.personalData.description':
    'Tu nombre, tu identificación, tu teléfono y tu dirección. Se guardan todos juntos.',
  'citizen.profile.phoneLabel': 'Teléfono',
  'citizen.profile.identification.label': 'Identificación',
  'citizen.profile.identification.value': 'Cédula o DIMEX',
  'citizen.profile.address.label': 'Dirección',
  'citizen.profile.security.label': 'Seguridad',
  'citizen.profile.security.value': 'Cambiar contraseña',
  'citizen.profile.language.label': 'Idioma',
  'citizen.profile.editCta': 'Editar',
  'citizen.profile.address.empty': 'Sin dirección registrada.',
  'citizen.profile.phone.empty': 'Sin teléfono registrado.',
  'citizen.profile.identification.empty': 'Sin documento registrado.',
  'citizen.profile.loadError': 'No se pudo cargar tu cuenta.',

  'inspector.home.offline': 'Sin conexión — datos guardados localmente',
  'inspector.home.online': 'En línea',
  'inspector.home.quick.lookup': 'Consultar placa',
  'inspector.home.quick.cite': 'Levantar boleta',
  'inspector.home.quick.evidence': 'Evidencia',
  'inspector.home.quick.queue': 'Pendientes',
  'inspector.home.quick.label': 'Acciones del turno',
  'inspector.home.quick.title': 'Acciones rápidas',
  // El título y el subtítulo de la pantalla (12-10-2026, orden visual v2 para iPad). La
  // pantalla deja de encabezarse con un saludo y pasa a decir qué se hace en ella.
  'inspector.home.title': 'Consultar vehículo',
  'inspector.home.subtitle': 'Verifica el estado de estacionamiento y los permisos.',
  // El resumen del turno (12-10-2026). Tres rótulos y no cuatro: «Evidencias tomadas» no tiene
  // fuente en el servidor y la tarjeta no se dibuja.
  'inspector.home.shift.checks': 'Consultas hoy',
  'inspector.home.shift.citations': 'Boletas emitidas',
  'inspector.home.shift.pending': 'Pendientes de enviar',
  'inspector.home.location.title': 'Ubicación de la consulta',
  'inspector.home.clock.label': 'Fecha y hora del aparato',

  /* --- Pantalla inicial de Fiscalización (07-10-2026, especificación visual responsive) ------ */
  'inspector.home.greeting': 'Hola, Inspector',
  'inspector.home.greeting.named': 'Hola, {{name}}',
  'inspector.home.where': 'Fiscalización · {{tenant}}',
  'inspector.home.where.noTenant': 'Fiscalización',
  'inspector.home.quick.lookup.hint': 'Verificar estado y permisos',
  // Las dos ayudas con las palabras exactas de la referencia v2.
  'inspector.home.quick.cite.hint': 'Infracción o aviso',
  'inspector.home.quick.queue.hint': 'Por enviar',
  'inspector.home.quick.evidence.hint': 'Fotos y adjuntos',
  'inspector.home.fast.go': 'Buscar',
  'inspector.home.last.title': 'Última consulta',
  // El estado vacío, con la frase de la referencia v2: dice qué va a pasar, no sólo que no
  // ha pasado nada.
  'inspector.home.last.empty': 'Los resultados aparecerán después de consultar una placa.',
  'inspector.home.last.again': 'Volver a consultar {{plate}}',
  'inspector.home.last.checkedAt': 'Consultada a las {{time}}',
  // Cuatro claves que la pantalla de inicio del inspector ya usaba sin que existieran: el tipo
  // `TranslationKey` se genera desde este archivo, así que su ausencia rompía `typecheck` en toda
  // la app. Se agregan tal cual las pide la pantalla en vez de renombrarlas ahí, porque la deuda
  // era la traducción faltante, no el nombre.
  'home.inspector.title': 'Patrullaje',
  'inspector.home.patrol.title': 'Recorrido de hoy',
  'home.inspector.patrolStub.title': 'Todavía no hay recorrido',
  'home.inspector.patrolStub.description':
    'Cuando la municipalidad habilite los recorridos, aquí aparecen las zonas asignadas y las boletas del día.',

  // ---- Platform back-office (CONTRACT.md §4 `/api/v1/platform/**`) -----------------------------

  'platform.banner.default': 'Estás operando a nivel plataforma — acceso a todas las municipalidades',
  'platform.nav.tenants': 'Municipalidades',
  'platform.nav.users': 'Padrón global',
  'platform.nav.audit': 'Auditoría global',
  'platform.nav.reports': 'Reportes',
  'platform.nav.catalogs': 'Catálogos',
  'platform.nav.system': 'Sistema',

  'platform.tenants.title': 'Municipalidades',
  'platform.tenants.searchPlaceholder': 'Buscar por nombre o slug',
  'platform.tenants.filter.status': 'Estado',
  'platform.tenants.filter.allStatuses': 'Todos los estados',
  'platform.tenants.filter.country': 'País',
  'platform.tenants.filter.allCountries': 'Todos los países',
  'platform.tenants.column.name': 'Municipalidad',
  'platform.tenants.column.slug': 'Slug',
  'platform.tenants.column.country': 'País',
  'platform.tenants.column.currency': 'Moneda',
  'platform.tenants.column.status': 'Estado',
  'platform.tenants.empty': 'No se encontraron municipalidades con esos criterios.',
  'platform.tenants.createCta': 'Nueva municipalidad',
  'platform.tenants.status.ACTIVE': 'Activa',
  'platform.tenants.status.SUSPENDED': 'Suspendida',
  'platform.tenants.status.CLOSED': 'Cerrada',

  'platform.tenants.create.title': 'Nueva municipalidad',
  'platform.tenants.create.slugLabel': 'Slug',
  'platform.tenants.create.legalNameLabel': 'Razón social',
  'platform.tenants.create.displayNameLabel': 'Nombre',
  'platform.tenants.create.countryLabel': 'País',
  'platform.tenants.create.currencyLabel': 'Moneda',
  'platform.tenants.create.localeLabel': 'Idioma / locale',
  'platform.tenants.create.timeZoneLabel': 'Zona horaria',
  'platform.tenants.create.selfRegistrationLabel': 'Política de auto-registro',
  'platform.tenants.create.selfRegistration.OPEN': 'Abierta',
  'platform.tenants.create.selfRegistration.APPROVAL_REQUIRED': 'Requiere aprobación',
  'platform.tenants.create.selfRegistration.INVITE_ONLY': 'Solo por invitación',
  'platform.tenants.create.submit': 'Crear municipalidad',
  'platform.tenants.create.success': 'Municipalidad creada correctamente.',

  'platform.tenants.detail.title': 'Detalle de municipalidad',
  'platform.tenants.detail.statusSectionTitle': 'Estado',
  'platform.tenants.detail.changeStatusReasonLabel': 'Motivo del cambio',
  'platform.tenants.detail.changeStatusSubmit': 'Aplicar cambio de estado',
  'platform.tenants.detail.settingsTitle': 'Configuración',
  'platform.tenants.detail.settingsSave': 'Guardar configuración',
  'platform.tenants.detail.settingsSaved': 'Configuración guardada.',
  'platform.tenants.detail.createAdminTitle': 'Crear administrador de la municipalidad',
  'platform.tenants.detail.createAdminEmailLabel': 'Correo electrónico',
  'platform.tenants.detail.createAdminGivenNameLabel': 'Nombre',
  'platform.tenants.detail.createAdminFamilyNameLabel': 'Primer apellido',
  'platform.tenants.detail.createAdminSubmit': 'Crear administrador',
  'platform.tenants.detail.createAdminSuccess': 'Administrador creado e invitado correctamente.',

  'platform.users.title': 'Padrón global',
  'platform.users.searchPlaceholder': 'Buscar por nombre, correo o documento',
  'platform.users.filter.country': 'País',
  'platform.users.filter.allCountries': 'Todos los países',
  'platform.users.filter.status': 'Estado',
  'platform.users.filter.allStatuses': 'Todos los estados',
  'platform.users.filter.tenant': 'Municipalidad',
  'platform.users.filter.allTenants': 'Todas las municipalidades',
  'platform.users.column.name': 'Nombre',
  'platform.users.column.email': 'Correo electrónico',
  'platform.users.column.status': 'Estado',
  'platform.users.column.memberships': 'Membresías',
  'platform.users.empty': 'No se encontraron personas con esos criterios.',
  'platform.users.detail.title': 'Detalle de usuario',
  'platform.users.detail.membershipsTitle': 'Membresías',
  'platform.users.detail.noMemberships': 'Esta persona no tiene membresías en ninguna municipalidad.',
  'platform.users.detail.createMembershipCta': 'Otorgar membresía',
  'platform.users.detail.createMembership.title': 'Otorgar membresía',
  'platform.users.detail.createMembership.tenantLabel': 'Municipalidad',
  'platform.users.detail.createMembership.portalLabel': 'Portal',
  'platform.users.detail.createMembership.roleLabel': 'Rol',
  'platform.users.detail.createMembership.submit': 'Otorgar',
  'platform.users.detail.actionSuccess': 'La acción se aplicó correctamente.',

  'platform.audit.filter.tenant': 'Municipalidad',
  'platform.audit.filter.allTenants': 'Todas las municipalidades',

  'platform.catalogs.title': 'Catálogos',
  'platform.catalogs.tab.countries': 'Países',
  'platform.catalogs.tab.adminLevels': 'Niveles administrativos',
  'platform.catalogs.tab.divisions': 'Divisiones administrativas',
  'platform.catalogs.tab.documentTypes': 'Tipos de documento',
  'platform.catalogs.countries.column.code': 'Código',
  'platform.catalogs.countries.column.name': 'Nombre',
  'platform.catalogs.countries.column.dialCode': 'Prefijo',
  'platform.catalogs.countries.column.currency': 'Moneda',
  'platform.catalogs.countries.column.locale': 'Locale',
  'platform.catalogs.countries.column.timeZone': 'Zona horaria',
  'platform.catalogs.selectCountry': 'País',
  'platform.catalogs.adminLevels.column.level': 'Nivel',
  'platform.catalogs.adminLevels.column.labelKey': 'Clave de etiqueta',
  'platform.catalogs.adminLevels.column.required': 'Obligatorio',
  'platform.catalogs.divisions.column.code': 'Código',
  'platform.catalogs.divisions.column.name': 'Nombre',
  'platform.catalogs.divisions.column.level': 'Nivel',
  'platform.catalogs.documentTypes.column.type': 'Tipo',
  'platform.catalogs.documentTypes.column.pattern': 'Patrón',
  'platform.catalogs.documentTypes.column.example': 'Ejemplo',
  'platform.catalogs.documentTypes.column.default': 'Predeterminado',

  'platform.system.title': 'Sistema',
  'platform.system.health.title': 'Estado de los servicios',
  'platform.system.featureFlags.title': 'Feature flags',
  'platform.system.jobs.title': 'Tareas programadas',
  'platform.system.extensionNotice':
    'Esta sección queda preparada: qué más se controla desde aquí se define más adelante.',

  // ---- Días de la semana (horario de cobro, CONTRACT.md v0.3) ---------------------------------
  'weekday.monday': 'Lunes',
  'weekday.tuesday': 'Martes',
  'weekday.wednesday': 'Miércoles',
  'weekday.thursday': 'Jueves',
  'weekday.friday': 'Viernes',
  'weekday.saturday': 'Sábado',
  'weekday.sunday': 'Domingo',

  'common.languageSwitcher.unavailable': 'no disponible en esta versión',
  'nav.settings': 'Configuración',

  // ---- Mi cuenta (CONTRACT.md v0.3 §"Perfil editable") -----------------------------------------
  'account.profile.saved': 'Tus datos quedaron guardados.',
  'account.language.meta': 'Idiomas habilitados por tu municipalidad.',
  'account.password.label': 'Contraseña',
  'account.password.meta': 'Cambiala cuando querás.',
  'account.password.currentLabel': 'Contraseña actual',
  'account.password.newLabel': 'Contraseña nueva',
  'account.password.confirmLabel': 'Confirmá la contraseña nueva',
  'account.password.submit': 'Cambiar contraseña',
  'account.password.changeCta': 'Cambiar contraseña',
  'account.password.sessionsWarning':
    'Al cambiarla se cierran todas tus sesiones, incluida esta: vas a tener que ingresar de nuevo con la contraseña nueva.',
  'account.password.done': 'Listo: tu contraseña cambió y se cerraron todas tus sesiones.',
  'account.password.signInAgain': 'Ingresar de nuevo',
  'account.password.error.currentInvalid': 'La contraseña actual no es correcta.',
  'account.email.title': 'Correo electrónico',
  'account.email.meta': 'La dirección con la que ingresás a tu cuenta.',
  'account.email.currentLabel': 'Correo actual',
  'account.email.newLabel': 'Correo nuevo',
  'account.email.submit': 'Enviar verificación',
  'account.email.changeCta': 'Cambiar correo',
  'account.email.verificationNotice':
    'Te enviamos un enlace al correo nuevo. Tu correo actual sigue funcionando hasta que lo verifiqués.',
  'account.email.pending': 'Revisá {{email}} y abrí el enlace para terminar el cambio.',
  'account.email.error.sameAsCurrent': 'Ese ya es tu correo actual.',
  'account.email.error.alreadyInUse': 'Ese correo ya está en uso por otra cuenta.',

  // ---- Configuración de la municipalidad (CONTRACT.md v0.3) ------------------------------------
  'admin.settings.saved': 'Cambios guardados.',
  'admin.settings.locales.title': 'Idiomas',
  'admin.settings.locales.description':
    'Idiomas que ofrecen los portales de esta municipalidad y cuál se usa cuando la persona no eligió ninguno.',
  'admin.settings.locales.defaultBadge': 'Predeterminado',
  'admin.settings.locales.makeDefault': 'Usar como predeterminado',
  'admin.settings.locales.platformDefault': 'Si esta municipalidad no resuelve, la plataforma usa {{locale}}.',
  'admin.settings.locales.error.noneEnabled': 'Tiene que quedar al menos un idioma habilitado.',
  'admin.settings.locales.error.defaultRequired': 'Elegí exactamente un idioma predeterminado entre los habilitados.',
  'admin.settings.spaceFormat.title': 'Código de espacio',
  'admin.settings.spaceFormat.description':
    'Cómo se escribe el código pintado en cada espacio. El servidor valida contra este formato al crear espacios y al iniciar un estacionamiento.',
  'admin.settings.spaceFormat.prefixLabel': 'Prefijo',
  'admin.settings.spaceFormat.prefixHint': 'Lo que va antes del número, por ejemplo "SJ-". Dejalo vacío para códigos solo numéricos.',
  'admin.settings.spaceFormat.digitsLabel': 'Cantidad de caracteres',
  'admin.settings.spaceFormat.digitsHint': 'Cuántos caracteres van después del prefijo, completados con ceros.',
  'admin.settings.spaceFormat.allowLettersLabel': 'Permitir letras',
  'admin.settings.spaceFormat.allowLettersHint': 'Si está marcado, el código admite letras además de números.',
  'admin.settings.spaceFormat.previewLabel': 'Así se vería',
  'admin.settings.spaceFormat.stored': 'Vigente ahora: {{example}} ({{pattern}})',
  'admin.settings.spaceFormat.error.digitsRange': 'La cantidad de caracteres tiene que estar entre {{min}} y {{max}}.',
  'admin.settings.schedule.title': 'Horario de cobro',
  'admin.settings.schedule.description':
    'Solo se cobran los minutos que caen dentro de una franja. Todo se evalúa en la zona horaria de la municipalidad ({{timeZone}}).',
  'admin.settings.schedule.allDayLabel': 'Se cobra parejo las 24 horas',
  'admin.settings.schedule.allDayHint': 'Si lo marcás, se ignoran las franjas y las excepciones de abajo.',
  'admin.settings.schedule.freeDaysSummary': 'No se cobra los días: {{days}}.',
  'admin.settings.schedule.noFreeDays': 'Todos los días tienen cobro. Ningún día queda libre.',
  'admin.settings.schedule.dayFree': 'No se cobra',
  'admin.settings.schedule.addBand': 'Agregar franja',
  'admin.settings.schedule.removeBand': 'Quitar',
  'admin.settings.schedule.fromLabel': 'Desde',
  'admin.settings.schedule.toLabel': 'Hasta',
  'admin.settings.schedule.error.invalidBand': 'Revisá las horas de {{day}}: la hora de fin tiene que ser posterior a la de inicio.',
  'admin.settings.schedule.confirm.title': '¿Guardar el horario de cobro?',
  'admin.settings.schedule.confirm.body':
    'Cambia desde este momento cuándo se cobra en toda la municipalidad. Las estadías que ya ' +
    'están corriendo mantienen lo que se les cobró; el cambio aplica a las que empiecen después.',
  'admin.settings.schedule.confirm.days': 'Días con cobro',
  'admin.settings.schedule.confirm.allDay': 'Se cobra las 24 horas',
  'admin.settings.schedule.confirm.exceptions': 'Excepciones',
  'admin.settings.schedule.chargingNow': 'En este momento se está cobrando.',
  'admin.settings.schedule.notChargingNow': 'En este momento no se cobra. El cobro se reanuda el {{resumesAt}}.',
  'admin.settings.schedule.exceptions.title': 'Excepciones por fecha',
  'admin.settings.schedule.exceptions.description': 'Feriados y días especiales: mandan sobre el horario semanal.',
  'admin.settings.schedule.exceptions.empty': 'Todavía no hay excepciones cargadas.',
  'admin.settings.schedule.exceptions.add': 'Agregar excepción',
  'admin.settings.schedule.exceptions.remove': 'Quitar',
  'admin.settings.schedule.exceptions.repeatLabel': 'Se repite',
  'admin.settings.schedule.exceptions.repeat.once': 'Sólo esta fecha',
  'admin.settings.schedule.exceptions.repeat.annual': 'Cada año, el mismo día',
  'admin.settings.schedule.exceptions.repeat.easter': 'Con la Semana Santa',
  'admin.settings.schedule.exceptions.monthLabel': 'Mes',
  'admin.settings.schedule.exceptions.dayLabel': 'Día',
  'admin.settings.schedule.exceptions.mondayLabel': 'Se traslada al lunes',
  'admin.settings.schedule.exceptions.easterLabel': 'Qué día',
  'admin.settings.schedule.exceptions.easter.maundyThursday': 'Jueves Santo',
  'admin.settings.schedule.exceptions.easter.goodFriday': 'Viernes Santo',
  'admin.settings.schedule.exceptions.easter.sunday': 'Domingo de Resurrección',
  'admin.settings.schedule.exceptions.easter.monday': 'Lunes de Pascua',
  // Las tres fechas de un feriado, cada una con su nombre. Ver `admin/src/lib/holidayDates.ts`:
  // la de ley es el día del año que manda la ley, la efectiva es el día en que de verdad no se
  // cobra, y entre las dos puede haber un traslado al lunes.
  'admin.settings.schedule.exceptions.legalDate': 'Fecha de ley: {{date}}',
  'admin.settings.schedule.exceptions.effectiveDate': 'No se cobra el {{date}}',
  'admin.settings.schedule.exceptions.effectivePast': 'No se cobró el {{date}} · ya pasó',
  'admin.settings.schedule.exceptions.effectiveUnknown': 'Sin fecha: falta completar la regla.',
  'admin.settings.schedule.holidays.title': 'Feriados del país',
  'admin.settings.schedule.holidays.description':
    'Para no volver a digitarlos cada diciembre. Al agregarlos quedan como excepciones suyas, que puede editar o quitar cuando quiera.',
  'admin.settings.schedule.holidays.notice':
    'Es un punto de partida, no asesoría legal: la ley de feriados cambia y el calendario de cobro de esta municipalidad es su responsabilidad. Revise cada uno antes de guardar.',
  'admin.settings.schedule.holidays.empty': 'La plataforma todavía no tiene el calendario de este país.',
  'admin.settings.schedule.holidays.add': 'Agregar',
  'admin.settings.schedule.holidays.added': 'Ya está',
  'admin.settings.schedule.exceptions.dateLabel': 'Fecha',
  'admin.settings.schedule.exceptions.labelLabel': 'Nombre del feriado',
  'admin.settings.schedule.exceptions.chargesLabel': 'Ese día se cobra',
  'admin.settings.schedule.exceptions.allDayLabel': 'Las 24 horas',

  // ---- Ciudadano: horario y minutos cobrables (CONTRACT.md v0.3) --------------------------------
  'citizen.parking.schedule.notCharging': 'Ahora no se cobra en esta municipalidad.',
  'citizen.parking.schedule.notChargingWithResume': 'Ahora no se cobra; el cobro se reanuda el {{resumesAt}}.',
  'citizen.parking.map.open': 'Ver la zona en el mapa',
  'citizen.parking.map.title': 'Zonas de cobro de esta municipalidad',
  'citizen.parking.map.expand': 'Ampliar',
  'citizen.parking.map.collapse': 'Cerrar',
  'citizen.parking.map.loading': 'Cargando el mapa…',
  'citizen.parking.map.empty': 'Esta municipalidad todavía no tiene zonas verificadas en el mapa. El estacionamiento funciona igual: la zona y la bahía que escribió son las que valen.',
  'citizen.parking.map.error': 'No se pudo cargar el mapa. Puede continuar sin él.',
  'citizen.parking.map.noProvider': 'Esta instalación no tiene configurado un proveedor de mapas.',
  'citizen.parking.map.notOfficial': 'No son límites oficiales.',
  'citizen.parking.map.notOfficialDetail': 'Las punteadas son datos de demostración.',
  'citizen.parking.map.tapHint': 'Tocá una zona del mapa, o elegila en la lista.',
  'citizen.parking.step1.zonePlaceholder': 'Selecciona la zona',
  'citizen.parking.map.emptyShort': 'Esta municipalidad todavía no tiene zonas dibujadas en el mapa.',

  'citizen.parking.step1.spaceCodeLabel': 'Número de parqueo',
  'citizen.parking.step1.spaceCodeHint': 'El número pintado en el espacio donde estás.',
  'citizen.parking.step1.noZones': 'Esta municipalidad todavía no publicó sus zonas de parqueo.',
  'citizen.parking.step4.chargeableMinutesLabel': 'Minutos que se cobran',
  'citizen.parking.step4.chargeableMinutesHint': 'El resto cae fuera del horario de cobro.',
  'citizen.parking.step4.courtesyHint': 'Es una estadía de cortesía: no se cobra.',

  // ---- Ciudadano: guía del código de espacio (rango real de la zona + formato del municipio) ----
  'citizen.parking.step1.spaceCodeRange': 'Espacios {{first}}–{{last}} en esta zona ({{count}} en total).',
  'citizen.parking.step1.spaceCodeRangeSingle': 'Esta zona tiene un solo espacio: {{code}}.',
  'citizen.parking.step1.spaceCodeFormat': 'Formato: {{example}}.',
  'citizen.parking.step1.spaceCodeError.format':
    'Ese código no tiene el formato de esta municipalidad. Por ejemplo: {{example}}.',
  'citizen.parking.step1.spaceCodeError.range': 'Esta zona solo tiene espacios del {{first}} al {{last}}.',

  // ---- Ciudadano: duración "Otro" dentro de los límites de la municipalidad --------------------
  'citizen.parking.step3.maxNotice': 'Esta municipalidad permite hasta {{max}} por estacionamiento.',
  'citizen.parking.step3.maxNoticeZone': 'En esta zona se permite hasta {{max}} por estacionamiento.',
  'citizen.parking.step3.courtesyNotice':
    'Los primeros {{minutes}} son de cortesía en esta zona, una vez al día por placa.',

  // ---- Catálogos (claves que devuelve el servidor: países, documentos, divisiones) --------------
  // El catálogo viaja con claves estables, no con nombres ya escritos: el mismo país se lee en
  // cada idioma que hable la plataforma (CONTRACT.md §5).
  'country.CR': 'Costa Rica',
  'country.ES': 'España',
  'country.MX': 'México',
  'country.PA': 'Panamá',
  'country.US': 'Estados Unidos',

  'document.CR.NATIONAL_ID': 'Cédula de identidad',
  'document.CR.FOREIGN_RESIDENT_ID': 'DIMEX',
  'document.CR.PASSPORT': 'Pasaporte',
  'document.CR.TAX_ID': 'Cédula jurídica',
  'document.CR.OTHER': 'Otro documento',
  'document.ES.NATIONAL_ID': 'DNI',
  'document.ES.FOREIGN_RESIDENT_ID': 'NIE',
  'document.ES.PASSPORT': 'Pasaporte',
  'document.MX.NATIONAL_ID': 'CURP',
  'document.MX.PASSPORT': 'Pasaporte',
  'document.MX.TAX_ID': 'RFC',
  'document.PA.NATIONAL_ID': 'Cédula',
  'document.PA.PASSPORT': 'Pasaporte',
  'document.US.NATIONAL_ID': 'Licencia de conducir',
  'document.US.PASSPORT': 'Pasaporte',
  'document.US.TAX_ID': 'SSN',

  // Tipos y colores de vehículo: el servidor manda `{value, labelKey}` y el cliente sólo traduce.
  // Ninguna pantalla lista estos valores por su cuenta (CONTRACT.md — catálogos de vehículo).
  'vehicle.type.car': 'Automóvil',
  'vehicle.type.motorcycle': 'Motocicleta',
  'vehicle.type.truck': 'Camión',
  'vehicle.type.pickup': 'Pick-up',
  'vehicle.type.van': 'Furgoneta',
  'vehicle.type.bus': 'Autobús',
  'vehicle.type.bicycle': 'Bicicleta',
  'vehicle.type.scooter': 'Scooter',
  'vehicle.type.other': 'Otro',

  'vehicle.color.white': 'Blanco',
  'vehicle.color.black': 'Negro',
  'vehicle.color.gray': 'Gris',
  'vehicle.color.grey': 'Gris',
  'vehicle.color.silver': 'Plateado',
  'vehicle.color.red': 'Rojo',
  'vehicle.color.blue': 'Azul',
  'vehicle.color.green': 'Verde',
  'vehicle.color.yellow': 'Amarillo',
  'vehicle.color.orange': 'Naranja',
  'vehicle.color.brown': 'Café',
  'vehicle.color.beige': 'Beige',
  'vehicle.color.gold': 'Dorado',
  'vehicle.color.purple': 'Morado',
  'vehicle.color.other': 'Otro',

  // ---- Fiscalización (CONTRACT.md v0.7) -------------------------------------------------------
  // El servidor manda `statusLabelKey` / `actionLabelKey` / `verdictLabelKey`; el cliente guarda su
  // propia copia de la tabla porque una clave que el servidor conoce y el diccionario no debe
  // fallar el typecheck, no aparecer en pantalla como texto crudo.

  'citation.status.draft': 'Borrador',
  'citation.status.issued': 'Emitida',
  'citation.status.paid': 'Pagada',
  'citation.status.appealed': 'Con reclamo',
  'citation.status.upheld': 'Reclamo rechazado',
  'citation.status.dismissed': 'Reclamo aceptado',
  'citation.status.cancelled': 'Anulada',
  'citation.status.expired': 'Vencida',

  'citation.action.drafted': 'Capturada',
  'citation.action.issued': 'Emitida',
  'citation.action.evidence_attached': 'Prueba adjuntada',
  'citation.action.paid': 'Pagada',
  'citation.action.appealed': 'Reclamo presentado',
  'citation.action.appeal_upheld': 'Reclamo rechazado',
  'citation.action.appeal_dismissed': 'Reclamo aceptado',
  'citation.action.cancelled': 'Anulada',
  'citation.action.expired': 'Vencida',

  'citation.evidence.photo': 'Fotografía',
  'citation.evidence.note': 'Nota',
  'citation.evidence.title': 'Pruebas',
  'citation.evidence.empty': 'Esta boleta no tiene pruebas adjuntas.',
  'citation.evidence.withheld':
    'Esta boleta tiene pruebas, pero no podemos mostrártelas: el vehículo no está registrado a tu nombre.',
  'citation.evidence.digest': 'Huella SHA-256',
  'citation.evidence.capturedAt': 'Capturada',
  'citation.evidence.size': 'Tamaño',
  'citation.evidence.open': 'Ver fotografía',
  'citation.evidence.loadError': 'No se pudo cargar la fotografía.',
  'citation.history.title': 'Historial',
  'citation.history.reason': 'Motivo',
  'citation.history.portal.citizen': 'Portal ciudadano',
  'citation.history.portal.admin': 'Administración',
  'citation.history.portal.inspector': 'Fiscalización',
  'citation.history.portal.platform': 'Plataforma',

  'citation.field.number': 'Número',
  'citation.source.luparx': 'Esta municipalidad',
  'citation.source.external': 'Otro sistema',
  'citation.source.external.named': 'Otro sistema · {{system}}',
  'citation.source.mirrorNotice':
    'Esta boleta se levantó en otro sistema de la municipalidad. Aquí se consulta; allá se cobra y se resuelve.',
  'citation.source.mirrorNotice.named':
    'Esta boleta se levantó en {{system}}, otro sistema de la municipalidad. Aquí se consulta; allá se cobra y se resuelve.',
  'citation.field.externalStatus': 'Estado en el otro sistema',
  'citation.field.lastSeenAt': 'Última confirmación del otro sistema',
  'admin.enforcement.citation.notManagedHere':
    'Esta boleta se levantó en otro sistema. Se anula allá, no aquí.',
  'citizen.fines.managedElsewhere':
    'Esta multa la gestiona la municipalidad en otro sistema. Consúltela aquí, pero páguela o presente su reclamo donde la municipalidad le indique.',
  'citizen.fines.managedElsewhere.named':
    'Esta multa la gestiona la municipalidad en {{system}}. Consúltela aquí, pero páguela o presente su reclamo donde la municipalidad le indique.',
  'citizen.fines.payElsewhere': 'Esta multa no se paga en LupaRX: la cobra la municipalidad en su otro sistema.',
  'citation.field.plate': 'Placa',
  'citation.field.infraction': 'Infracción',
  'citation.field.zone': 'Zona',
  'citation.field.bay': 'Bahía',
  'citation.field.address': 'Dirección',
  'citation.field.coordinates': 'Coordenadas',
  'citation.field.accuracy': 'Precisión',
  'citation.field.occurredAt': 'Hora declarada',
  'citation.field.issuedAt': 'Hora de emisión',
  'citation.field.clockSkew': 'Desfase del reloj del dispositivo',
  'citation.field.fine': 'Monto',
  'citation.field.amountPayable': 'Monto exigible hoy',
  'citation.field.discountUntil': 'Descuento hasta',
  'citation.field.dueAt': 'Vence',
  'citation.field.notes': 'Notas',
  'citation.field.inspector': 'Funcionario',
  'citation.field.statusReason': 'Motivo del estado',
  'citation.field.noNumber': 'Sin número (borrador)',
  'citation.field.noCoordinates': 'Sin coordenadas',
  'citation.field.noCoordinates.hint':
    'La boleta se emitió sin ubicación GPS. Queda constancia de que no hubo coordenadas.',
  'citation.clockSkew.seconds': '{{seconds}} s',

  // Una consulta que NO concluyó: se rechazó por falta de señal, placa ilegible o bahía fuera de
  // zona, así que quedó con su código de rechazo y sin veredicto. No es «desconocido» por error:
  // es que no se llegó a preguntar.
  'plate.verdict.unknown': 'Sin resolver',
  'plate.verdict.exempt': 'Exonerado',
  'plate.verdict.expired': 'Pago vencido',
  'plate.verdict.covered': 'Pago vigente en esta bahía',
  'plate.verdict.bay_mismatch': 'Pagó por otra bahía',
  'plate.verdict.not_covered': 'Sin pago vigente',
  'plate.verdict.ambiguous': 'Falta la bahía para responder',

  // ---- App de fiscalización -------------------------------------------------------------------

  'inspector.nav.lookup': 'Consulta',
  'inspector.nav.cite': 'Boleta',
  'inspector.nav.citations': 'Mis boletas',
  'inspector.nav.queue': 'Pendientes',
  'inspector.nav.more': 'Más',

  'inspector.lookup.title': 'Consultar placa',
  'inspector.lookup.help': 'Verifica permisos, infracciones y datos disponibles del vehículo.',
  'inspector.lookup.offline': 'Sin señal: la consulta necesita conexión. La boleta sí se puede levantar y sale sola cuando vuelva.',
  'inspector.lookup.checking': 'Consultando la placa…',
  // «Placa del vehículo» y no «Placa»: desde la v2 este rótulo SE VE encima del campo, y la
  // referencia lo dibuja con esas tres palabras. Sigue siendo además el nombre accesible.
  'inspector.lookup.plateLabel': 'Placa del vehículo',
  // Una frase y no un ejemplo de formato (12-10-2026): desde la especificación definitiva el
  // campo no lleva rótulo visible, así que el marcador de posición es lo único que dice qué va
  // ahí. La hoja lo dibuja a 15px mientras el valor tecleado sigue a 24.
  'inspector.lookup.platePlaceholder': 'Ingresa la placa del vehículo',
  'inspector.lookup.zoneLabel': 'Zona',
  // Propio y no el genérico «Selecciona una opción»: es el texto de la referencia v2 y además
  // dice QUÉ se elige, que es lo que un marcador de posición tiene que hacer.
  'inspector.lookup.zonePlaceholder': 'Seleccionar zona',
  'inspector.lookup.bayLabel': 'Bahía',
  'inspector.lookup.bayPlaceholder': '0042',
  'inspector.lookup.submit': 'Consultar',
  // Abreviada el 12-10-2026, que es lo que la v2 autoriza en letra: «se puede abreviar la
  // explicación en pantalla, pero nunca modificar su lógica». La regla no cambia —sin bahía el
  // servidor no confirma cobertura—; lo que cambia es que se dice en un renglón y no en tres.
  'inspector.lookup.bayHint': 'Sin la bahía, la cobertura no se confirma.',
  'inspector.lookup.bayIncomplete': 'Indicá la zona y la bahía juntas, o ninguna de las dos.',
  'inspector.lookup.checkedAt': 'Consultado a las {{time}}',
  'inspector.lookup.coveringStay': 'Sesión vigente hasta {{time}}',
  'inspector.lookup.otherStays': 'Sesiones vigentes de esta placa en otras bahías',
  'inspector.lookup.stayRow': '{{zone}} · bahía {{bay}} · hasta {{time}}',
  'inspector.lookup.useThisBay': 'Consultar con esta bahía',
  'inspector.lookup.verdict.covered.detail':
    'Esta placa tiene una sesión de parqueo vigente en la bahía {{bay}}. No corresponde boleta por falta de pago.',
  'inspector.lookup.verdict.bay_mismatch.detail':
    'Pagó por la bahía {{other}}, no por ésta ({{bay}}). Es una infracción distinta de no pagar: revisá el tipo antes de emitir.',
  'inspector.lookup.verdict.not_covered.detail':
    'No hay ninguna sesión de parqueo vigente para esta placa en esta municipalidad.',
  'inspector.lookup.verdict.ambiguous.detail':
    'Hay sesiones vigentes para esta placa, pero sin la bahía no se puede saber si son de este carro. Indicá la bahía.',
  'inspector.lookup.cite': 'Emitir boleta',
  'inspector.lookup.verdict.exempt.detail':
    'Esta municipalidad exonera esta placa. No corresponde boleta por falta de pago, aunque no haya pagado.',
  'inspector.lookup.verdict.expired.detail':
    'Pagó por esta misma bahía y se le venció. Es distinto de no haber pagado nunca: revisá el tipo de infracción antes de emitir.',
  'inspector.lookup.exemption.reason': 'Motivo: {{reason}}',
  'inspector.lookup.exemption.document': 'Respaldo: {{ref}}',
  'inspector.lookup.exemption.until': 'Vigente hasta el {{date}}',
  'inspector.lookup.exemption.noEnd': 'Vigente sin fecha de vencimiento',
  'inspector.lookup.stay.startedAt': 'Inició: {{datetime}}',
  'inspector.lookup.stay.paid': 'Pagado · {{amount}}',
  'inspector.lookup.stay.noCharge': 'Sin costo',
  'inspector.lookup.stay.noCharge.COURTESY': 'Sin costo · cortesía de la zona',
  'inspector.lookup.stay.noCharge.CREDIT': 'Sin costo · usó minutos a favor',
  'inspector.lookup.stay.noCharge.OUTSIDE_HOURS': 'Sin costo · fuera del horario de cobro',
  'inspector.lookup.stay.transaction': 'Mov. {{id}}',
  'inspector.lookup.stay.expiresAt': 'Vence: {{datetime}}',
  'inspector.lookup.stay.expiredAt': 'Venció: {{datetime}}',
  'inspector.lookup.stay.zone': 'Zona: {{zone}} · bahía {{bay}}',
  'inspector.lookup.stay.remaining': 'Le quedan {{minutes}} min',
  'inspector.lookup.stay.overdue': 'Venció hace {{minutes}} min',
  'inspector.lookup.withinGrace':
    'Ya pasó la hora, pero está dentro de la tolerancia de {{minutes}} min de esta municipalidad. Todavía no corresponde boleta por falta de pago.',
  'inspector.lookup.needBayToCite':
    'Sin la bahía no se puede emitir: el sistema no sabe todavía si este carro pagó. Indicá la bahía y volvé a consultar.',
  'inspector.lookup.error.PARKING_SPACE_NOT_FOUND': 'Esa bahía no existe en la zona seleccionada.',
  'inspector.lookup.error.VALIDATION_FAILED': 'Revisá la placa y la bahía: la zona y la bahía viajan juntas.',

  'inspector.zones.title': 'Zonas conocidas',
  'inspector.zones.empty': 'Todavía no hay zonas conocidas en este dispositivo.',
  'inspector.zones.emptyHint':
    'El portal de fiscalización aún no publica el catálogo de zonas de la municipalidad. Las zonas se aprenden de tus boletas y de las consultas de placa, y quedan guardadas en este dispositivo.',

  'inspector.cite.bayBlocked': 'Falta la otra mitad de la ubicación: indicá la zona y la bahía juntas, o quitá las dos.',
  'inspector.cite.draftFound': 'Quedó una boleta sin terminar, guardada {{when}}.',
  'inspector.cite.draftRestore': 'Retomarla',
  'inspector.cite.draftDiscard': 'Descartarla',
  'inspector.cite.draftCarries.one': 'Trae la placa {{plate}} y una fotografía.',
  'inspector.cite.draftCarries.other': 'Trae la placa {{plate}} y {{count}} fotografías.',
  'inspector.cite.draftNoPlate': 'sin placa',
  'inspector.cite.draftOtherVehicle':
    'El borrador es de otra placa: dice {{draft}} y esta consulta es de {{current}}. Retomarlo reemplaza TODO lo que hay en pantalla —placa, tipo, ubicación, notas y fotografías—; no se mezcla nada.',
  'inspector.cite.draftRestoreReplacing': 'Retomar el borrador y reemplazar',
  'inspector.cite.draftKeepLookup': 'Seguir con esta consulta',
  'inspector.cite.draftKeepLookupHint':
    'Si seguís con esta consulta, el borrador se descarta: sólo se guarda uno a la vez.',
  'inspector.cite.draftPhotosLost.one': 'El aparato ya no tiene una de sus fotografías: habrá que tomarla otra vez.',
  'inspector.cite.draftPhotosLost.other': 'El aparato ya no tiene {{count}} de sus fotografías: habrá que tomarlas otra vez.',
  'inspector.queue.error.EVIDENCE_BYTES_MISSING':
    'La fotografía ya no está en este aparato, así que esta boleta no se puede enviar como está. No se reintenta más: hay que volver a tomarla, o descartar la boleta.',
  'inspector.cite.possibleDuplicate':
    'En ESTE aparato ya hay una boleta para {{placa}}, capturada hace {{minutos}} min. Si es el mismo hecho, no la emitás de nuevo; si es una infracción distinta, seguí.',
  'inspector.cite.possibleDuplicate.bay': 'Era la misma bahía ({{bahia}}).',
  'inspector.queue.error.EVIDENCE_STORE_FAILED':
    'Este aparato no pudo guardar la fotografía, así que la boleta NO quedó en cola. Liberá espacio —o salí del modo privado— y volvé a capturarla.',
  'inspector.queue.retry.attempted': 'Se intentó enviar. Mirá cada boleta para ver cómo le fue.',
  'inspector.queue.retry.noNetwork': 'No se envió nada: este aparato no tiene red en este momento.',
  'inspector.queue.retry.inProgress': 'Ya hay un envío en curso.',
  'inspector.queue.retry.nothing': 'No había nada pendiente de enviar.',
  'inspector.storage.lowSpace':
    'A este aparato le queda poco espacio ({{restante}} de {{total}}). Las fotografías de las próximas boletas pueden no guardarse: conviene enviar lo pendiente ahora.',
  'inspector.cite.draftNotSaved': 'El aparato no está guardando el borrador. Si se cierra la aplicación, lo escrito se pierde: emitila antes de alejarte.',
  'inspector.storage.degraded.cola': 'El aparato no está guardando las boletas pendientes. Siguen en pantalla, pero NO sobreviven a un cierre de la aplicación.',
  'inspector.storage.degraded.borrador': 'El aparato no está guardando el borrador en curso.',
  'inspector.storage.degraded.fotos': 'El aparato no está guardando las fotos. Las que ya tomaste siguen en pantalla, pero no sobreviven a un cierre.',
  'inspector.cite.title': 'Emitir boleta',
  'inspector.cite.step.type': 'Tipo de infracción',
  'inspector.cite.step.where': 'Placa y ubicación',
  /* --- El riel de avance de la boleta (08-10-2026) ------------------------------------------ */
  'inspector.cite.track.title': 'Avance de la boleta',
  'inspector.cite.track.compact': 'Paso {{current}} de {{total}}',
  'inspector.cite.track.vehicle': 'Vehículo',
  'inspector.cite.track.infraction': 'Infracción',
  'inspector.cite.track.evidence': 'Evidencia',
  'inspector.cite.track.review': 'Revisar',
  'inspector.cite.typeSearch': 'Buscar infracción',
  'inspector.cite.typeSearchPlaceholder': 'Nombre o importe',
  'inspector.cite.typeSearchCount': '{{shown}} de {{total}} tipos coinciden.',
  'inspector.cite.typeLabel': 'Tipo de infracción',
  'inspector.cite.typePlaceholder': 'Elegí el tipo',
  'inspector.cite.typeDetail': '{{amount}} · vence en {{days}} días',
  'inspector.cite.typeRequiresPhoto': 'Este tipo exige fotografía: no se puede emitir sin al menos una.',
  'inspector.cite.typeAllowsAppeal': 'Admite reclamo',
  'inspector.cite.typeNoAppeal': 'No admite reclamo',
  'inspector.cite.typeDiscount': 'Descuento del {{percent}}% durante {{days}} días',
  'inspector.cite.addressLabel': 'Dirección escrita',
  'inspector.cite.addressPlaceholder': 'Costado sur del mercado',
  'inspector.cite.notesLabel': 'Notas',
  'inspector.cite.notesPlaceholder': 'Lo que un reclamo tendría que poder leer.',
  'inspector.cite.photos': 'Fotografías',
  'inspector.cite.photosCount': '{{count}} de {{max}}',
  'inspector.cite.addPhoto': 'Tomar fotografía',
  'inspector.cite.removePhoto': 'Quitar fotografía',
  'inspector.cite.photoRequired': 'Falta la fotografía que exige este tipo de infracción.',
  'inspector.cite.photoUnchanged':
    'Las fotografías se envían tal cual se tomaron: no se recortan ni se recomprimen.',
  'inspector.cite.photoResized':
    'Esta fotografía pesa {{size}} y supera el límite de subida ({{max}}). Se envía reducida; queda dicho aquí y en la boleta.',
  'inspector.cite.photoTooLarge': 'La fotografía pesa {{size}} y supera el límite de {{max}}.',
  'inspector.cite.location': 'Ubicación',
  'inspector.cite.locationCapture': 'Tomar coordenadas',
  'inspector.cite.locationCaptured': '{{lat}}, {{lon}} (±{{accuracy}} m)',
  'inspector.cite.locationNone': 'Sin coordenadas',
  'inspector.cite.locationNoneHint':
    'La boleta se puede emitir igual. Queda constancia de que no hubo coordenadas — nunca se inventa una posición.',
  'inspector.cite.submit': 'Emitir boleta',
  'inspector.cite.submitting': 'Emitiendo…',
  'inspector.cite.queued': 'Guardada para enviar',
  'inspector.cite.queuedHint': 'Se envía sola en cuanto vuelva la señal.',
  'inspector.cite.issued': 'Boleta {{number}} emitida',
  'inspector.cite.draft': 'Boleta capturada como borrador',
  'inspector.cite.reset': 'Emitir otra',
  'inspector.cite.viewCitation': 'Ver la boleta',

  'inspector.permission.title': 'Permiso necesario',
  'inspector.permission.camera.why':
    'La cámara se usa únicamente para adjuntar la fotografía de la infracción a la boleta.',
  'inspector.permission.camera.denied':
    'Sin acceso a la cámara no se puede adjuntar la fotografía. Habilitalo en los ajustes del dispositivo.',
  'inspector.permission.location.why':
    'La ubicación se adjunta a la boleta como parte de la prueba. Se toma una sola vez, al emitir.',
  'inspector.permission.location.denied':
    'Sin acceso a la ubicación la boleta se emite igual, sin coordenadas.',
  'inspector.permission.allow': 'Continuar',
  'inspector.permission.skip': 'Continuar sin esto',

  'inspector.queue.title': 'Pendientes de enviar',
  // Plurales por familia de claves (`.one` / `.other`), resueltos con las reglas CLDR del locale.
  // "1 boleta(s)" es la forma de no traducir: la barra que ve el fiscalizador todo el día no puede
  // ser la única línea de la aplicación escrita con paréntesis.
  'inspector.queue.count.one': '1 boleta pendiente',
  'inspector.queue.count.other': '{{count}} boletas pendientes',
  'inspector.queue.empty': 'No hay boletas pendientes de enviar.',
  'inspector.queue.retryAll': 'Reintentar el envío',
  'inspector.queue.sending': 'Enviando…',
  'inspector.queue.discard': 'Descartar',
  'inspector.queue.discardConfirm': 'Se pierde la captura y no se emite ninguna boleta. ¿Descartarla?',
  'inspector.queue.state.pending': 'Esperando señal',
  'inspector.queue.state.sending': 'Enviando',
  'inspector.queue.state.failed': 'Falló el envío',
  'inspector.queue.state.sent': 'Enviada',
  'inspector.queue.attempts.one': '1 intento',
  'inspector.queue.attempts.other': '{{count}} intentos',
  'inspector.queue.technical': 'Detalles técnicos',
  'inspector.queue.deviceId': 'Identificador del dispositivo',
  'inspector.queue.duplicateSafe':
    'Reenviar no duplica: la boleta lleva un identificador propio del dispositivo, generado una sola vez.',
  'inspector.queue.photosPending.one': '1 fotografía por subir',
  'inspector.queue.photosPending.other': '{{count}} fotografías por subir',

  'inspector.citations.title': 'Mis boletas',
  'inspector.citations.empty': 'Todavía no emitiste boletas en esta municipalidad.',
  'inspector.citations.detail.title': 'Boleta',
  'inspector.citations.emptyFiltered': 'No hay boletas que coincidan con esos filtros.',
  'inspector.citations.filter.status': 'Estado',
  'inspector.citations.filter.from': 'Desde',
  'inspector.citations.filter.to': 'Hasta',
  'inspector.citations.filter.allStatuses': 'Todos los estados',
  'inspector.citations.filter.apply': 'Aplicar',
  'inspector.citations.filter.clear': 'Limpiar',

  'inspector.offline.badge': 'Sin conexión',
  'inspector.offline.queued.one': 'Sin conexión · 1 pendiente',
  'inspector.offline.queued.other': 'Sin conexión · {{count}} pendientes',

  // ---- Administración: fiscalización -----------------------------------------------------------

  'admin.enforcement.citations.title': 'Boletas',
  'admin.enforcement.citations.description':
    'Todo lo que emitieron los funcionarios de esta municipalidad. Una boleta no se edita ni se borra: se anula con motivo.',
  'admin.enforcement.citations.filter.status': 'Estado',
  'admin.enforcement.citations.filter.zone': 'Zona',
  'admin.enforcement.citations.filter.allInspectors': 'Todos los funcionarios',
  'admin.enforcement.citations.filter.inspector': 'Funcionario',
  'admin.enforcement.citations.filter.plate': 'Placa',
  'admin.enforcement.citations.filter.from': 'Desde',
  'admin.enforcement.citations.filter.to': 'Hasta',
  'admin.enforcement.citations.filter.all': 'Todos',
  'admin.enforcement.citations.filter.apply': 'Aplicar',
  'admin.enforcement.citations.filter.clear': 'Limpiar',
  'admin.enforcement.citations.column.number': 'Número',
  'admin.enforcement.citations.column.plate': 'Placa',
  'admin.enforcement.citations.column.infraction': 'Infracción',
  'admin.enforcement.citations.column.zone': 'Zona y bahía',
  'admin.enforcement.citations.column.issuedAt': 'Emitida',
  'admin.enforcement.citations.column.status': 'Estado',
  'admin.enforcement.citations.column.amount': 'Monto',
  'admin.enforcement.citations.empty': 'Ninguna boleta coincide con estos filtros.',
  'admin.enforcement.citations.open': 'Ver',
  'admin.enforcement.citation.title': 'Boleta {{number}}',
  'admin.enforcement.citation.back': 'Volver a boletas',
  'admin.enforcement.citation.cancel': 'Anular boleta',
  'admin.enforcement.citation.cancel.title': 'Anular la boleta',
  'admin.enforcement.citation.cancel.reason': 'Motivo de la anulación',
  'admin.enforcement.citation.cancel.reasonHint':
    'El motivo queda en la boleta y en su historial, y lo lee también la persona multada.',
  'admin.enforcement.citation.cancel.confirm': 'Anular',
  'admin.enforcement.citation.cancel.required': 'El motivo es obligatorio.',
  'admin.enforcement.citation.cancel.done': 'Boleta anulada.',
  'admin.enforcement.citation.notCancellable': 'Esta boleta ya no admite anulación.',
  'admin.enforcement.citation.noVoidPermission': 'Tu rol no puede anular boletas.',

  'admin.enforcement.mapping.title': 'Causales de otros sistemas sin equivalencia',
  'admin.enforcement.mapping.description':
    'Estas causales llegaron de otro sistema y no coinciden con ninguna del catálogo. Las boletas ya están completas: traen el código, el nombre y el monto tal como vinieron. Lo que falta es la equivalencia para que los reportes puedan sumarlas junto con las suyas.',
  'admin.enforcement.mapping.waiting': '{{system}} · {{count}} boleta(s) esperando',
  'admin.enforcement.mapping.target': 'Equivale a',
  'admin.enforcement.mapping.choose': 'Elija una infracción del catálogo',
  'admin.enforcement.mapping.apply': 'Aplicar',
  'admin.enforcement.mapping.done': '{{code}}: se enlazaron {{count}} boleta(s).',
  'admin.enforcement.mapping.notice':
    'La equivalencia sólo afecta los reportes. La boleta sigue diciendo exactamente por qué la multaron en el otro sistema.',
  'admin.enforcement.types.title': 'Catálogo de infracciones',
  'admin.enforcement.types.description':
    'Lo que esta municipalidad multa, y por cuánto. Se guarda el catálogo completo: una fila quitada se desactiva, nunca se borra, porque hay boletas de años anteriores que la referencian.',
  'admin.enforcement.types.column.code': 'Código',
  'admin.enforcement.types.column.name': 'Nombre',
  'admin.enforcement.types.column.amount': 'Monto',
  'admin.enforcement.types.column.photo': 'Exige foto',
  'admin.enforcement.types.column.appeal': 'Admite reclamo',
  'admin.enforcement.types.add': 'Agregar tipo',
  'admin.enforcement.types.remove': 'Desactivar',
  'admin.enforcement.types.restore': 'Reactivar',
  'admin.enforcement.types.save': 'Guardar catálogo',
  'admin.enforcement.types.saved': 'Catálogo guardado.',
  'admin.enforcement.types.currencyNote': 'La moneda es la de la municipalidad: {{currency}}.',
  'admin.enforcement.types.dueDays': 'Días de plazo',
  'admin.enforcement.types.discountDays': 'Días de descuento',
  'admin.enforcement.types.discountPercent': '% de descuento',
  'admin.enforcement.types.discountBoth': 'Los días y el porcentaje del descuento van juntos, o ninguno.',
  'admin.enforcement.types.amountLabel': 'Monto ({{currency}})',
  'admin.enforcement.types.error.required': 'Completá el código, el nombre, el monto y el plazo de cada fila.',
  'admin.enforcement.types.inactive': 'Desactivada',

  'nav.enforcement': 'Fiscalización',
  'nav.enforcement.citations': 'Boletas',
  'nav.enforcement.types': 'Infracciones',
  'nav.enforcement.exemptions': 'Exoneraciones',
  'nav.enforcement.checks': 'Consultas',
  'admin.checks.title': 'Consultas de fiscalización',
  'admin.checks.description':
    'Cada placa que un funcionario consultó, qué le contestó el sistema, dónde estaba y si después emitió boleta. Es lo que se abre cuando alguien dice que lo multaron sin ir a ver el carro.',
  'admin.checks.retentionNotice':
    'Estas filas dicen dónde estuvo un funcionario durante su turno. Se conservan 12 meses y después se borran solas.',
  'admin.checks.empty': 'No hay consultas en este período.',
  'admin.checks.filter.plate': 'Placa',
  'admin.checks.filter.inspector': 'Funcionario',
  'admin.checks.filter.allInspectors': 'Todos los funcionarios',
  'admin.checks.filter.zone': 'Zona',
  'admin.checks.filter.allZones': 'Todas las zonas',
  'admin.checks.filter.result': 'Resultado',
  'admin.checks.filter.allResults': 'Todos los resultados',
  'admin.checks.column.when': 'Fecha y hora',
  'admin.checks.column.inspector': 'Funcionario',
  'admin.checks.column.plate': 'Placa',
  'admin.checks.column.where': 'Zona y bahía',
  'admin.checks.column.result': 'Resultado',
  'admin.checks.column.action': 'Acción',
  'admin.checks.column.location': 'Ubicación',
  'admin.checks.noZone': 'Sin zona',
  'admin.checks.refused': 'Rechazada ({{code}})',
  'admin.checks.action.cited': 'Emitió boleta',
  'admin.checks.action.none': 'Sólo consultó',
  'admin.checks.location.noFix': 'Sin señal en ese momento',
  'admin.checks.location.notGranted': 'Ubicación no concedida',
  'admin.exemptions.title': 'Exoneraciones',
  'admin.exemptions.description':
    'Placas que esta municipalidad no multa por falta de pago. No es un descuento: el cobro sigue igual, lo que cambia es que al fiscalizador le sale EXONERADO en vez de «sin pago».',
  'admin.exemptions.create': 'Exonerar una placa',
  'admin.exemptions.createNotice':
    'Cuelga de la placa, no de una persona ni de un vehículo registrado: la ambulancia y la flotilla municipal casi nunca tienen cuenta en la aplicación. Por eso el motivo y la vigencia son obligatorios de leer.',
  'admin.exemptions.empty': 'No hay exoneraciones registradas.',
  'admin.exemptions.filter.status': 'Estado',
  'admin.exemptions.filter.all': 'Todos los estados',
  'admin.exemptions.filter.plate': 'Buscar por placa',
  'admin.exemptions.column.plate': 'Placa',
  'admin.exemptions.column.reason': 'Motivo',
  'admin.exemptions.column.validity': 'Vigencia',
  'admin.exemptions.column.state': 'Estado',
  'admin.exemptions.status.ACTIVE': 'Registradas',
  'admin.exemptions.status.REVOKED': 'Retiradas',
  'admin.exemptions.state.inForce': 'Vigente',
  'admin.exemptions.state.pending': 'Aún no empieza',
  'admin.exemptions.state.expired': 'Vencida',
  'admin.exemptions.state.revoked': 'Retirada',
  'admin.exemptions.validFrom': 'Desde el {{date}}',
  'admin.exemptions.validUntil': 'Hasta el {{date}}',
  'admin.exemptions.noEnd': 'Sin vencimiento',
  'admin.exemptions.plateHint': 'Como está pintada. Se compara sin guiones ni espacios.',
  'admin.exemptions.reasonHint':
    'En palabras, y obligatorio: es la respuesta a «¿por qué no se multó este carro?» cuando alguien pregunte dentro de dos años.',
  'admin.exemptions.documentRef': 'Documento de respaldo',
  'admin.exemptions.documentRefHint': 'Número de acuerdo, oficio o resolución. Es lo primero que se pide si alguien impugna.',
  'admin.exemptions.validToLabel': 'Vence el',
  'admin.exemptions.validToHint': 'Déjelo en blanco sólo si de verdad no vence, como la flotilla municipal.',
  'admin.exemptions.noEndWarning':
    'Sin fecha de vencimiento nadie la va a revisar. Si el carro se vende, sigue exonerado hasta que alguien lo note.',
  'admin.exemptions.granted': 'Placa exonerada.',
  'admin.exemptions.revoked': 'Exoneración retirada.',
  'admin.exemptions.action.revoke': 'Retirar',
  'admin.exemptions.revoke.title': 'Retirar la exoneración',
  'admin.exemptions.revoke.body': 'La placa {{plate}} vuelve a multarse por falta de pago desde este momento.',
  'admin.exemptions.revoke.keepsRow':
    'La fila se conserva: es lo que explica por qué no se multó a ese carro mientras estuvo vigente.',
  'admin.exemptions.revoke.reasonLabel': 'Motivo de la retirada',
  'admin.exemptions.error.alreadyExists': 'Esa placa ya tiene una exoneración vigente en esta municipalidad.',
  'admin.exemptions.error.invalid': 'Revise la placa, el motivo y las fechas.',
  'admin.exemptions.error.notActive': 'Esa exoneración ya no está vigente.',
  'admin.exemptions.status.PENDING': 'Pendientes',
  'admin.exemptions.status.APPROVED': 'Aprobados',
  'admin.exemptions.status.REJECTED': 'Rechazados',
  'admin.exemptions.state.awaiting': 'Pendiente de resolver',
  'admin.exemptions.state.rejected': 'Rechazado',
  'admin.exemptions.filter.type': 'Categoría',
  'admin.exemptions.filter.allTypes': 'Todas las categorías',
  'admin.exemptions.column.plates': 'Placas',
  'admin.exemptions.column.type': 'Categoría',
  'admin.exemptions.column.decision': 'Solicitud y resolución',
  'admin.exemptions.typeHint':
    'Es configuración de esta municipalidad, no una lista fija del sistema: lo que exonera un país no es lo que exonera otro.',
  'admin.exemptions.typePlaceholder': 'Escoja la categoría',
  'admin.exemptions.beneficiaryKind': 'El permiso es de',
  'admin.exemptions.beneficiaryKindPlaceholder': 'Persona u organización',
  'admin.exemptions.beneficiary.PERSON': 'Una persona',
  'admin.exemptions.beneficiary.ORGANISATION': 'Una organización',
  'admin.exemptions.beneficiaryName': 'Nombre del beneficiario',
  'admin.exemptions.beneficiaryDocument': 'Identificación del beneficiario',
  'admin.exemptions.beneficiaryDocumentHint':
    'Cédula de la persona o cédula jurídica. Sólo se ve aquí: nunca sale hacia la aplicación del fiscalizador.',
  'admin.exemptions.requestedBy': 'Solicitó: {{name}}',
  'admin.exemptions.decidedBy': 'Resolvió: {{name}}',
  'admin.exemptions.selfApproved': 'Solicitó y aprobó la misma persona',
  'admin.exemptions.selfApprovedNotice':
    'La misma persona solicitó y aprobó este permiso. Se permite —una municipalidad pequeña puede no tener a nadie más— y por eso queda anotado en lugar de quedar escondido.',
  'admin.exemptions.requested': 'Solicitud registrada. No exonera a nadie hasta que se apruebe.',
  'admin.exemptions.approved': 'Permiso aprobado.',
  'admin.exemptions.rejected': 'Permiso rechazado.',
  'admin.exemptions.plateAdded': 'Placa agregada al permiso.',
  'admin.exemptions.plateRemoved': 'Placa retirada del permiso.',
  'admin.exemptions.documentAttached': 'Documento adjuntado.',
  'admin.exemptions.documentTitle': 'Nombre del documento',
  'admin.exemptions.detail.title': 'Detalle del permiso',
  'admin.exemptions.detail.plates': 'Placas que ampara',
  'admin.exemptions.detail.documents': 'Documentos de respaldo',
  'admin.exemptions.detail.documentsNotice':
    'Los archivos no se borran desde aquí: son en lo que se sustentó no multar a un vehículo. Si uno quedó mal, adjunte el correcto y quedan los dos.',
  'admin.exemptions.action.detail': 'Ver detalle',
  'admin.exemptions.action.approve': 'Aprobar',
  'admin.exemptions.action.reject': 'Rechazar',
  'admin.exemptions.action.addPlate': 'Agregar placa',
  'admin.exemptions.action.removePlate': 'Quitar la placa',
  'admin.exemptions.action.submitRequest': 'Registrar solicitud',
  'admin.exemptions.action.attachDocument': 'Adjuntar documento',
  'admin.exemptions.action.openDocument': 'Abrir',
  'admin.exemptions.reject.title': 'Rechazar el permiso',
  'admin.exemptions.reject.body': 'Las placas {{plate}} no quedan exoneradas. La fila se conserva con el motivo.',
  'admin.exemptions.reject.reasonLabel': 'Motivo del rechazo',
  'admin.exemptions.reject.keepsRow':
    'La fila se conserva: un rechazo es una respuesta que alguien se merece, y con el motivo escrito.',
  'admin.exemptions.error.notPending': 'Alguien más ya resolvió ese permiso.',
  'admin.exemptions.error.notEditable': 'Un permiso rechazado o retirado ya no se edita.',
  'admin.exemptions.error.lastPlate': 'Un permiso tiene que amparar al menos una placa. Si ya no aplica, retírelo.',
  'admin.exemptions.error.plateLimit': 'Un permiso no puede amparar más placas.',
  'admin.exemptions.error.documentLimit': 'Este permiso ya tiene el máximo de documentos.',
  'admin.exemptions.error.typeInactive': 'Esa categoría fue retirada y ya no admite permisos nuevos.',
  'admin.exemptions.error.documentTooLarge': 'El documento es demasiado grande.',
  'admin.exemptions.error.documentType': 'Sólo se aceptan documentos PDF o fotografías.',
  'nav.appeals': 'Reclamos',

  // ---- Municipalidad: reclamos ----------------------------------------------------------------

  'admin.appeals.title': 'Reclamos',
  'admin.appeals.description':
    'Los reclamos que presentaron los ciudadanos, del más viejo al más reciente. Aceptar deja la boleta sin efecto; rechazar la mantiene. En los dos casos hay que escribir el motivo.',
  'admin.appeals.emptyQueue': 'No hay reclamos esperando resolución.',
  'admin.appeals.empty': 'No hay reclamos con ese estado.',
  'admin.appeals.error': 'No se pudo resolver el reclamo.',
  'admin.appeals.accepted': 'Reclamo aceptado: la boleta quedó sin efecto.',
  'admin.appeals.rejected': 'Reclamo rechazado: la boleta se mantiene.',
  'admin.appeals.filter.status': 'Estado',
  'admin.appeals.filter.all': 'Todos los estados',
  'admin.appeals.status.SUBMITTED': 'En trámite',
  'admin.appeals.status.ACCEPTED': 'Aceptado',
  'admin.appeals.status.REJECTED': 'Rechazado',
  'admin.appeals.status.WITHDRAWN': 'Retirado',
  'admin.appeals.column.filed': 'Presentado',
  'admin.appeals.column.body': 'Lo que alega',
  'admin.appeals.column.images': 'Fotos',
  'admin.appeals.noticeVersion': 'Texto legal v{{version}}',
  'admin.appeals.action.read': 'Leer',
  'admin.appeals.action.citation': 'Ver boleta',
  'admin.appeals.action.accept': 'Aceptar',
  'admin.appeals.action.reject': 'Rechazar',
  'admin.appeals.read.title': 'Reclamo del ciudadano',
  'admin.appeals.image': 'Ver foto',
  'admin.appeals.noImages': 'El ciudadano no adjuntó fotos.',
  'admin.appeals.alreadyResolved': 'Este reclamo ya se resolvió: {{outcome}}, el {{date}}.',
  'admin.appeals.accept.title': '¿Aceptar el reclamo?',
  'admin.appeals.accept.body': 'La boleta queda sin efecto y el ciudadano deja de deberla.',
  'admin.appeals.reject.title': '¿Rechazar el reclamo?',
  'admin.appeals.reject.body': 'La boleta se mantiene y vuelve a ser exigible desde hoy.',
  'admin.appeals.reasonNotice':
    'El motivo lo lee el ciudadano y queda en el historial de la boleta. Escríbalo como para que lo entienda quien no estuvo ahí.',
  'admin.appeals.reasonLabel': 'Motivo de la resolución',
  'admin.appeals.reasonPlaceholder': 'La fotografía de la boleta muestra…',

  // ---- Ciudadano: multas -----------------------------------------------------------------------

  'citizen.fines.detail.title': 'Multa',
  'citizen.fines.amountOriginal': 'Monto original',
  'citizen.fines.amountPayable': 'Monto exigible hoy',
  'citizen.fines.discountUntil': 'Con descuento hasta el {{date}}',
  'citizen.fines.pay': 'Pagar multa',
  'citizen.fines.appealable': 'Admite reclamo',
  'citizen.fines.notAppealable': 'No admite reclamo',
  'citizen.fines.empty.history': 'No hay multas en el historial de esta municipalidad.',
  'citizen.fines.open': 'Ver detalle',
  'citizen.fines.appeal': 'Presentar un reclamo',
  'citizen.fines.viewAppeal': 'Ver mi reclamo',

  // ---- Ciudadano: reclamo ---------------------------------------------------------------------

  'citizen.appeal.title': 'Reclamo',
  'citizen.appeal.notice.title': 'Antes de escribir',
  'citizen.appeal.notice.version': 'Versión {{version}}, vigente desde el {{date}}',
  'citizen.appeal.write.title': 'Su reclamo',
  'citizen.appeal.write.description':
    'Cuente qué pasó y por qué considera que la boleta no corresponde. Las fotos se agregan después de guardar el texto.',
  'citizen.appeal.write.label': 'Texto del reclamo',
  'citizen.appeal.write.placeholder': 'Ese día había pagado desde la app y me quedaba tiempo…',
  'citizen.appeal.write.count': '{{used}} de {{max}} caracteres',
  'citizen.appeal.acceptNotice': 'Al presentarlo usted acepta el texto legal que aparece arriba.',
  'citizen.appeal.attach.title': 'Adjuntar evidencia (opcional)',
  'citizen.appeal.attach.hint': 'Hasta 3 imágenes de la galería. Las grandes se reducen para poder enviarlas.',
  'citizen.appeal.attach.select': 'Seleccionar imágenes',
  'citizen.appeal.attach.remove': 'Quitar',
  'citizen.appeal.attach.reduced': 'reducida',
  'citizen.appeal.attach.error.type': 'Formato no admitido. Usá JPG, PNG o WebP.',
  'citizen.appeal.attach.error.max': 'Máximo {{max}} imágenes.',
  'citizen.appeal.attach.error.prepare': 'No se pudo preparar esta imagen. Probá con otra.',
  'citizen.appeal.submit': 'Presentar el reclamo',
  'citizen.appeal.notAppealable': 'Esta multa no admite reclamo.',
  'citizen.appeal.waiting': 'La municipalidad todavía no resuelve su reclamo. Mientras tanto la multa no se cobra.',
  'citizen.appeal.filed.title': 'Reclamo presentado',
  'citizen.appeal.filed.submittedAt': 'Presentado el {{date}}',
  'citizen.appeal.decision.title': 'Resolución de la municipalidad',
  'citizen.appeal.decision.accepted': 'Se aceptó su reclamo: la multa quedó sin efecto.',
  'citizen.appeal.decision.rejected': 'Se rechazó su reclamo: la multa se mantiene y vuelve a ser exigible.',
  'citizen.appeal.images.title': 'Fotos del reclamo',
  'citizen.appeal.images.count': '{{used}} de {{max}} permitidas por esta municipalidad',
  'citizen.appeal.images.item': 'Foto {{number}} · {{date}}',
  'citizen.appeal.images.camera': 'Tomar foto con la cámara',
  'citizen.appeal.images.add': 'Agregar foto',
  'citizen.appeal.error.generic': 'No se pudo presentar el reclamo.',
  'citizen.appeal.error.alreadyFiled': 'Ya hay un reclamo presentado para esta multa.',
  'citizen.appeal.error.noticeOutdated':
    'La municipalidad cambió el texto legal mientras usted escribía. Vuelva a leerlo arriba y presente de nuevo; su texto no se perdió.',
  'citizen.appeal.error.noticeUnavailable': 'No se pudo cargar el texto legal, así que no se puede presentar el reclamo.',
  'citizen.appeal.error.imageLimit': 'Ya alcanzó el máximo de fotos que permite esta municipalidad.',
  'citizen.appeal.error.imageTooLarge': 'Esa foto pesa demasiado. Debe ser de 1 MB o menos.',
  'citizen.appeal.error.imageFailed': 'No se pudo agregar la foto. Su reclamo ya quedó presentado.',

  // ---- v0.41 — pagar la multa con el saldo -----------------------------------------------------
  'citizen.fines.pay.confirmTitle': '¿Pagar esta multa?',
  'citizen.fines.pay.amount': 'Se cobran {{amount}} de su billetera.',
  'citizen.fines.pay.discountNotice': 'Es el monto con descuento; después del {{date}} sube a {{full}}.',
  'citizen.fines.pay.balanceAfter': 'Le quedarían {{amount}}.',
  // El aviso es el punto entero del diálogo cuando hay reclamo: el ciudadano está renunciando a que
  // se lo resuelvan, y enterarse después por una fila del historial es enterarse mal.
  'citizen.fines.pay.withdrawsAppeal':
    'Pagar retira el reclamo que usted presentó: la municipalidad ya no lo va a resolver. Esto no se puede deshacer.',
  'citizen.fines.pay.submit': 'Sí, pagar',
  'citizen.fines.pay.success': 'Multa pagada.',
  'citizen.fines.pay.successWithdrew': 'Multa pagada. Su reclamo quedó retirado.',
  'citizen.fines.pay.topUp': 'Recargar saldo',
  'citizen.fines.pay.error.INSUFFICIENT_BALANCE': 'No le alcanza el saldo para esta multa.',
  'citizen.fines.pay.error.CITATION_NOT_PAYABLE': 'Esta multa ya no se puede pagar: puede estar pagada o sin efecto.',
  'citizen.fines.pay.error.CITATION_NOT_MANAGED_HERE': 'Esta multa la cobra la municipalidad en su otro sistema.',
  'citizen.fines.pay.error.APPEAL_BY_ANOTHER_CITIZEN':
    'El reclamo que espera sobre esta multa lo presentó otra persona, así que pagarla aquí cerraría el reclamo de ella. La municipalidad tiene que resolverlo primero.',

  'appeal.status.withdrawn': 'Retirado',
  'citizen.appeal.withdrawn': 'Usted retiró este reclamo al pagar la multa.',

  'appeal.status.submitted': 'En trámite',
  'appeal.status.accepted': 'Aceptado',
  'appeal.status.rejected': 'Rechazado',

  'geo.level.CR.1': 'Provincia',
  'geo.level.CR.2': 'Cantón',
  'geo.level.CR.3': 'Distrito',
  'geo.level.ES.1': 'Comunidad autónoma',
  'geo.level.ES.2': 'Provincia',
  'geo.level.ES.3': 'Municipio',
  'geo.level.MX.1': 'Estado',
  'geo.level.MX.2': 'Municipio',
  'geo.level.PA.1': 'Provincia',
  'geo.level.PA.2': 'Distrito',
  'geo.level.PA.3': 'Corregimiento',
  'geo.level.US.1': 'Estado',
  /* ---- El aparato: la cámara del navegador, compartida por Ciudadano y Fiscalización ----
     Los textos viven acá y no en cada portal porque describen al APARATO, no al rol: «otra
     aplicación tiene la cámara tomada» se dice igual para quien impugna una multa que para quien
     la levanta, y dos copias de esa frase son dos frases que algún día dirán cosas distintas. */
  'device.camera.title': 'Cámara',
  'device.camera.state.granted': 'Habilitada',
  'device.camera.state.asksOnUse': 'Se pide al usarla',
  'device.camera.state.denied': 'Bloqueada',
  'device.camera.state.untested': 'Sin comprobar',
  'device.camera.state.unsupported': 'No disponible en este navegador',
  'device.camera.state.insecure': 'No disponible sin conexión segura',
  'device.camera.state.notFound': 'No hay cámara en este aparato',
  'device.camera.state.busy': 'La está usando otra aplicación',
  'device.camera.state.failed': 'No se pudo abrir',
  'device.camera.test': 'Comprobar la cámara',
  'device.camera.enable': 'Habilitar cámara',
  'device.camera.testing': 'Comprobando…',
  'device.camera.note.denied': 'El permiso está bloqueado para este sitio. Se habilita en el candado de la barra de direcciones, en los permisos del sitio, y después se vuelve a comprobar acá.',
  'device.camera.note.insecure': 'El navegador sólo entrega la cámara en páginas seguras (https). Entrá por la dirección con https y volvé a intentar.',
  'device.camera.note.unsupported': 'Este navegador no permite usar la cámara desde una página. Podés adjuntar una fotografía ya tomada.',
  'device.camera.note.notFound': 'No se encontró ninguna cámara conectada. Podés adjuntar una fotografía ya tomada.',
  'device.camera.note.busy': 'Otra aplicación tiene la cámara tomada. Cerrala y volvé a comprobar.',
  'device.camera.note.failed': 'No se pudo abrir la cámara. Podés adjuntar una fotografía ya tomada.',
  'device.camera.note.asksOnUse': 'El navegador la pide en el momento de usarla. Comprobala acá para saber si de verdad responde.',
  'device.camera.note.granted': 'Comprobada: el aparato entregó imagen.',
  'device.capture.title': 'Tomar una fotografía',
  /* La misma ventana, en modo prueba: no se toma nada y por eso no se llama igual. */
  'device.capture.testTitle': 'Probar la cámara',
  'device.capture.close': 'Cerrar',
  'device.capture.testOk': 'La cámara está entregando imagen. Al cerrar se apaga.',
  'device.capture.shutter': 'Tomar foto',
  'device.capture.again': 'Repetir',
  'device.capture.use': 'Usar esta foto',
  'device.capture.cancel': 'Cancelar',
  'device.capture.opening': 'Abriendo la cámara…',
  'device.capture.preview': 'Vista previa de lo que se va a guardar',
  'device.capture.live': 'Lo que ve la cámara en este momento',
  'device.capture.attach': 'Adjuntar una imagen',
  'device.capture.failedFrame': 'No se pudo tomar el fotograma. Intentá de nuevo.',

  'geo.level.US.2': 'Condado',
  'geo.level.US.3': 'Ciudad',
} as const;

export type TranslationKey = keyof typeof esCR;
